import {
  CompanyAttributes,
  ExpeditionConditionAttributes,
  WarehouseAttributes,
} from '@albatrosdeveloper/ave-models-package';
import BusinessPartnerAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity';
import SalesOfficeAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/configurations/sales-office/sales-office.entity';
import DocumentClassAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/documents/document-class/document-class.entity';
import CurrencyAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/masters/currency/currency.entity';
import PaymentConditionAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/masters/payment-condition/payment-condition.entity';
import AddressAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/common/embedded/address/address.entity';
import { PaymentConditionTypeEnum, StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import { OrderDetailDiscountsEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/order-detail-discounts.enum';
import { BonusBehaviourEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/bonus-behaviour.enum';
import { buildQuery, Ops, where, andWhere } from '@albatrosdeveloper/ave-utils-npm/lib/utils/query.util';

import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { debounceTime, distinctUntilChanged, firstValueFrom, forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { environment } from 'environments/environment';
import { IRequestResult } from 'app/common/interfaces';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { ConfirmationService } from 'app/shared/services/confirmation.service';
import { ConfirmationModalType } from 'app/shared/components/confirmation-modal';
import { User } from 'app/core/user/user.types';
import { UserService } from 'app/core/user/user.service';
import { DocumentTypeEnum, DOCUMENT_TYPE_LABELS } from 'app/common/enums/document-types.enum';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';


import { AttachmentInputComponent } from 'app/shared/components/dynamic-forms/inputs/attachment-input/attachment-input.component';
import { FieldConfig } from 'app/shared/components/dynamic-forms/interfaces/field-config.interface';

import { WarehouseService } from '../../configuration/warehouse/warehouse.service';
import { CompanyService } from '../../configuration/company/company.service.service';
import { DocumentGenerateService } from './document-generate.service';
import { DocumentGenerateConfigService } from './document-generate-config.service';
import { DgNumberPipe } from './dg-number.pipe';
import { altUnitsOf } from './alt-units.util';
import { ItemsTableComponent } from './components/items-table/items-table.component';
import { OrderHistoryTabComponent } from './components/order-history-tab/order-history-tab.component';
import { SuggestedProductsTabComponent } from './components/suggested-products-tab/suggested-products-tab.component';
import { DocumentCreatedSnackbarComponent } from './components/document-created-snackbar/document-created-snackbar.component';
import { ProductSearchModalComponent } from './dialogs/product-search-modal/product-search-modal.component';
import { OrderItemsModalComponent } from './dialogs/order-items-modal/order-items-modal.component';
import { WarehouseDistributionModalComponent } from './dialogs/warehouse-distribution-modal/warehouse-distribution-modal.component';
import { PackLoanRequestsModalComponent } from './dialogs/pack-loan-requests-modal/pack-loan-requests-modal.component';
import {
  ConfirmedPackLoanRequests,
  PackagingRequirementDocumentRequest,
  PackLoanRequestPayload,
} from './interfaces/pack-loan-request.interface';
import { ItemDetailModalComponent } from './dialogs/item-detail-modal/item-detail-modal.component';
import { ItemDetailResult } from './interfaces/dialog-data.interface';
import { OrderDetailModalComponent } from './dialogs/order-detail-modal/order-detail-modal.component';
import { DocumentPreviewModalComponent } from './dialogs/document-preview-modal/document-preview-modal.component';
import { DocumentSplitModalComponent } from './dialogs/document-split-modal/document-split-modal.component';
import { ChangeUnitModalComponent } from './dialogs/change-unit-modal/change-unit-modal.component';
import { UnitCalculatorModalComponent } from './dialogs/unit-calculator-modal/unit-calculator-modal.component';
import { UnpricedUomModalComponent } from './dialogs/unpriced-uom-modal/unpriced-uom-modal.component';
import { AddressSelectModalComponent } from './dialogs/address-select-modal/address-select-modal.component';
import { ShipToSearchModalComponent } from './dialogs/ship-to-search-modal/ship-to-search-modal.component';
import { OfficeChangeConfirmModalComponent } from './dialogs/office-change-confirm-modal/office-change-confirm-modal.component';
import { DeliveryRouteCandidatesModalComponent } from './dialogs/delivery-route-candidates-modal/delivery-route-candidates-modal.component';
import { FormConfigChangeModalComponent } from './dialogs/form-config-change-modal/form-config-change-modal.component';
import { MyDailyTransactionsModalComponent } from './dialogs/my-daily-transactions-modal/my-daily-transactions-modal.component';
import { OpenItemsModalComponent } from './dialogs/open-items-modal/open-items-modal.component';
import { InvalidItemsModalComponent } from './dialogs/invalid-items-modal/invalid-items-modal.component';
import { SalesOfficeSelectModalComponent } from './dialogs/sales-office-select-modal/sales-office-select-modal.component';
import {
  buildLineId,
  DocumentItem,
  DocumentItemBonus,
  DocumentSummary,
  PricedMaterial,
  WarehouseAllocation,
  WarehouseStockRow,
} from './interfaces/document-item.interface';
import { WarehousePriorityStrategyEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/warehouse-priority-strategy.enum';

import { DocumentSeller } from './interfaces/document-seller.interface';
import { IOverduePaymentRestrictionResult, ShipToInterface } from './interfaces/ship-to.interface';
import {
  DELIVERY_ROUTE_REJECTED_LABEL_KEYS,
  DeliveryRouteCandidate,
  DeliveryRouteDocumentRequest,
  DeliveryRouteDocumentResolution,
  DeliveryRouteRequest,
  DeliveryRouteResolution,
  DeliveryRouteTrip,
} from './interfaces/delivery-route.interface';
import {
  DeliveryRouteCandidatesGroup,
  DocumentAttachment,
  DocumentPreviewData,
  DocumentPreviewDocument,
  DeliveryRouteSplitDocument,
  DocumentSplitReason,
  LineAltUnits,
} from './interfaces/dialog-data.interface';
import {
  DeliveryRouteSplitService,
  WEEKDAY_KEYS,
} from './services/delivery-route-split.service';
import { DeliveryRouteSplitModalComponent } from './dialogs/delivery-route-split-modal/delivery-route-split-modal.component';

// tipo de clase de línea que habilita bonificaciones en la clase de documento
const GIFT_LINE_TYPE_CODE = 'GIFT';

// Respaldo para los tipos del selector cuando el enum o su traducción no están disponibles.
// Guarda CLAVES, no texto: este objeto se evalúa al importar el módulo, antes de que el idioma
// esté resuelto, así que un literal traducido quedaría congelado en el idioma de arranque.
const DOCUMENT_TYPE_FALLBACK_KEYS: Partial<Record<DocumentTypeEnum, string>> = {
  [DocumentTypeEnum.PEDIDO]: 'DOCUMENT_GENERATE.MESSAGES.DOCUMENT_TYPE_ORDER',
  [DocumentTypeEnum.COTIZACION]: 'DOCUMENT_GENERATE.MESSAGES.DOCUMENT_TYPE_QUOTATION',
};

// preguntar antes de pasar a la unidad secundaria cuando la base no se puede vender.
// Hoy se selecciona sin preguntar; queda a la espera de la preferencia de usuario
const CONFIRM_SECONDARY_UNIT: boolean = false;

// el resto de la pantalla imprime las fechas con dd/MM/yyyy, así que el datepicker
// de fecha de carga usa el mismo formato en lugar del largo de Luxon ('DDD')
const LUXON_LONG_DATE_FORMATS = {
  parse: {
    dateInput: ['dd/MM/yyyy', 'd/M/yyyy', 'dd/M/yyyy', 'd/MM/yyyy', 'DDD', 'D'],
  },
  display: {
    dateInput: 'dd/MM/yyyy',
    monthYearLabel: 'LLL yyyy',
    dateA11yLabel: 'dd/MM/yyyy',
    monthYearA11yLabel: 'LLLL yyyy',
  },
};

// resultado de evaluar si el almacén principal puede sostener la regalía
type BonusStockCheck = 'ok' | 'sin-respaldo' | 'bloqueada';

interface ItemWarning {
  title: string;
  message: string;
  icon?: string;
}

@Component({
  selector: 'app-document-generate',
  standalone: true,
  providers: [
    // No MAT_DATE_LOCALE override here: this screen displays dates with Luxon's 'DDD' long format,
    // which spells the month out, so pinning the locale printed Spanish month names to users
    // running the app in English. AppComponent drives DateAdapter.setLocale from the active
    // language instead.
    { provide: MAT_DATE_FORMATS, useValue: LUXON_LONG_DATE_FORMATS },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'always' } },
    DeliveryRouteSplitService,
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    TextFieldModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatTabsModule,
    MatMenuModule,
    NgxMaterialTimepickerModule,
    AttachmentInputComponent,
    ItemsTableComponent,
    OrderHistoryTabComponent,
    SuggestedProductsTabComponent,
    DgNumberPipe,
    TranslatePipe,
  ],
  templateUrl: './document-generate.component.html',
  styleUrl: './document-generate.component.scss',
})
export class DocumentGenerateComponent implements OnInit, OnDestroy {
  private subs: Subscription[] = [];
  private sidebarForced = false;
  private destroyed = false;
  screenIsSmall = false;
  screenIsWide = false;
  private suggestionsRef: MatDialogRef<any> | null = null;
  @ViewChild('suggestionsDialogTpl') private suggestionsDialogTpl?: TemplateRef<any>;

  // Tipo de documento (unifica pedido / cotización)
  documentTypeControl = new FormControl<DocumentTypeEnum>(DocumentTypeEnum.PEDIDO, Validators.required);
  // último tipo confirmado (para revertir al cancelar el modal de cambio)
  private lastDocumentType: DocumentTypeEnum = DocumentTypeEnum.PEDIDO;
  documentTypeOptions = [
    { value: DocumentTypeEnum.PEDIDO },
    { value: DocumentTypeEnum.COTIZACION },
  ];

  // Empresa
  company: CompanyAttributes;

  // Aplicación / subaplicación del documento
  applicationSelected: any = null;
  subApplicationSelected: any = null;

  // Creación en curso (evita reenviar el documento)
  submitting = false;

  // Clase de documento
  documentClassControl = new FormControl('', Validators.required);
  documentClassesList: DocumentClassAttributes[] = [];
  documentClassSelected: DocumentClassAttributes = null;
  // la clase admite bonificaciones si tiene una clase de línea activa de tipo GIFT
  private classSupportsBonus = false;
  // líneas por las que ya se preguntó por la bonificación no soportada (no se repregunta)
  private bonusWarnedIds = new Set<string>();
  // líneas ya avisadas de que su regalía no se entrega por falta de existencia
  private bonusNoStockWarned = new Set<string>();
  // líneas ya avisadas de que su regalía se entrega por encima de la existencia
  private bonusOutOfStockWarned = new Set<string>();
  @ViewChild('classTrigger') private classAutoTrigger?: MatAutocompleteTrigger;

  // Oficina de venta
  salesOfficeControl = new FormControl('', Validators.required);
  salesOfficesList: SalesOfficeAttributes[] = [];
  salesOfficeSelected: SalesOfficeAttributes = null;

  // Condición de expedición
  expeditionConditionControl = new FormControl('', Validators.required);
  expeditionConditionsList: ExpeditionConditionAttributes[] = [];
  expeditionConditionSelected: ExpeditionConditionAttributes = null;
  expeditionConditionRequired = true;
  expeditionConditionDisabled = false;

  // Solicitante (ship-to)
  shipToControl = new FormControl('', Validators.required);
  shipToList: any[] = [];
  @ViewChild('shipToTrigger') private shipToAutoTrigger?: MatAutocompleteTrigger;
  shipToSelected: any = null;

  // Pagador (sold-to)
  soldToControl = new FormControl('', Validators.required);
  soldToSelected: any = null;

  // Direcciones
  addressControl = new FormControl('', Validators.required);
  addressesList: AddressAttributes[] = [];
  addressSelected: AddressAttributes = null;
  addressRequired = true;

  // Almacén
  warehouseControl = new FormControl('', Validators.required);
  warehousesList: WarehouseAttributes[] = [];
  warehousesFilterList: WarehouseAttributes[] = [];
  warehouseSelected: WarehouseAttributes = null;

  // ids numéricos de todas las compañías activas, para no acotar los solicitantes a una sola
  companyIds: number[] = [];

  // Referencia
  referenceControl = new FormControl('');

  // Fecha de documento / creación
  // la fecha del documento siempre es hoy y no se edita: es parámetro del cálculo de entrega
  documentDateControl = new FormControl({ value: new Date(), disabled: true }, Validators.required);

  // Ruta de entrega
  // opcional: un documento se puede generar sin ruta de entrega
  deliveryRouteControl = new FormControl('');
  deliveryRouteList: DeliveryRouteCandidate[] = [];
  deliveryRouteSelected: DeliveryRouteCandidate = null;
  deliveryRouteRequired = true;
  // el endpoint resuelve en background: el control muestra skeleton en lugar del overlay global
  deliveryRouteLoading = false;

  // Fecha de carga
  // sale del viaje, no del usuario; sin viaje queda nula y el documento igual se genera
  chargeDateControl = new FormControl<Date | null>(null);

  // Fecha de entrega: solo lectura, la escribe la resolución de ruta
  deliveryDateControl = new FormControl<Date | null>(null);

  // Hora de entrega (opcional)
  deliveryHourControl = new FormControl('');

  // Moneda
  currencyControl = new FormControl('', Validators.required);
  selectedCurrency: CurrencyAttributes;
  factorCurrencySelected: number = null;

  // Condición de pago
  paymentConditionControl = new FormControl('', Validators.required);
  paymentConditionsList: PaymentConditionAttributes[] = [];
  paymentConditionsFilterList: PaymentConditionAttributes[] = [];
  paymentConditionSelected: PaymentConditionAttributes = null;
  paymentConditionDisabled = false;
  overdueRestrictionActive = false;

  // Vendedor
  salesForceControl = new FormControl('');
  salesForcesList: DocumentSeller[] = [];
  salesForcesFilterList: DocumentSeller[] = [];
  salesForceSelected: DocumentSeller = null;
  currentUser: User = null;
  salesForceRequired = false;

  // Observaciones
  comentariesControl = new FormControl('', Validators.maxLength(900));

  // Adjuntos (reusa el componente estándar de adjuntos del backoffice)
  readonly MAX_ATTACHMENTS = 6;
  attachmentsControl = new FormControl<File[]>([]);
  attachmentConfig: FieldConfig = {
    _id: 'document-attachments',
    name: 'attachments',
    label: '',
    type: 'attachment',
    visible: true,
    required: false,
    isTypeMandatory: false,
    position: 0,
    group: '',
    groupPosition: 0,
    multiple: true,
    // mismos límites que valida el backend, el tamaño es por archivo
    allowedExtensions: 'pdf,jpg,jpeg,png,webp',
    maxFileSize: 10,
  };

  // Sugerencias (pedidos y productos del solicitante, desde el endpoint)
  orderHistoryList: any[] = [];
  suggestedProductsList: any[] = [];
  suggestedLoading = false;
  suggestedTab = 0;
  suggestedDaysOptions = [30, 60, 90];
  suggestionsExpanded = true;
  // filtros/paginación de la pestaña de documentos
  docDays = 30;
  docOfficeId = '';
  docOfficeSearch = '';
  docSearch = '';
  documentsPage = 1;
  documentsCount = 0;
  // filtros/paginación de la pestaña de productos
  prodDays = 30;
  prodOfficeId = '';
  prodOfficeSearch = '';
  prodSearch = '';
  productsPage = 1;
  productsCount = 0;
  readonly SUGGESTED_PAGE_SIZE = 10;

  // Resumen (calculado desde los artículos)
  summary = {
    itemsCount: 0,
    subtotal: 0,
    discount: 0,
    taxes: 0,
    total: 0,
  };

  private priceSubject = new Subject<void>();
  // el reprecio del cambio de solicitante descarta lo que quedó sin precio para él;
  // pasa por el subject para no adelantarse a la moneda de la nueva oficina
  private dropUnpricedOnNextRepricing = false;
  // cambiar de solicitante u oficina cancela la resolución de ruta anterior
  private deliveryRouteSubject = new Subject<void>();
  // la distribución espera a que el usuario termine de escribir la cantidad
  private distributionSubject = new Subject<DocumentItem>();
  // última cantidad aceptada por línea; se limpia al vaciar el carrito porque el lineId es determinista
  private lastValidQuantity = new Map<string, number>();

  // el listado de solicitantes resuelve en remoto: el dropdown muestra esqueleto mientras llega
  shipToSearchLoading = false;

  // autocomplete de búsqueda de artículos (dropdown inline en la lista)
  itemSearchResults: any[] = [];
  itemSearchLoading = false;
  private lastItemSearchTerm = '';
  private itemSearchSubject = new Subject<string>();

  formData = new FormGroup({
    documentTypeControl: this.documentTypeControl,
    documentClassControl: this.documentClassControl,
    salesOfficeControl: this.salesOfficeControl,
    expeditionConditionControl: this.expeditionConditionControl,
    shipToControl: this.shipToControl,
    soldToControl: this.soldToControl,
    addressControl: this.addressControl,
    warehouseControl: this.warehouseControl,
    referenceControl: this.referenceControl,
    documentDateControl: this.documentDateControl,
    deliveryRouteControl: this.deliveryRouteControl,
    chargeDateControl: this.chargeDateControl,
    deliveryDateControl: this.deliveryDateControl,
    deliveryHourControl: this.deliveryHourControl,
    currencyControl: this.currencyControl,
    paymentConditionControl: this.paymentConditionControl,
    salesForceControl: this.salesForceControl,
    comentariesControl: this.comentariesControl,
  });

  // Lista de artículos seleccionados
  items: DocumentItem[] = [];
  private dismissedBonuses = new Set<string>(); // bonos eliminados por el usuario, no se re-agregan

  constructor(
    private readonly companyService: CompanyService,
    private warehouseService: WarehouseService,
    private documentGenerateService: DocumentGenerateService,
    private fuseNavigationService: FuseNavigationService,
    private fuseMediaWatcherService: FuseMediaWatcherService,
    private handlerMessage: HandlerMessageService,
    private snackBar: MatSnackBar,
    private confirmationService: ConfirmationService,
    private userService: UserService,
    private dialog: MatDialog,
    public dgConfig: DocumentGenerateConfigService,
    private readonly i18n: LanguageService,
    private readonly routeSplit: DeliveryRouteSplitService
  ) {}

  ngOnInit(): void {
    this.watchSidebar();
    this.subs.push(this.userService.user$.subscribe((user) => (this.currentUser = user)));
    this.updateValidators();
    this.fetchDocumentClass();
    this.fetchExpeditionCondition();
    this.fetchPaymentCondition();
    this.getApplications();
    this.fetchCompanyIds();

    // La oficina de venta se completa desde el solicitante; el solicitante requiere clase de documento
    this.salesOfficeControl.disable();
    this.shipToControl.disable();

    // Búsqueda remota del solicitante (autocomplete inline, sin depender de la oficina)
    this.subs.push(
      this.shipToControl.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe((value) => {
          if (typeof value === 'string' && /^[a-f0-9]{24}$/.test(value.trim())) {
            return; // Es una selección (ObjectId), no una búsqueda
          }
          if (!this.documentClassSelected) return;
          this.shipToList = [];
          this.shipToSearchLoading = true;
          this.searchShipTosWithoutOffice(value);
        })
    );

    // búsqueda remota de artículos (autocomplete inline en la lista).
    // distinctUntilChanged va antes del debounce: el campo se limpia por código al elegir o
    // agregar un artículo, así que comparar términos ya buscados dejaba la búsqueda pegada.
    // switchMap descarta la respuesta de un término anterior que llegue tarde
    this.subs.push(
      this.itemSearchSubject
        .pipe(
          distinctUntilChanged(),
          debounceTime(250),
          switchMap((term) => this.searchItemMaterials(term))
        )
        .subscribe((results) => {
          this.itemSearchResults = results;
          this.itemSearchLoading = false;
        })
    );

    // re-precia con debounce al cambiar cantidades
    this.subs.push(
      this.priceSubject.pipe(debounceTime(400)).subscribe(() => {
        const drop = this.dropUnpricedOnNextRepricing;
        this.dropUnpricedOnNextRepricing = false;
        this.priceItems(
          drop
            ? () =>
                this.dropUnpricedItems(
                  this.items.filter((it) => !it.isBonus).map((it) => it.lineId)
                )
            : undefined
        );
      })
    );

    // misma espera que el reprecio: escribir "100" no abre el reparto al pasar por 1 y 10
    this.subs.push(
      this.distributionSubject
        .pipe(debounceTime(400))
        .subscribe((item) => this.resolveWarehouseDistribution(item))
    );

    // switchMap descarta la respuesta de un solicitante que ya no está seleccionado
    this.subs.push(
      this.deliveryRouteSubject
        .pipe(switchMap(() => this.requestDeliveryRoutes()))
        .subscribe((res) => this.applyDeliveryRouteResolution(res))
    );

    // no permitir más de MAX_ATTACHMENTS adjuntos
    this.subs.push(
      this.attachmentsControl.valueChanges.subscribe((files) => {
        if ((files?.length ?? 0) > this.MAX_ATTACHMENTS) {
          this.attachmentsControl.setValue((files as File[]).slice(0, this.MAX_ATTACHMENTS), {
            emitEvent: false,
          });
          this.handlerMessage.warning({
            message: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.MAX_ATTACHMENTS', {
              max: this.MAX_ATTACHMENTS,
            }),
          });
        }
      })
    );

    this.formData.markAllAsTouched();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.closeSuggestionsPanel();
    this.restoreSidebar();
    this.subs.forEach((s) => s.unsubscribe());
  }

  // Sidebar

  // el layout repone mode/opened por binding: se reimpone tras el ciclo de detección
  private watchSidebar(): void {
    this.subs.push(
      this.fuseMediaWatcherService.onMediaChange$.subscribe(({ matchingAliases }) => {
        this.screenIsSmall = !matchingAliases.includes('md');
        this.screenIsWide = matchingAliases.includes('lg');
        // en lg las sugerencias vuelven a la columna: el panel no puede quedar duplicado
        if (this.screenIsWide) this.closeSuggestionsPanel();
        this.sidebarForced = true;
        this.forceOverlay();
        setTimeout(() => this.forceOverlay(), 0);
      })
    );
  }

  private forceOverlay(): void {
    if (this.destroyed) return;
    const navigation = this.fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
    if (!navigation) return;
    navigation.mode = 'over';
    navigation.close();
  }

  private restoreSidebar(): void {
    if (!this.sidebarForced) return;
    const navigation = this.fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
    if (!navigation) return;
    // cierra primero para quitar el backdrop del modo 'over' (si quedó abierto al navegar)
    navigation.close();
    navigation.mode = this.screenIsSmall ? 'over' : 'side';
    if (!this.screenIsSmall) {
      navigation.open();
    }
  }

  // Validadores dinámicos (según cliente / environment)

  updateValidators(): void {
    this.deliveryRouteRequired = environment.isDeliveryRouteRequired;
    this.setControlValidators(this.deliveryRouteControl, this.deliveryRouteRequired);

    this.addressRequired = environment.isAddressesRequired;
    this.setControlValidators(this.addressControl, this.addressRequired);

    this.expeditionConditionRequired = environment.isExpeditionConditionRequired;
    this.setControlValidators(this.expeditionConditionControl, this.expeditionConditionRequired);

    this.salesForceRequired = environment.isSellerRequired;
    this.setControlValidators(this.salesForceControl, this.salesForceRequired);
  }

  private setControlValidators(control: AbstractControl, required: boolean): void {
    if (required) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  // Tipo y clase de documento

  // la etiqueta se resuelve en cada render: si la clave no está traducida (o el tipo no está en el
  // enum) se usa el texto de respaldo en lugar de mostrar la clave cruda
  documentTypeLabel(value: DocumentTypeEnum | null): string {
    if (!value) return '';
    // En este ambiente DOCUMENT_TYPE_LABELS ya trae el texto en español. resolve() hace
    // passthrough con un literal y traducira solo cuando esas etiquetas pasen a ser claves i18n.
    const label = DOCUMENT_TYPE_LABELS?.[value];
    if (label) return this.i18n.resolve(label);
    const fallbackKey = DOCUMENT_TYPE_FALLBACK_KEYS[value];
    return fallbackKey ? this.i18n.resolve(fallbackKey) : value;
  }

  onDocumentTypeChange(): void {
    const newType = this.documentTypeControl.value ?? DocumentTypeEnum.PEDIDO;
    if (newType === this.lastDocumentType) return;
    // el tipo define clase/oficina/almacén/artículos: siempre se confirma el reinicio
    const ref = this.dialog.open(FormConfigChangeModalComponent, {
      data: {
        title: this.i18n.t('DOCUMENT_GENERATE.CONFIRM_TYPE_CHANGE.TITLE'),
        message: this.i18n.t('DOCUMENT_GENERATE.CONFIRM_TYPE_CHANGE.MESSAGE'),
        icon: 'swap_horiz',
        confirmText: this.i18n.t('DOCUMENT_GENERATE.ACTIONS.CONTINUE'),
        cancelText: this.i18n.t('GENERAL.CANCEL'),
      },
      ...this.dgConfig.dialogSize('sm'),
    });
    ref.afterClosed().subscribe((ok) => {
      if (ok) {
        this.lastDocumentType = newType;
        this.applyDocumentTypeChange();
      } else {
        this.documentTypeControl.setValue(this.lastDocumentType, { emitEvent: false });
      }
    });
  }

  private applyDocumentTypeChange(): void {
    // Reinicia toda la cascada dependiente al cambiar el tipo
    this.documentClassesList = [];
    this.documentClassControl.setValue('');
    this.documentClassSelected = null;
    this.classSupportsBonus = false;
    this.bonusWarnedIds.clear();
    this.bonusNoStockWarned.clear();
    this.bonusOutOfStockWarned.clear();
    this.salesOfficeControl.setValue('');
    this.salesOfficeSelected = null;
    this.salesOfficesList = [];
    this.shipToControl.setValue('', { emitEvent: false });
    this.shipToControl.disable({ emitEvent: false });
    this.shipToSelected = null;
    this.shipToList = [];
    this.soldToSelected = null;
    this.warehouseControl.setValue('');
    this.warehousesFilterList = [];
    this.addressesList = [];
    // limpieza completa: artículos y sugerencias
    this.items = [];
    this.dismissedBonuses.clear();
    this.lastValidQuantity.clear();
    this.orderHistoryList = [];
    this.suggestedProductsList = [];
    this.documentsCount = 0;
    this.productsCount = 0;
    this.documentsPage = 1;
    this.productsPage = 1;
    this.docSearch = '';
    this.prodSearch = '';
    this.fetchDocumentClass();
  }

  private fetchDocumentClass(): void {
    const documentTypeCode = this.documentTypeControl.value ?? DocumentTypeEnum.PEDIDO;
    const query = buildQuery<DocumentClassAttributes>(
      where('_deleted', Ops.eq(false)),
      andWhere('statusId', StatusEnum.ACTIVE),
      andWhere(<any>'documentType.code', Ops.eq(documentTypeCode))
    );

    this.documentGenerateService.fetchDocumentClasses(query).subscribe((res) => {
      this.documentClassesList = [...(res.data ?? [])].sort((a, b) =>
        (a.name ?? '').localeCompare(b.name ?? '')
      );
      if (this.documentClassesList.length === 1) {
        this.documentClassControl.setValue(this.documentClassesList[0]._id.toString());
        this.onDocumentClassChange();
      }
    });
  }

  // etiqueta "CÓDIGO - NOMBRE" de los catálogos; se prefiere el código propio sobre el externo
  optionLabel(item: any): string {
    const code = item?.code || item?.externalKeys?.code || '';
    const name = item?.name ?? '';
    return code ? `${code} - ${name}` : name;
  }

  articleLabel(item: any): string {
    const code = this.applyRemoveLeftZeros(item?.code || item?.externalKeys?.code || '');
    const name = item?.name ?? '';
    return code ? `${code} - ${name}` : name;
  }

  // el autocomplete filtra por código y por nombre
  private matchesOption(item: any, term: string): boolean {
    return this.optionLabel(item).toLowerCase().includes(term);
  }

  displayDocumentClass = (value: string): string => {
    const dc = this.documentClassesList.find((d) => d._id.toString() === value);
    return dc ? this.optionLabel(dc) : value ?? '';
  };

  filteredDocumentClasses(): DocumentClassAttributes[] {
    const value = this.documentClassControl.value ?? '';
    if (/^[a-f0-9]{24}$/.test(value)) return this.documentClassesList;
    const term = value.toLowerCase();
    return this.documentClassesList.filter((dc) => this.matchesOption(dc, term));
  }

  displaySalesOffice = (value: string): string => {
    const so = this.salesOfficesList.find((s) => s._id.toString() === value);
    return so ? this.optionLabel(so) : value ?? '';
  };

  filteredSalesOffices(): SalesOfficeAttributes[] {
    const value = this.salesOfficeControl.value ?? '';
    if (/^[a-f0-9]{24}$/.test(value)) return this.salesOfficesList;
    const term = value.toLowerCase();
    return this.salesOfficesList.filter((so) => this.matchesOption(so, term));
  }

  // modo de recálculo de precio para unidades sin precio (0=no permite, 1=recalcula precio, 2=recalcula cantidad)
  get recalcMode(): number {
    return (this.documentClassSelected as any)?.recalcPriceForUnpricedUoM ?? 2;
  }

  // permite editar el precio manualmente en la tabla (canUpdatePrices)
  get canUpdatePrices(): boolean {
    return (this.documentClassSelected as any)?.canUpdatePrices === true;
  }

  // la clase mantiene los precios de la transacción origen (cotización usada como base)
  get keepsSourcePrices(): boolean {
    return (this.documentClassSelected as any)?.keepsPricesOfSourceTransaction === true;
  }

  // el precio del artículo ya incluye impuesto (company.isTaxIncluded)
  get taxIncluded(): boolean {
    return (this.company as any)?.isTaxIncluded === true;
  }

  // ISV de una línea sobre la base con descuento: si el precio incluye impuesto se extrae, si no se suma
  private computeIsv(price: number, qty: number, taxPct: number, discountUnit = 0): number {
    const gross = ((price || 0) - (discountUnit || 0)) * (qty || 0);
    const t = taxPct || 0;
    const isv = this.taxIncluded ? gross - gross / (1 + t / 100) : gross * (t / 100);
    return this.dgConfig.roundCalc(isv);
  }

  onDocumentClassChange(): void {
    const newClass = this.documentClassesList.find(
      (dc) => dc._id.toString() === this.documentClassControl.value
    );
    const prevClassId = this.documentClassSelected?._id?.toString();
    // sin clase válida o la misma clase: aplica sin refrescar (incluye el auto-select inicial)
    if (!newClass || newClass._id.toString() === prevClassId) {
      this.applyDocumentClassChange();
      return;
    }
    // cambio real sin artículos: aplica y refresca todo lo derivado de la clase (sugerencias, formato, precios)
    if (!this.items.length) {
      this.applyDocumentClassChange();
      this.reevaluateForNewClass();
      return;
    }
    // cambio real con artículos: valida cuáles siguen disponibles en la nueva clase (re-consulta backend)
    this.validateItemsForClass(newClass).subscribe((affected) => {
      const ref = this.dialog.open(FormConfigChangeModalComponent, {
        data: {
          title: this.i18n.t('DOCUMENT_GENERATE.CONFIRM_CLASS_CHANGE.TITLE'),
          message: affected.length
            ? this.i18n.t('DOCUMENT_GENERATE.CONFIRM_CLASS_CHANGE.MESSAGE_WITH_ITEMS')
            : this.i18n.t('DOCUMENT_GENERATE.CONFIRM_CLASS_CHANGE.MESSAGE'),
          icon: 'category',
          confirmText: this.i18n.t('DOCUMENT_GENERATE.ACTIONS.CONTINUE'),
          cancelText: this.i18n.t('GENERAL.CANCEL'),
          affectedItems: affected,
          affectedNote: affected.length
            ? this.i18n.t('DOCUMENT_GENERATE.CONFIRM_CLASS_CHANGE.AFFECTED_NOTE')
            : undefined,
        },
        ...this.dgConfig.dialogSize('sm'),
      });
      ref.afterClosed().subscribe((ok) => {
        // cierra el panel del autocomplete para que no quede la selección cancelada marcada
        this.classAutoTrigger?.closePanel();
        if (ok) {
          if (affected.length) this.items = this.items.filter((it) => !affected.includes(it));
          this.applyDocumentClassChange();
          this.reevaluateForNewClass();
        } else {
          this.documentClassControl.setValue(prevClassId ?? '', { emitEvent: false });
        }
      });
    });
  }

  // valida los artículos actuales contra el listado de la nueva clase; devuelve los que ya no están disponibles
  private validateItemsForClass(newClass: DocumentClassAttributes): Observable<DocumentItem[]> {
    const salesOfficeId = this.salesOfficeSelected?._id?.toString();
    const warehouseId = this.warehouseSelected?._id?.toString();
    const businessPartnerId = this.soldToSelected?.parentMongoId;
    // sin contexto suficiente para validar: no se marca ninguno (se recalculan luego)
    if (!salesOfficeId || !warehouseId || !businessPartnerId) return of([]);
    const checks = this.items
      .filter((it) => it.material) // los copiados de "usar como base" no se revalidan
      .map((it) =>
        this.documentGenerateService
          .findDocumentGeneratorMaterials({
            documentClassId: newClass._id.toString(),
            salesOfficeId,
            warehouseId,
            businessPartnerId,
            currencyId: this.selectedCurrency?._id?.toString(),
            search: it.material?.code ?? it.code ?? '',
            page: 1,
            size: 50,
          })
          .pipe(
            map((res) => {
              const list: any[] = res?.data?.data ?? [];
              const found = list.some((m) => m._id?.toString() === it._id);
              return found ? null : it;
            }),
            catchError(() => of(null)) // ante error se conserva el artículo (no se elimina)
          )
      );
    if (!checks.length) return of([]);
    return forkJoin(checks).pipe(map((results) => results.filter((x): x is DocumentItem => !!x)));
  }

  // re-evalúa todo lo derivado de la nueva clase, como al iniciar el formulario (haya o no artículos)
  private reevaluateForNewClass(): void {
    // artículos del formulario: reformatea códigos (removeLeftZeros), revalida stock y reprecia
    for (const it of this.items) {
      it.code = this.applyRemoveLeftZeros(it.material?.code ?? it.code);
    }
    // la clase nueva puede traer otras banderas de almacén: el reparto vigente
    // quedaría en el modo de la clase anterior, así que se rehace
    this.resetWarehouseAllocations();
    this.items.forEach((it) => this.validateStock(it));
    this.recomputeSummary();
    this.priceItems();
    // sugerencias (documentos + productos): re-consulta con el nuevo formato de código y reprecia
    if (this.shipToSelected) this.reloadSuggested();
  }

  private applyDocumentClassChange(): void {
    this.documentClassSelected = this.documentClassesList.find(
      (dc) => dc._id.toString() === this.documentClassControl.value
    );
    this.classSupportsBonus = this.resolveClassSupportsBonus(this.documentClassSelected);
    // habilita el solicitante solo cuando hay una clase de documento
    if (this.documentClassSelected) {
      this.shipToControl.enable({ emitEvent: false });
    } else {
      this.shipToControl.disable({ emitEvent: false });
    }
    this.fetchSalesOffice();

    if (this.documentClassSelected?.expeditionCondition) {
      this.expeditionConditionControl.setValue(this.documentClassSelected.expeditionCondition._id.toString());
      this.expeditionConditionDisabled = true;
    } else {
      this.expeditionConditionControl.setValue(null);
      this.expeditionConditionDisabled = false;
    }
    this.onExpeditionConditionChange();
  }

  // el statusId evaluado es el de la clase de línea, no el del tipo (copia congelada del maestro)
  private resolveClassSupportsBonus(documentClass: DocumentClassAttributes): boolean {
    return (documentClass?.allowedLineClasses ?? []).some(
      (lc: any) =>
        lc?.documentDetailType?.code === GIFT_LINE_TYPE_CODE &&
        (lc.statusId === undefined || lc.statusId === null || lc.statusId === StatusEnum.ACTIVE)
    );
  }

  // Oficina de venta

  private fetchSalesOffice(): void {
    if (!this.documentClassSelected) return;

    const query = buildQuery<SalesOfficeAttributes>(
      where('_deleted', Ops.eq(false)),
      andWhere('statusId', StatusEnum.ACTIVE)
    );

    this.documentGenerateService
      .fetchSalesOfficesByDocumentClass(this.documentClassSelected._id.toString(), query)
      .subscribe((res) => {
        this.salesOfficesList = res.data;
        if (this.salesOfficesList.length === 1) {
          this.salesOfficeControl.setValue(this.salesOfficesList[0]._id.toString());
          this.onSalesOfficeChange();
        }
      });
  }

  onSalesOfficeChange(): void {
    this.salesOfficeSelected = this.salesOfficesList.find(
      (so) => so._id.toString() === this.salesOfficeControl.value
    );
    this.fetchShipTo(null);
    this.getAllWarehouseByOffice();
    this.getCurrency();
    this.getListSeller();
    this.loadSuggested();
  }

  // Solicitante (ship-to) y pagador (sold-to)

  private fetchShipTo(search?: string): void {
    if (!this.salesOfficeSelected) return;
    // sin compañías resueltas no se consulta: environment.companyId ya no existe y caer a un id
    // fijo devolvería solicitantes de una compañía fuera del alcance del usuario
    if (!this.companyIds.length) return;

    const dataToSend: ShipToInterface = {
      companyIds: this.companyIds,
      distributionChainId: this.salesOfficeSelected?.distributionchain?._id?.toString() ?? '',
      search,
    };

    this.documentGenerateService.fetchShipTo(dataToSend).subscribe((res) => {
      this.shipToList = res.data;
      if (this.shipToList.length === 1) {
        this.shipToControl.setValue(this.shipToList[0]._id.toString());
        this.onShipToChange();
      }
    });
  }

  fetchShipToName = (shipToId: string): string => {
    const found = this.shipToList.find((st) => st._id.toString() === shipToId);
    return found ? this.shipToLabel(found) : '';
  };

  shipToLabel(shipTo: any): string {
    const code = shipTo?.externalKeys?.code || shipTo?.code || '';
    const name = shipTo?.name ?? '';
    return code ? `${code} - ${name}` : name;
  }

  soldToLabel(): string {
    const code =
      this.soldToSelected?.parentExternalKeys?.code || this.soldToSelected?.parentCode || '';
    const name = this.soldToSelected?.parentName ?? '';
    return code ? `${code} - ${name}` : name;
  }

  onShipToChange(): void {
    const newId = this.shipToControl.value;
    const previous = this.shipToSelected;
    const changed = previous && previous._id?.toString() !== newId;

    if (this.items.length && changed) {
      this.confirmationService
        .warning(
          this.i18n.t('DOCUMENT_GENERATE.CONFIRM_SHIP_TO_CHANGE.TITLE'),
          this.i18n.t('DOCUMENT_GENERATE.CONFIRM_SHIP_TO_CHANGE.MESSAGE_SHORT')
        )
        .subscribe((ok) => {
          if (ok) {
            this.applyShipToChange();
          } else {
            this.shipToControl.setValue(previous._id.toString(), { emitEvent: false });
          }
        });
      return;
    }
    this.applyShipToChange();
  }

  private applyShipToChange(): void {
    this.shipToSelected = this.shipToList.find((st) => st._id.toString() === this.shipToControl.value);
    if (!this.shipToSelected) {
      this.overdueRestrictionActive = false;
      this.paymentConditionsFilterList = [...this.paymentConditionsList];
      return;
    }

    this.soldToSelected = {
      parentId: this.shipToSelected.parentId,
      parentMongoId: this.shipToSelected.parentMongoId,
      parentExternalKeys: this.shipToSelected.parentExternalKeys,
      parentName: this.shipToSelected.parentName,
      parentPriceList: this.shipToSelected.parentPriceList,
      parentDiscountList: this.shipToSelected.parentDiscountList,
    };
    this.soldToControl.setValue(this.soldToSelected.parentMongoId);

    if (this.shipToSelected.paymentCondition) {
      this.paymentConditionSelected = this.shipToSelected.paymentCondition;
      this.paymentConditionControl.setValue(this.paymentConditionSelected._id.toString());
    }

    this.getRouteDelivery();

    // Direcciones embebidas en el ship-to
    if (this.shipToSelected?.addresses) {
      this.addressesList = this.shipToSelected.addresses;
      this.autoSelectAddress();
    }

    this.setUpPaymentCondition();

    // re-precia con el nuevo cliente lo que ya esté cargado y descarta, avisando,
    // lo que no tiene precio en su lista
    if (this.items.length) {
      this.dropUnpricedOnNextRepricing = true;
      this.priceSubject.next();
    }
    // carga sugerencias del solicitante (reinicia paginación de ambas tabs)
    this.reloadSuggested();
  }

  // Búsqueda de solicitante sin depender de la oficina de venta (requiere clase de documento)
  openShipToSearch(): void {
    if (!this.documentClassSelected) {
      this.handlerMessage.warning({
        message: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.SHIP_TO_REQUIRES_CLASS'),
      });
      return;
    }
    // cierra el autocomplete del solicitante por si quedó abierto (opciones cargadas + foco)
    this.shipToAutoTrigger?.closePanel();
    const ref = this.dialog.open(ShipToSearchModalComponent, {
      // El buscador de solicitantes va contra todas las compañías activas (companyIds),
      // pero el select de RUTA del modal se acota a la compañía del documento y usa el
      // _id de Mongo — son campos distintos, por eso van los dos.
      data: {
        initialSearch: '',
        salesOffices: this.salesOfficesList,
        companyIds: this.companyIds,
        companyMongoId: this.company?._id?.toString(),
      },
      ...this.dgConfig.dialogSize('lg'),
      restoreFocus: false,
    });
    ref.afterOpened().subscribe(() => this.shipToAutoTrigger?.closePanel());
    ref.afterClosed().subscribe((shipTo) => {
      if (shipTo) this.confirmShipToChange(shipTo, () => this.applyShipToFromSearch(shipTo));
    });
  }

  // búsqueda remota del solicitante (autocomplete inline), también sin oficina
  private searchShipTosWithoutOffice(search?: string): void {
    this.documentGenerateService
      .findShipTosWithoutOffice(search, undefined, undefined, undefined, 1, 25, this.companyIds)
      .subscribe({
        next: (res) => {
          this.shipToList = res?.data?.data ?? [];
          this.shipToSearchLoading = false;
        },
        error: () => {
          this.shipToList = [];
          this.shipToSearchLoading = false;
        },
      });
  }

  onInlineShipToSelected(): void {
    const shipTo = this.shipToList.find((st) => st._id.toString() === this.shipToControl.value);
    if (shipTo) this.confirmShipToChange(shipTo, () => this.applyShipToFromSearch(shipTo));
  }

  // confirma el cambio de solicitante cuando ya hay artículos (afecta precios y datos del documento)
  private confirmShipToChange(shipTo: any, apply: () => void): void {
    const changed = this.shipToSelected && this.shipToSelected._id?.toString() !== shipTo._id?.toString();
    if (this.items.length && changed) {
      const ref = this.dialog.open(FormConfigChangeModalComponent, {
        data: {
          title: this.i18n.t('DOCUMENT_GENERATE.CONFIRM_SHIP_TO_CHANGE.TITLE'),
          message: this.i18n.t('DOCUMENT_GENERATE.CONFIRM_SHIP_TO_CHANGE.MESSAGE'),
          icon: 'person',
          confirmText: this.i18n.t('DOCUMENT_GENERATE.ACTIONS.CONTINUE'),
          cancelText: this.i18n.t('GENERAL.CANCEL'),
        },
        ...this.dgConfig.dialogSize('sm'),
      });
      ref.afterClosed().subscribe((ok) => {
        this.shipToAutoTrigger?.closePanel();
        if (ok) apply();
        else this.restorePreviousShipTo();
      });
      return;
    }
    apply();
  }

  // restaura el solicitante anterior en el control (y lo reinyecta a la lista para conservar su nombre)
  private restorePreviousShipTo(): void {
    if (!this.shipToSelected) {
      this.shipToControl.setValue('', { emitEvent: false });
      return;
    }
    if (!this.shipToList.some((st) => st._id?.toString() === this.shipToSelected._id?.toString())) {
      this.shipToList = [this.shipToSelected, ...this.shipToList];
    }
    this.shipToControl.setValue(this.shipToSelected._id.toString(), { emitEvent: false });
  }

  // Al elegir un solicitante, autocompleta la oficina desde su routingSalesOffice
  private applyShipToFromSearch(shipTo: any): void {
    const office = shipTo?.routingSalesOffice;
    if (!office?._id) {
      this.setShipToFromSearch(shipTo);
      return;
    }
    const officeIdStr = office._id.toString();
    const existing = this.salesOfficesList.find((o) => o._id.toString() === officeIdStr);
    if (existing) {
      this.resolveOfficeForShipTo(existing, shipTo);
      return;
    }
    // la oficina del solicitante no está en la lista de la clase: la traemos completa
    const query = buildQuery<SalesOfficeAttributes>(
      where('_id', Ops.eq(officeIdStr)),
      andWhere('_deleted', Ops.eq(false)),
      andWhere('statusId', StatusEnum.ACTIVE)
    );
    this.documentGenerateService.fetchSalesOffices(query).subscribe({
      next: (res) => {
        // se verifica el _id: un filtro vacío devolvería todas las oficinas
        const full = res.data?.find((o) => o._id?.toString() === officeIdStr);
        if (full) {
          this.salesOfficesList = [...this.salesOfficesList, full];
          this.resolveOfficeForShipTo(full, shipTo);
        } else {
          this.setShipToFromSearch(shipTo);
        }
      },
      error: () => this.setShipToFromSearch(shipTo),
    });
  }

  // si la oficina del solicitante no tiene almacén usable, el usuario elige otra con almacén para la transacción
  private resolveOfficeForShipTo(office: any, shipTo: any): void {
    this.officeUsableWarehouseCount(office).subscribe((count) => {
      if (count > 0) {
        this.selectOfficeAndCascade(office);
        this.setShipToFromSearch(shipTo);
      } else {
        this.openSalesOfficeSelect(office, shipTo);
      }
    });
  }

  // cantidad de almacenes usables de la oficina (activos + de la company actual, vía filterComboBox);
  // el array embebido `warehouses` no basta: puede referenciar almacenes de otra company
  private officeUsableWarehouseCount(office: any): Observable<number> {
    const ids = (office?.warehouses ?? []).map((w: any) => w._id);
    const companyId = office?.company?.id;
    if (!ids.length || !companyId) return of(0);
    return this.warehouseService
      .filterComboBox({
        officeWarehouse: ids,
        company: office?.company?.id ? [office.company.id] : this.companyIds,
      })
      .pipe(
        map((res: IRequestResult<WarehouseAttributes[]>) => res?.data?.length ?? 0),
        catchError(() => of(0))
      );
  }

  // precalcula qué oficinas de la clase tienen almacén usable y abre el modal de selección
  private openSalesOfficeSelect(office: any, shipTo: any): void {
    const offices = this.salesOfficesList;
    const checks = offices.map((o) =>
      this.officeUsableWarehouseCount(o).pipe(map((count) => ({ id: o._id.toString(), usable: count > 0 })))
    );
    const notice = this.i18n.t('DOCUMENT_GENERATE.MESSAGES.OFFICE_WITHOUT_WAREHOUSE', {
      officeName: office.name ?? '',
    });
    (checks.length ? forkJoin(checks) : of([])).subscribe((results) => {
      const usableOfficeIds = results.filter((r) => r.usable).map((r) => r.id);
      const ref = this.dialog.open(SalesOfficeSelectModalComponent, {
        data: { offices, notice, usableOfficeIds },
        ...this.dgConfig.dialogSize('sm'),
        disableClose: true,
      });
      ref.afterClosed().subscribe((picked: any) => {
        if (picked) this.selectOfficeAndCascade(picked);
        this.setShipToFromSearch(shipTo);
      });
    });
  }

  private selectOfficeAndCascade(office: any): void {
    this.salesOfficeControl.setValue(office._id.toString());
    this.salesOfficeSelected = office;
    this.getAllWarehouseByOffice();
    this.getCurrency();
    this.getListSeller();
  }

  private setShipToFromSearch(shipTo: any): void {
    this.shipToList = [shipTo];
    this.shipToSelected = null;
    this.shipToControl.setValue(shipTo._id.toString(), { emitEvent: false });
    this.applyShipToChange();
    // con un solicitante ya elegido, se puede cambiar la oficina de venta
    this.salesOfficeControl.enable({ emitEvent: false });
  }

  // Cambio manual de oficina (con solicitante ya elegido): confirma y quita artículos no válidos
  onSalesOfficeManualChange(): void {
    const newId = this.salesOfficeControl.value;
    const previous = this.salesOfficeSelected;
    if (previous && previous._id?.toString() === newId) return;
    const newOffice = this.salesOfficesList.find((o) => o._id.toString() === newId);
    if (!newOffice) return;

    const affected = this.itemsNotInOffice(newOffice);
    const ref = this.dialog.open(OfficeChangeConfirmModalComponent, {
      data: { officeName: this.optionLabel(newOffice), affectedItems: affected, totalItems: this.items.length },
      ...this.dgConfig.dialogSize('sm'),
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.applyOfficeChange(newOffice, affected);
      } else {
        this.salesOfficeControl.setValue(previous?._id?.toString() ?? '', { emitEvent: false });
      }
    });
  }

  // artículos cuyo material NO pertenece a la oficina (cadena de distribución / división)
  private itemsNotInOffice(office: any): DocumentItem[] {
    const chainId = office?.distributionchain?.id;
    const divisionIds = (office?.divisions ?? []).map((d: any) => d.id);
    return this.items.filter((it) => {
      const m = it.material;
      if (!m) return false; // sin material (ej. sugerido mock): no se elimina
      const inChain = chainId == null || (m.distributionChainIds ?? []).includes(chainId);
      const inDivision = !divisionIds.length || divisionIds.includes(m.divisionId);
      return !(inChain && inDivision);
    });
  }

  private applyOfficeChange(office: any, affected: DocumentItem[]): void {
    if (affected.length) {
      this.items = this.items.filter((it) => !affected.includes(it));
    }
    this.salesOfficeControl.setValue(office._id.toString(), { emitEvent: false });
    this.salesOfficeSelected = office;
    // recarga todo lo dependiente de la oficina + reprecio (getCurrency reprecia al final)
    this.getAllWarehouseByOffice();
    this.getListSeller();
    this.getCurrency();
    // la nueva oficina puede traer otra compañía, y con ella otros días festivos
    this.getRouteDelivery();
    this.recomputeSummary();
  }

  onAddressChange(): void {
    this.addressSelected = this.addressesList.find((a) => a._id.toString() === this.addressControl.value);
  }

  // una dirección es de envío si su tipo (o alguno de sus tipos) tiene isDelivery
  private isDeliveryAddress(a: any): boolean {
    return a?.type?.isDelivery === true || (a?.types ?? []).some((t: any) => t?.isDelivery === true);
  }

  // selecciona la dirección; si hay varias de entrega, abre un modal para elegir
  private autoSelectAddress(): void {
    if (!this.addressesList?.length) return;

    if (this.addressesList.length === 1) {
      this.applyAddress(this.addressesList[0]);
      return;
    }

    const deliveries = this.addressesList.filter((a: any) => this.isDeliveryAddress(a));
    if (deliveries.length === 1) {
      this.applyAddress(deliveries[0]);
    } else if (deliveries.length > 1) {
      const ref = this.dialog.open(AddressSelectModalComponent, {
        data: { addresses: deliveries },
        ...this.dgConfig.dialogSize('sm'),
        disableClose: true,
      });
      ref.afterClosed().subscribe((selected: any) => {
        if (selected) this.applyAddress(selected);
      });
    }
  }

  private applyAddress(address: any): void {
    this.addressControl.setValue(address._id.toString());
    this.addressSelected = address;
    this.onAddressChange();
  }

  // Almacén

  private getAllWarehouseByOffice(): void {
    const filter = {
      officeWarehouse: this.salesOfficeSelected ? this.salesOfficeSelected.warehouses.map((w) => w._id) : [],
      // sin compañía en la oficina se acota a las del usuario, nunca a un id fijo
      company: this.salesOfficeSelected?.company?.id
        ? [this.salesOfficeSelected.company.id]
        : this.companyIds,
    };

    this.warehouseService.filterComboBox(filter).subscribe({
      next: (res: IRequestResult<WarehouseAttributes[]>) => {
        this.warehousesList = res.data;
        this.warehousesFilterList = this.warehousesList.slice();
        // si la oficina tiene uno o más almacenes, se selecciona el primero
        if (this.warehousesList.length >= 1) {
          this.warehouseControl.setValue(this.warehousesList[0]._id.toString());
          this.onWarehouseChange();
        }
      },
      error: (err) => {
        this.handlerMessage.error({
          message:
            err.error?.message || this.i18n.t('DOCUMENT_GENERATE.MESSAGES.FETCH_WAREHOUSES_ERROR'),
          statusCode: 500,
        });
      },
    });
  }

  displayWarehouse = (value: string): string => {
    const w = this.warehousesList.find((wh) => wh._id.toString() === value);
    return w ? this.optionLabel(w) : value ?? '';
  };

  filteredWarehouses(): WarehouseAttributes[] {
    const value = this.warehouseControl.value ?? '';
    if (/^[a-f0-9]{24}$/.test(value)) return this.warehousesFilterList;
    const term = value.toLowerCase();
    return this.warehousesFilterList.filter((w) => this.matchesOption(w, term));
  }

  onWarehouseChange(): void {
    this.warehouseSelected = this.warehousesList.find((w) => w._id.toString() === this.warehouseControl.value);
    this.resetWarehouseAllocations();
  }

  // el conjunto de almacenes y su orden de prioridad dependen del almacén y de la
  // oficina: al cambiar cualquiera de los dos, las líneas repartidas se recalculan
  private resetWarehouseAllocations(): void {
    if (!this.items.length) return;
    const grouped = new Map<string, DocumentItem>();
    for (const it of this.items) {
      if (it.isBonus) continue;
      const key = `${it._id}|${it.unitMeasureId}`;
      const first = grouped.get(key);
      if (first) {
        // las hermanas se refunden en la primera línea del grupo
        first.quantity += it.quantity || 0;
        first.quantityText = String(first.quantity);
        continue;
      }
      grouped.set(key, it);
    }
    const survivors = new Set([...grouped.values()].map((it) => it.lineId));
    this.items = this.items.filter(
      (it) => survivors.has(it.lineId) || (it.isBonus && survivors.has(it.bonusParentId ?? ''))
    );
    // la disponibilidad cacheada corresponde a la oficina anterior
    for (const it of grouped.values()) {
      it.warehouseStocks = undefined;
      it.allocations = undefined;
      it.withoutStock = false;
      this.assignWarehouseToItem(it, this.warehouseSelected);
      // el stock capturado con el artículo es el de la oficina anterior: se recarga antes
      // de repartir para no medir la disponibilidad contra almacenes que ya no aplican
      this.loadWarehouseStocks(it).subscribe(() => {
        if (this.allowsMultipleWarehouses) this.distributionSubject.next(it);
      });
    }
    this.recomputeSummary();
  }

  // Ruta de entrega

  private getRouteDelivery(): void {
    // cambió el solicitante o la oficina: lo resuelto por documento ya no aplica
    this.clearResolvedRoutes();
    if (!this.shipToControl.value || !this.salesOfficeSelected?.company?._id) {
      this.clearDeliveryRoute();
      return;
    }
    this.deliveryRouteLoading = true;
    this.deliveryRouteSubject.next();
  }

  // devuelve una resolución vacía ante error para no romper el switchMap del sujeto
  private requestDeliveryRoutes(
    materialIds?: string[],
    documents?: DeliveryRouteDocumentRequest[],
    background = true
  ): Observable<DeliveryRouteResolution | null> {
    const body: DeliveryRouteRequest = {
      shipToId: this.shipToControl.value,
      companyId: this.salesOfficeSelected.company._id.toString(),
      documentDate: this.plainDocumentDate(),
    };
    if (materialIds?.length) body.materialIds = materialIds;
    if (documents?.length) body.documents = documents;

    return this.documentGenerateService.resolveDeliveryRoutes(body, background).pipe(
      map((res: IRequestResult<DeliveryRouteResolution>) => res.data ?? null),
      catchError((err) => {
        this.handlerMessage.error({
          message: err.error?.message,
          alternativeMessage: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.FETCH_DELIVERY_ROUTES_ERROR'),
          statusCode: err?.status,
        });
        return of(null);
      })
    );
  }

  private applyDeliveryRouteResolution(resolution: DeliveryRouteResolution | null): void {
    this.deliveryRouteLoading = false;
    if (!resolution) {
      this.clearDeliveryRoute();
      return;
    }
    this.deliveryRouteList = resolution.routes.filter((route) => route.eligible);
    this.deliveryRouteControl.setValue(resolution.selectedRouteId ?? '');
    this.onDeliveryRouteChange();
  }

  private clearDeliveryRoute(): void {
    this.deliveryRouteLoading = false;
    this.deliveryRouteList = [];
    this.deliveryRouteControl.setValue('');
    this.onDeliveryRouteChange();
  }

  onDeliveryRouteChange(): void {
    this.deliveryRouteSelected = this.deliveryRouteList.find(
      (dr) => dr._id.toString() === this.deliveryRouteControl.value
    );
    const trip = this.deliveryRouteSelected?.trip;
    // sin viaje queda la fecha estimada que calculó la ventana horaria
    const estimated = this.routeResolution?.estimatedDeliveryDate;
    this.deliveryDateControl.setValue(
      trip?.dateHourStartTeoric
        ? new Date(trip.dateHourStartTeoric)
        : estimated
          ? new Date(estimated)
          : null
    );
    this.chargeDateControl.setValue(trip?.loadDateTime ? new Date(trip.loadDateTime) : null);
  }

  // descripción del viaje ganador, para el hint bajo el control de ruta
  deliveryTripLabel(): string {
    return this.tripLabel(this.deliveryRouteSelected?.trip);
  }

  private tripLabel(trip: DeliveryRouteTrip | null | undefined): string {
    if (!trip) return '';
    const code = trip.externalKeys?.code ?? trip.id;
    const parts = [`Viaje ${code}`];
    if (trip.description) parts.push(trip.description);
    return parts.join(' - ');
  }

  // día calendario local, sin hora, para que el backend no lo desplace por zona horaria
  private plainDocumentDate(): string {
    const date = this.documentDateControl.value ?? new Date();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  // Compañías

  // el listado de solicitantes se filtra por compañía; se piden todas las activas
  // en lugar de la del environment, que dejaba fuera a los socios de las demás
  private fetchCompanyIds(): void {
    const query = buildQuery<CompanyAttributes>(
      where('statusId', Ops.eq(StatusEnum.ACTIVE)),
      andWhere('_deleted', Ops.eq(false))
    );

    this.companyService.fetchCompany(query).subscribe({
      next: (res: IRequestResult<CompanyAttributes[]>) => {
        this.companyIds = (res.data ?? []).map((company) => company.id).filter(Boolean);
      },
      error: () => {},
    });
  }

  // Moneda

  private getCurrency(): void {
    const companyId = this.salesOfficeSelected ? this.salesOfficeSelected.company.id : null;
    const query = buildQuery<CompanyAttributes>(where('id', Ops.eq(companyId)));

    this.companyService.fetchCompany(query).subscribe({
      next: (res: IRequestResult<CompanyAttributes[]>) => {
        this.company = res.data?.length > 0 ? res.data[0] : null;
        this.dgConfig.setFromCompany(this.company);
        if (this.company?.currencyBase) {
          this.currencyControl.setValue(this.company.currencyBase._id.toString());
          this.selectedCurrency = this.company.currencyBase;
          this.getCurrencyFactor();
          // reprecia los artículos y los productos sugeridos con la nueva oficina/moneda
          this.priceItems();
          this.repriceSuggestedProducts();
        }
      },
      error: (err) => {
        this.handlerMessage.error({
          message:
            err.error?.message || this.i18n.t('DOCUMENT_GENERATE.MESSAGES.FETCH_CURRENCY_ERROR'),
          statusCode: 500,
        });
      },
    });
  }

  private getApplications(): void {
    this.documentGenerateService
      .fetchApplications(environment.applicationId, environment.subApplicationId)
      .subscribe({
        next: (res: IRequestResult<any>) => {
          this.applicationSelected = res.data?.application ?? null;
          this.subApplicationSelected = res.data?.subApplication ?? null;
        },
        error: () => {},
      });
  }

  private getCurrencyFactor(): void {
    const currencySourceId = this.selectedCurrency._id.toString();
    const currencyTargetId = this.company.currencyBase._id.toString();

    this.documentGenerateService.fetchCurrencyFactor(currencySourceId, currencyTargetId).subscribe({
      next: (res: IRequestResult<any>) => {
        this.factorCurrencySelected = res.data?.factor;
      },
      error: () => {},
    });
  }

  // Vendedor

  private getListSeller(): void {
    const dataToSend = {
      applications_ids: environment.applicationId,
      sub_applications_ids: environment.subApplicationId_csmobile,
      distribution_chains_ids: this.salesOfficeSelected?.distributionchain._id,
    };

    this.documentGenerateService.findPreSaleAllUserLogin(dataToSend).subscribe({
      next: (res: IRequestResult<DocumentSeller[]>) => {
        if (res.status === 'success') {
          this.salesForcesList = res.data;
          this.salesForcesFilterList = this.salesForcesList.slice();
        }
      },
      error: (err) => {
        this.handlerMessage.error({
          message:
            err.error?.message || this.i18n.t('DOCUMENT_GENERATE.MESSAGES.FETCH_SELLERS_ERROR'),
          statusCode: 500,
        });
      },
    });
  }

  onSalesForcesChange(): void {
    this.salesForceSelected = this.salesForcesList.find(
      (sf) => sf.masterUserId.toString() === this.salesForceControl.value
    );
  }

  // Condición de expedición

  private fetchExpeditionCondition(): void {
    const query = buildQuery<ExpeditionConditionAttributes>(
      where('_deleted', Ops.eq(false)),
      andWhere('statusId', StatusEnum.ACTIVE)
    );

    this.documentGenerateService.fetchExpeditionCondition(query).subscribe((res) => {
      this.expeditionConditionsList = res.data;
      if (this.expeditionConditionsList.length === 1) {
        this.expeditionConditionControl.setValue(this.expeditionConditionsList[0]._id.toString());
      }
      this.onExpeditionConditionChange();
    });
  }

  onExpeditionConditionChange(): void {
    this.expeditionConditionSelected = this.expeditionConditionsList.find(
      (ec) => ec._id.toString() === this.expeditionConditionControl.value
    );
  }

  // Condición de pago (+ restricción por morosidad)

  private fetchPaymentCondition(): void {
    const query = buildQuery<PaymentConditionAttributes>(
      where('_deleted', Ops.eq(false)),
      andWhere('statusId', StatusEnum.ACTIVE)
    );

    this.documentGenerateService.fetchPaymentConditions(query).subscribe((res) => {
      this.paymentConditionsList = res.data;
      this.paymentConditionsFilterList = [...this.paymentConditionsList];
      if (this.paymentConditionsList.length === 1) {
        this.paymentConditionControl.setValue(this.paymentConditionsList[0]._id.toString());
      }
      if (this.shipToSelected) {
        this.setUpPaymentCondition();
      }
    });
  }

  onPaymentConditionChange(): void {
    this.paymentConditionSelected = this.paymentConditionsList.find(
      (pc) => pc._id.toString() === this.paymentConditionControl.value
    );
  }

  private setUpPaymentCondition(): void {
    if (!this.paymentConditionsList?.length) return;

    const soldToMongoId = this.shipToSelected?.parentMongoId?.toString();
    const distChainId = this.salesOfficeSelected?.distributionchain?._id?.toString();

    if (soldToMongoId && distChainId) {
      this.subs.push(
        this.documentGenerateService.shouldRestrictToCashOnly(soldToMongoId, distChainId).subscribe({
          next: (result: IOverduePaymentRestrictionResult) => {
            if (
              this.shipToSelected?.parentMongoId?.toString() === soldToMongoId &&
              this.salesOfficeSelected?.distributionchain?._id?.toString() === distChainId
            ) {
              this.applyOverdueRestriction(result);
            }
          },
          error: () => {
            this.handlerMessage.warning({
              message: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.OVERDUE_CHECK_FAILED'),
            });
            this.applyOverdueRestriction({ applies: true });
          },
        })
      );
    } else {
      this.overdueRestrictionActive = false;
      this.applyStandardPaymentConditions();
    }
  }

  private applyOverdueRestriction(result: IOverduePaymentRestrictionResult): void {
    if (result.applies) {
      this.overdueRestrictionActive = true;
      const cashOnly = this.paymentConditionsList.filter((pc) => pc.type === PaymentConditionTypeEnum.DEBIT);
      this.paymentConditionsFilterList = cashOnly;
      this.paymentConditionDisabled = false;
      if (cashOnly.length > 0) {
        this.paymentConditionControl.setValue(cashOnly[0]._id.toString());
        this.onPaymentConditionChange();
      }
    } else {
      this.overdueRestrictionActive = false;
      this.applyStandardPaymentConditions();
    }
  }

  private applyStandardPaymentConditions(): void {
    if (!this.shipToSelected) {
      this.paymentConditionsFilterList = [...this.paymentConditionsList];
      return;
    }
    const shiptoPaymentCondition = this.shipToSelected.paymentCondition;
    const byValue = this.paymentConditionsList.filter(
      (pc) => pc._id.toString() === shiptoPaymentCondition?._id?.toString()
    );
    const byType = this.paymentConditionsList.filter((pc) => pc.type === PaymentConditionTypeEnum.DEBIT);

    this.paymentConditionsFilterList = [
      ...new Map([...byValue, ...byType].map((item) => [item._id.toString(), item])).values(),
    ];
    this.paymentConditionDisabled = false;
  }

  // Lista de artículos

  // el usuario escribe en el buscador de artículos: dispara la búsqueda remota con debounce
  onItemSearchTerm(term: string): void {
    if (!term) this.itemSearchResults = [];
    const value = term ?? '';
    // el pipe descarta el término repetido (escribir un espacio al final, por ejemplo):
    // sin comprobarlo aquí, el skeleton se quedaría encendido esperando una respuesta que no viene
    this.itemSearchLoading = !!value && value !== this.lastItemSearchTerm;
    this.lastItemSearchTerm = value;
    // el término vacío también se emite: es lo que permite volver a buscar la misma palabra
    this.itemSearchSubject.next(value);
  }

  // el campo se limpió por código (selección o agregado): se olvida el último término buscado
  private resetItemSearch(): void {
    this.itemSearchResults = [];
    this.itemSearchLoading = false;
    this.lastItemSearchTerm = '';
    this.itemSearchSubject.next('');
  }

  // resultados del dropdown de artículos (mismo listado que usa el modal de búsqueda)
  private searchItemMaterials(term: string): Observable<any[]> {
    // sin contexto completo no se busca (silencioso: no molesta con avisos en cada tecla)
    const hasContext =
      !!term &&
      !!this.shipToSelected &&
      !!this.documentClassSelected?._id &&
      !!this.salesOfficeSelected?._id &&
      !!this.warehouseSelected?._id &&
      !!this.soldToSelected?.parentMongoId;
    if (!hasContext) return of([]);
    // el listado resuelve el precio de cada material que devuelve: menos filas es
    // menos espera, y en el dropdown no se ven más de diez
    const body = this.buildMaterialsQueryBody(term, 1, 10);
    if (!body) return of([]);
    return this.documentGenerateService.findDocumentGeneratorMaterials(body, true).pipe(
      map((res) => res?.data?.data ?? []),
      catchError(() => of([]))
    );
  }

  // el usuario elige un artículo del dropdown: se agrega directamente
  onItemSearchSelect(material: any): void {
    this.resetItemSearch();
    if (material) this.addMaterials([material]);
  }

  onItemsAdd(searchValue: string): void {
    this.resetItemSearch();
    if (!this.shipToSelected) {
      this.handlerMessage.warning({
        message: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.SHIP_TO_REQUIRED'),
      });
      return;
    }
    if (!searchValue) {
      this.openProductSearch('');
      return;
    }
    const body = this.buildMaterialsQueryBody(searchValue, 1, 50);
    if (!body) return;
    this.documentGenerateService.findDocumentGeneratorMaterials(body).subscribe({
      next: (res) => {
        const list: any[] = res?.data?.data ?? [];
        const term = searchValue.toLowerCase();
        const exact = list.find(
          (m) =>
            m.code?.toLowerCase() === term ||
            this.applyRemoveLeftZeros(m.code)?.toLowerCase() === term ||
            m.externalKeys?.code?.toLowerCase() === term ||
            m.name?.toLowerCase() === term
        );
        // la coincidencia exacta siempre pasa por el flujo de agregado, que avisa si no se puede vender
        if (exact) {
          this.addMaterials([exact]);
        } else {
          this.openProductSearch(searchValue);
        }
      },
      error: () => this.openProductSearch(searchValue),
    });
  }

  // arma el body del listado; el backend resuelve los filtros por jerarquía oficina > clase > socio
  private buildMaterialsQueryBody(search: string, page: number, size: number): any | null {
    const documentClassId = this.documentClassSelected?._id?.toString();
    const salesOfficeId = this.salesOfficeSelected?._id?.toString();
    const warehouseId = this.warehouseSelected?._id?.toString();
    const businessPartnerId = this.soldToSelected?.parentMongoId;
    if (!documentClassId || !salesOfficeId || !warehouseId || !businessPartnerId) {
      this.handlerMessage.warning({
        message: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.SELECT_CLASS_SHIPTO_WAREHOUSE'),
      });
      return null;
    }
    return {
      documentClassId,
      salesOfficeId,
      warehouseId,
      // con múltiples almacenes el disponible debe venir de toda la oficina, no solo del principal
      warehouseIds: this.allowsMultipleWarehouses ? this.officeWarehouseIds() : undefined,
      businessPartnerId,
      currencyId: this.selectedCurrency?._id?.toString(),
      search: search ?? '',
      page,
      size,
    };
  }

  // almacenes candidatos en orden de prioridad: el del formulario y luego el resto
  // en el orden en que los devuelve la oficina de venta
  private orderedWarehouses(): WarehouseAttributes[] {
    const selectedId = this.warehouseSelected?._id?.toString();
    const rest = this.warehousesList.filter((w) => w._id?.toString() !== selectedId);
    return this.warehouseSelected ? [this.warehouseSelected, ...rest] : [...this.warehousesList];
  }

  private officeWarehouseIds(): string[] {
    return this.orderedWarehouses()
      .map((w) => w._id?.toString())
      .filter(Boolean);
  }

  // el stock que trae cada origen es distinto: la búsqueda de artículos ya viene
  // acotada, pero los sugeridos traen todos los almacenes del sistema. Se acota aquí
  // para que la disponibilidad sea la misma en todas las pantallas
  private scopeMaterialStock<T>(material: T): T {
    const rows = (material as any)?.materialStock;
    if (!Array.isArray(rows)) return material;
    const allowed = this.allowsMultipleWarehouses
      ? this.officeWarehouseIds()
      : [this.warehouseSelected?._id?.toString()].filter(Boolean);
    if (!allowed.length) return material;
    return {
      ...(material as any),
      materialStock: rows.filter((x: any) => allowed.includes(x?.warehouseId?.toString())),
    };
  }

  openProductSearch(term: string): void {
    if (!this.shipToSelected) {
      this.handlerMessage.warning({
        message: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.SHIP_TO_REQUIRED'),
      });
      return;
    }
    if (!this.documentClassSelected?._id || !this.warehouseSelected?._id) {
      this.handlerMessage.warning({
        message: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.SELECT_CLASS_WAREHOUSE'),
      });
      return;
    }
    const ref = this.dialog.open(ProductSearchModalComponent, {
      data: {
        initialSearch: term,
        documentClassId: this.documentClassSelected?._id?.toString(),
        warehouseId: this.warehouseSelected?._id?.toString(),
        businessPartnerId: this.soldToSelected?.parentMongoId,
        salesOfficeId: this.salesOfficeSelected?._id?.toString(),
        currencyId: this.selectedCurrency?._id?.toString(),
        showStock: (this.documentClassSelected as any)?.showCurrentStockForMaterials === true,
        removeLeftZeros: (this.documentClassSelected as any)?.removeLeftZerosOfMaterial === true,
        umpActive: (this.company as any)?.UMPActive !== false,
        recalcMode: this.recalcMode,
      },
      ...this.dgConfig.dialogSize('lg'),
    });
    ref.afterClosed().subscribe((selected: any[]) => {
      if (selected?.length) {
        this.addMaterials(selected);
      }
    });
  }

  openOrderItems(): void {
    const ref = this.dialog.open(OrderItemsModalComponent, {
      data: {
        items: this.items,
        clientName: this.shipToSelected?.name,
        clientCode: this.shipToSelected?.externalKeys?.code,
        canAdd: !!this.shipToSelected,
        editablePrice: this.canUpdatePrices,
        taxIncluded: this.taxIncluded,
        allowMultipleWarehouses: this.allowsMultipleWarehouses,
        splitsByWarehouse: this.splitsByWarehouse,
        onAdd: (term: string) => this.onItemsAdd(term),
        onChangeUnit: (item: DocumentItem) => this.onChangeUnit(item),
        onQuantityChange: (it: DocumentItem) => this.onItemQuantityChange(it),
        onCalculate: (item: DocumentItem) => this.onOpenCalculator(item),
        onDetail: (item: DocumentItem) => this.onOpenDetail(item),
        onWarehouses: (item: DocumentItem) => this.onOpenWarehouseDistribution(item),
        onPriceChange: (it: DocumentItem) => this.onItemPriceChange(it),
        onRemove: (item: DocumentItem) => this.onRemoveItem(item),
      },
      ...this.dgConfig.dialogSize('lg'),
    });
    ref.afterClosed().subscribe(() => this.recomputeSummary());
  }

  onChangeUnit(item: DocumentItem): void {
    const ref = this.dialog.open(ChangeUnitModalComponent, {
      data: {
        item,
        warehouseId: this.warehouseSelected?._id?.toString(),
        businessPartnerId: this.soldToSelected?.parentMongoId,
        salesOfficeId: this.salesOfficeSelected?._id?.toString(),
        currencyId: this.selectedCurrency?._id?.toString(),
        currency: 'L.',
        showStock: (this.documentClassSelected as any)?.showCurrentStockForMaterials === true,
        umpActive: (this.company as any)?.UMPActive !== false,
        recalcMode: this.recalcMode,
      },
      ...this.dgConfig.dialogSize('md'),
    });
    ref.afterClosed().subscribe((unit) => {
      if (!unit || !unit.unitMeasureId) return;
      // modo 2: unidad sin precio -> pedir cantidad y convertir a la UMB
      if (this.recalcMode === 2 && unit.hasPrice === false && !unit.isUMB) {
        this.openUnpricedUomModal(item, unit);
        return;
      }
      this.applyUnitToItem(item, unit.unitMeasureId, unit.name, unit.price, unit.isCommercialUom);
    });
  }

  private applyUnitToItem(
    item: DocumentItem,
    unitMeasureId: string,
    unitLabel: string,
    price: number | undefined,
    isCommercialUom: boolean
  ): void {
    const wasBlocked = !item.isCommercialUom;
    item.unitMeasureId = unitMeasureId;
    item.unitLabel = unitLabel;
    item.price = price ?? item.price;
    item.manualPrice = false; // cambia la unidad → se reprecia
    item.isCommercialUom = isCommercialUom !== false;
    this.refreshLineId(item); // la unidad forma parte de la identidad de la línea
    // si estaba bloqueado y la nueva unidad es vendible, se activa con cantidad 1
    if (wasBlocked && item.isCommercialUom && (item.quantity ?? 0) <= 0) {
      item.quantity = 1;
      item.quantityText = '1';
    }
    this.applyQuantityDecimals(item); // la nueva unidad puede no aceptar decimales
    this.validateStock(item); // el disponible cambia según la unidad
    this.recomputeSummary();
    this.priceSubject.next();
  }

  // modo 2: pide la cantidad en la unidad sin precio y convierte la línea a la UMB
  private openUnpricedUomModal(item: DocumentItem, unit: any): void {
    const base = item.material?.baseUnitOfMeasure ?? {};
    const ref = this.dialog.open(UnpricedUomModalComponent, {
      data: {
        productLabel: this.articleLabel(item),
        selectedUnitName: unit.name,
        baseUnitName: base.name ?? base.code ?? this.i18n.t('DOCUMENT_GENERATE.COLUMNS.BASE_UOM_FALLBACK'),
        factor: unit.factor ?? 1,
      },
      ...this.dgConfig.dialogSize('sm'),
    });
    ref.afterClosed().subscribe((qtyBase: number) => {
      if (typeof qtyBase !== 'number' || qtyBase <= 0) return;
      item.unitMeasureId = base._id?.toString() ?? item.unitMeasureId;
      item.unitLabel = base.name ?? base.code ?? item.unitLabel;
      item.isCommercialUom = true;
      this.refreshLineId(item);
      item.quantity = qtyBase;
      item.quantityText = String(qtyBase);
      item.manualPrice = false; // cambia la unidad → se reprecia
      this.validateStock(item);
      this.recomputeSummary();
      this.priceSubject.next(); // reprecia en la UMB (que sí tiene precio)
    });
  }

  // Calculadora de unidades: convierte una cantidad de otra unidad a la unidad de venta actual
  onOpenCalculator(item: DocumentItem): void {
    const ref = this.dialog.open(UnitCalculatorModalComponent, {
      data: {
        item,
        umpActive: (this.company as any)?.UMPActive !== false,
      },
      ...this.dgConfig.dialogSize('md'),
    });
    ref.afterClosed().subscribe((converted: number) => {
      if (typeof converted === 'number' && converted > 0) {
        item.quantity = converted;
        item.quantityText = String(converted);
        this.validateStock(item);
        this.recomputeSummary();
        this.priceSubject.next();
      }
    });
  }

  // Detalle de artículo: notas, adjuntos y stock por almacén
  onOpenDetail(item: DocumentItem): void {
    const ref = this.dialog.open(ItemDetailModalComponent, {
      data: {
        item,
        salesOfficeId: this.salesOfficeSelected?._id?.toString(),
        showStock: (this.documentClassSelected as any)?.showCurrentStockForMaterials === true,
      },
      ...this.dgConfig.dialogSize('md'),
    });
    ref.afterClosed().subscribe((result: ItemDetailResult) => {
      if (!result) return;
      item.note = result.note;
      item.evidences = result.evidences ?? [];
      item.newAttachments = result.newAttachments ?? [];
    });
  }

  // Imprimir Proforma: requiere al menos un artículo; arma los datos del formulario y genera el PDF
  onPrintProforma(): void {
    if (!this.items.length) {
      this.handlerMessage.warning({
        message: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.PROFORMA_REQUIRES_ITEM'),
      });
      return;
    }
    this.documentGenerateService.generateProforma(this.buildProformaPayload()).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      },
      error: (err) => {
        this.handlerMessage.error({
          message: err?.error?.message || this.i18n.t('DOCUMENT_GENERATE.MESSAGES.PROFORMA_ERROR'),
          statusCode: err?.status,
        });
      },
    });
  }

  // arma el cuerpo de la proforma; el encabezado de empresa lo consulta el backend con companyId
  private buildProformaPayload(): any {
    const now = new Date();
    const dash = (v: string) => (v && v.trim() !== '' ? v : '-');
    return {
      companyId: this.company?._id?.toString() ?? '',
      branchName: this.salesOfficeSelected?.name ?? '',
      salesOfficeAddress: (this.salesOfficeSelected as any)?.address?.address ?? this.salesOfficeSelected?.name ?? '',
      classDocument: dash(this.documentClassSelected?.name ?? ''),
      invoiceDate: dash(this.fmtDate(this.documentDateControl.value)),
      loadDate: dash(this.fmtDate(this.chargeDateControl.value)),
      deliveryDate: dash(this.fmtDate(this.deliveryDateControl.value)),
      currentDate: this.fmtDate(now),
      currentTime: this.fmtTime(now),
      clientName: dash(this.shipToSelected?.name ?? ''),
      addressName: dash(this.addressSelected?.address ?? ''),
      paymentCondition: dash(this.paymentConditionSelected?.name ?? ''),
      warehouseName: dash(this.warehouseSelected?.name ?? ''),
      createdBy: '-',
      subRouteCode: dash(this.deliveryRouteSelected?.name ?? ''),
      products: this.items.map((it) => {
        const u = this.proformaUnits(it);
        return {
          code: it.code ?? '',
          description: it.name ?? '',
          umv: u.umv,
          umb: u.umb,
          ums: u.ums,
          ump: u.ump,
          price: this.fmtNum(it.price),
          discount: this.fmtNum(it.discount),
          total: this.fmtNum((it.quantity || 0) * (it.price || 0) - (it.discount || 0)),
        };
      }),
      totals: { discounts: this.fmtNum(this.summary.discount), total: this.fmtNum(this.summary.total) },
      subtotal: this.fmtNum(this.summary.subtotal),
      discounts: this.fmtNum(this.summary.discount),
      totalTaxes: this.fmtNum(this.summary.taxes),
      totalNumber: this.summary.total,
      summary: { invoiceTotal: this.fmtNum(this.summary.total) },
    };
  }

  // columnas de unidad de medida de la proforma, con el código de la unidad:
  // UMV (venta), UMB (base), UMS (secundaria) y UMP (paralela).
  // La paralela va vacía si no la habilitan la compañía y el artículo; el PDF decide
  // con eso si imprime la columna y marca con guion las líneas que no la tienen
  private proformaUnits(it: DocumentItem): { umv: string; umb: string; ums: string; ump: string } {
    const qty = it.quantity || 0;
    const umv = `${this.fmtNum(qty)} ${this.saleUnitCode(it)}`.trim();
    const aums: any[] = it.material?.alternalUnitOfMeasures ?? [];
    const qtyBase = qty * this.unitFactor(it);
    const conv = (a: any): string => {
      const f = Number(a?.conversionFactor ?? a?.equivalentQuantity ?? 1) || 1;
      const code = a?.unitOfMeasure?.code ?? a?.unitOfMeasure?.name ?? '';
      return `${this.fmtNum(qtyBase / f)} ${code}`.trim();
    };
    const umbUnit = aums.find((a) => a?.isUMB === true);
    const umbCode =
      umbUnit?.unitOfMeasure?.code ??
      it.material?.baseUnitOfMeasure?.code ??
      it.material?.baseUnitOfMeasure?.name ??
      '';
    const ums = aums.find((a) => a?.isSecondaryAlternativeUom === true);
    const ump = aums.find((a) => a?.isUMP === true);
    const umpVisible =
      (this.company as any)?.UMPActive !== false && it.material?.UMPActive === true;
    return {
      umv,
      umb: `${this.fmtNum(qtyBase)} ${umbCode}`.trim(),
      ums: ums ? conv(ums) : '',
      ump: ump && umpVisible ? conv(ump) : '',
    };
  }

  // código corto de la unidad de venta actual (como el pedido: "ST", "CS")
  private saleUnitCode(it: DocumentItem): string {
    const base: any = it.material?.baseUnitOfMeasure;
    if (base && base._id?.toString() === it.unitMeasureId) return base.code ?? base.name ?? it.unitLabel ?? '';
    const aums: any[] = it.material?.alternalUnitOfMeasures ?? [];
    const found = aums.find((a) => a?.unitOfMeasure?._id?.toString() === it.unitMeasureId);
    return found?.unitOfMeasure?.code ?? found?.unitOfMeasure?.name ?? it.unitLabel ?? '';
  }

  private fmtNum(n: number | null | undefined): string {
    return (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private fmtDate(d: Date | null | undefined): string {
    if (!d) return '';
    const date = new Date(d);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${date.getFullYear()}`;
  }

  private fmtTime(d: Date): string {
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mi}`;
  }

  // un subdocumento vacío falla en el backend (sus campos internos son requeridos): se manda null
  private subdocOrNull<T>(subdoc: T): T | null {
    return subdoc && (subdoc as any)._id ? subdoc : null;
  }

  // precio unitario sin impuesto (si la compañía lo incluye, se extrae la base)
  private netUnitPrice(it: DocumentItem): number {
    const price = it.price || 0;
    const tax = it.taxPct || 0;
    if (!this.taxIncluded || !tax) return price;
    return price / (1 + tax / 100);
  }

  // arma la línea del documento a partir del artículo del formulario;
  // las posiciones referenciadas son las del documento al que pertenece la línea
  private buildOrderDetail(
    it: DocumentItem,
    index: number,
    lines: DocumentItem[] = this.items,
    orderIndex = 0
  ): any {
    const round = (n: number) => this.dgConfig.roundCalc(n);
    const { umb, ump, ums, sold } = this.itemUnits(it);
    const taxPercentage = it.taxPct || 0;
    // regalía inclusiva (misma línea): las unidades gratis se entregan pero no se cobran.
    // La línea entrega lo comprado más el regalo, y los importes siguen solo la compra:
    // las cantidades *OriginalQuantity guardan lo cobrado y las *Quantity lo que sale
    const includedBonusQty = it.bonusIncludedQty || 0;
    const soldQty = it.quantity || 0;
    const deliveredQty = soldQty + includedBonusQty;
    const quantities = this.itemQuantities(it, soldQty);
    // el total se convierte de una vez: sumar la conversión de la compra y la del regalo
    // redondea dos veces y, en unidades sin decimales, no daría lo que sale del almacén
    const deliveredQuantities = includedBonusQty
      ? this.itemQuantities(it, deliveredQty)
      : quantities;
    // cantidades del regalo inclusivo por unidad de medida, con la misma conversión de la línea
    const bonusQuantities = includedBonusQty ? this.itemQuantities(it, includedBonusQty) : null;

    const shortUnit = (u: any) =>
      u
        ? { _id: u.unitOfMeasure?._id, code: u.unitOfMeasure?.code, name: u.unitOfMeasure?.name }
        : null;
    const baseUnit = shortUnit(umb) ?? {
      _id: it.material?.baseUnitOfMeasure?._id,
      code: it.material?.baseUnitOfMeasure?.code,
      name: it.material?.baseUnitOfMeasure?.name,
    };
    const soldUnit = shortUnit(sold) ?? baseUnit;

    const unitPrice = round(this.netUnitPrice(it));
    // descuento a espacio neto para cuadrar con unitPrice (que ya es neto)
    const toNet = (v: number) => (this.taxIncluded && taxPercentage ? v / (1 + taxPercentage / 100) : v);
    const netDiscountUnit = toNet(it.discountUnit || 0);
    const totalDiscounts = round(netDiscountUnit * soldQty);
    const totalSurcharges = 0;
    const subTotal = round(unitPrice * soldQty);
    const subTotalBeforeTaxes = round(subTotal - totalDiscounts + totalSurcharges);
    const totalTaxes = round(it.isv || 0);
    // posiciones de las líneas de regalo (SEPARATE_FREE_LINE) que dependen de esta línea
    const childBonusPositions = it.isBonus
      ? []
      : lines
          .map((x, i) => (x.isBonus && x.bonusParentId === it.lineId ? i + 1 : 0))
          .filter((p) => p > 0);
    const packLoanRequests = this.packLoanRequestsFor(orderIndex, index + 1);

    return {
      position: index + 1,
      // el código va crudo: los ceros solo se quitan para mostrar
      material: {
        _id: it.material?._id,
        externalKeys: it.material?.externalKeys ?? null,
        code: it.material?.code ?? it.code,
        materialGroups: it.material?.materialGroups ?? [],
        name: it.material?.name ?? it.name,
        UMPActive: it.material?.UMPActive ?? false,
        batchControlActive: it.material?.batchControlActive ?? false,
      },
      warehouse: this.subdocOrNull(this.lineWarehouse(it)),
      baseOriginalQuantity: quantities.base,
      baseConversionFactor: Number(umb?.conversionFactor ?? 1) || 1,
      baseQuantity: deliveredQuantities.base,
      baseUnitOfMeasure: baseUnit,
      // ambos factores van "desde 1 UMB": la paralela se invierte
      pararellOriginalQuantity: quantities.pararell,
      pararellConversionFactor: ump?.conversionFactor ? 1 / ump.conversionFactor : null,
      pararellQuantity: deliveredQuantities.pararell,
      pararellUnitOfMeasure: shortUnit(ump),
      // unidad secundaria: el factor original va en unidades base y el de conversión invertido
      alternalOriginalQuantity: quantities.alternal,
      alternalConversionFactor: ums?.conversionFactor ? 1 / ums.conversionFactor : null,
      originalAlternalConversionFactor: ums?.conversionFactor ?? null,
      alternalQuantity: deliveredQuantities.alternal,
      alternalUnitOfMeasure: shortUnit(ums),
      soldOriginalQuantity: soldQty,
      soldQuantity: deliveredQty,
      soldQuantitySuggested: 0,
      soldUnitOfMeasure: soldUnit,
      stockAtCreation: Math.floor(this.availableForItem(it)),
      originalUnitPrice: unitPrice,
      unitPrice,
      priceConditionClasses: it.priceConditionClasses ?? [],
      hasMinimumAndMaximunPrices: false,
      minimumAndMaximunPrices: { minimumPrice: 0, maximumPrice: 0 },
      hasScalePrices: false,
      scalePrices: {
        quantityFrom: 0,
        quantityTo: 0,
        minimumAndMaximunPrices: { minimumPrice: 0, maximumPrice: 0 },
        price: 0,
      },
      // el endpoint ya resuelve el monto; el descuento normal va como AMOUNT unitario
      discounts:
        (it.discountUnit || 0) > 0
          ? [
              {
                type: OrderDetailDiscountsEnum.AMOUNT,
                discountOriginal: round(netDiscountUnit),
                discount: round(netDiscountUnit),
                hasMinimumAndMaximunDiscounts: false,
                hasScaleDiscounts: false,
                fromBonus: false,
              },
            ]
          : [],
      surcharges: [],
      taxes: [
        {
          // el impuesto del material no tiene código: se usa el porcentaje
          code: String(taxPercentage),
          name: it.taxName || String(taxPercentage),
          taxBase: subTotalBeforeTaxes,
          percentage: Number(taxPercentage),
          amount: totalTaxes,
        },
      ],
      batches: [],
      subTotal,
      subTotalBeforeTaxes,
      totalTaxes,
      totalDiscounts,
      totalSurcharges,
      total: round(subTotalBeforeTaxes + totalTaxes),
      note: it.note ?? null,
      // evidencias ya subidas; las nuevas se suben en el guardado y el backend las inyecta por position
      evidences: it.evidences ?? [],
      // origen: la línea proviene de una cotización usada como base (encadena por position)
      sourceTransaction: it.sourceRef
        ? {
            serial: it.sourceRef.serial,
            type: it.sourceRef.type,
            class: it.sourceRef.class,
            position: it.sourceRef.position,
          }
        : null,
      // línea de producto gratis: se enlaza con la posición de la línea que la originó
      bonustParentPosition: it.isBonus
        ? lines.findIndex((x) => x.lineId === it.bonusParentId) + 1
        : null,
      isPromotion: it.isBonus,
      // línea comprada con regalos como líneas separadas: posiciones de esas líneas
      bonusChildPositions: childBonusPositions,
      // regalía inclusiva (INCLUDED_AS_DISCOUNT): unidades gratis entregadas en la misma línea
      bonusSoldQuantity: includedBonusQty || null,
      bonusBaseQuantity: bonusQuantities?.base ?? null,
      bonusPararellQuantity: bonusQuantities?.pararell ?? null,
      bonusAlternalQuantity: bonusQuantities?.alternal ?? null,
      // el comportamiento lo resuelve el endpoint; solo se persiste en la línea que lleva el regalo incluido
      bonusBehaviour: includedBonusQty ? it.bonusInfo?.behaviour : undefined,
      // solicitudes de préstamo de material de empaque confirmadas para esta línea
      areTherePackagingRequests: packLoanRequests.length > 0,
      packLoanRequests,
    };
  }

  // transacciones origen a nivel cabecera: cotizaciones distintas usadas como base
  private buildSourceTransactions(lines: DocumentItem[]): any[] {
    const seen = new Set<string>();
    const result: any[] = [];
    for (const it of lines) {
      const ref = it.sourceRef;
      if (!ref?.serial || seen.has(ref.serial)) continue;
      seen.add(ref.serial);
      result.push({ serial: ref.serial, type: ref.type, class: ref.class });
    }
    return result;
  }

  // el almacén de la línea; si no se resolvió uno, el del formulario
  private lineWarehouse(it: DocumentItem): any {
    const found = this.warehousesList.find((w) => w._id?.toString() === it.warehouseId);
    return found ?? this.warehouseSelected;
  }

  // almacenes usados por las líneas del documento, sin repetidos
  private documentWarehouses(lines: DocumentItem[]): any[] {
    const byId = new Map<string, any>();
    for (const it of lines) {
      const warehouse = this.lineWarehouse(it);
      const id = warehouse?._id?.toString();
      if (!id || byId.has(id)) continue;
      byId.set(id, {
        _id: warehouse._id,
        externalKeys: warehouse.externalKeys,
        id: warehouse.id,
        name: warehouse.name,
        plants: warehouse.plants,
      });
    }
    return [...byId.values()];
  }

  // reparte las líneas en los documentos que se van a generar, según las banderas de la clase.
  // El orden de la lista se conserva dentro de cada documento
  private splitIntoDocuments(): DocumentItem[][] {
    const bonusesOf = (line: DocumentItem) =>
      this.items.filter((it) => it.isBonus && it.bonusParentId === line.lineId);
    // separando por almacén la línea es una sola en pantalla: aquí se abre en una
    // línea por asignación, que es lo que termina en cada documento
    const purchased = this.items
      .filter((it) => !it.isBonus)
      .flatMap((it) => this.expandAllocations(it));

    // el grupo de una línea: almacén cuando se separa por almacén, y aparte el remanente
    // sin respaldo cuando la clase separa por disponibilidad
    const separatesWithoutStock =
      (this.documentClassSelected as any)?.canSeparateOrdersWithoutStock === true;
    const groupKey = (it: DocumentItem): string => {
      const parts: string[] = [];
      if (this.splitsByWarehouse) parts.push(it.warehouseId ?? '');
      if (separatesWithoutStock || this.splitsByWarehouse) {
        parts.push(this.isWithoutStock(it) ? 'sin-stock' : 'con-stock');
      }
      return parts.join('|');
    };

    const groups = new Map<string, DocumentItem[]>();
    for (const line of purchased) {
      const key = groupKey(line);
      const group = groups.get(key) ?? [];
      if (!groups.has(key)) groups.set(key, group);
      // la bonificación viaja en el mismo documento que su línea de origen
      group.push(line, ...bonusesOf(line));
    }

    const documents = [...groups.values()].filter((lines) => lines.length > 0);
    return this.routeSplit.applyWindowSplit(
      documents.length ? documents : [this.items],
      this.deliveryRouteSelected?.effectiveWindow?.days
    );
  }

  // los días de entrega admitidos, en el idioma activo
  private windowDaysLabel(days: number[] | undefined): string {
    if (!days?.length) return '';
    return days
      .slice()
      .sort((a, b) => a - b)
      .map((day) => (WEEKDAY_KEYS[day] ? this.i18n.t(WEEKDAY_KEYS[day]) : ''))
      .filter(Boolean)
      .join(', ');
  }

  // por qué el documento quedó separado del resto
  private splitReasonsOf(lines: DocumentItem[], index: number): DocumentSplitReason[] {
    const reasons: DocumentSplitReason[] = [];
    if (this.splitsByWarehouse) reasons.push('WAREHOUSE');
    if (lines.some((it) => !it.isBonus && this.isWithoutStock(it))) reasons.push('WITHOUT_STOCK');
    if (this.routeSplit.splitByWindow(index)) reasons.push('DELIVERY_WINDOW');
    return reasons;
  }

  // abre una línea con reparto guardado en una línea por asignación; el bono acompaña
  // a la primera, que es la que conserva el lineId original
  private expandAllocations(item: DocumentItem): DocumentItem[] {
    const allocations = item.allocations ?? [];
    if (allocations.length <= 1) return [item];
    return allocations.map((allocation, index) => {
      if (index === 0) {
        return {
          ...item,
          quantity: allocation.quantity,
          quantityText: this.quantityText(allocation.quantity),
          withoutStock: allocation.withoutStock === true,
        };
      }
      return {
        ...item,
        lineId: buildLineId(
          item._id,
          allocation.warehouseId,
          item.unitMeasureId,
          allocation.withoutStock === true
        ),
        warehouseId: allocation.warehouseId,
        warehouseCode: allocation.warehouseCode,
        warehouseName: allocation.warehouseName,
        quantity: allocation.quantity,
        quantityText: this.quantityText(allocation.quantity),
        withoutStock: allocation.withoutStock === true,
        // el bono se resolvió sobre la cantidad completa: queda en la primera asignación
        bonusInfo: undefined,
        bonusIncludedQty: 0,
        // las notas y evidencias pertenecen a la línea que las capturó
        note: undefined,
        evidences: [],
        newAttachments: [],
      };
    });
  }

  // la línea no tiene respaldo de existencia: o se marcó al distribuir, o el artículo
  // no tiene disponible en los almacenes que la clase considera
  private isWithoutStock(it: DocumentItem): boolean {
    if (it.withoutStock === true) return true;
    if (!(this.documentClassSelected as any)?.makesStockValidation) return false;
    if (!it.material) return false;
    return this.materialBaseStock(it.material) <= 0;
  }

  // arma un documento a partir de un subconjunto de líneas (misma forma que pedido y cotización)
  private buildOrderPayload(lines: DocumentItem[] = this.items, orderIndex = 0): any {
    const round = (n: number) => this.dgConfig.roundCalc(n);
    const office: any = this.salesOfficeSelected;
    const chain = office?.distributionchain;

    const detail = lines.map((it, index) => this.buildOrderDetail(it, index, lines, orderIndex));
    const containerLoanRequests = this.containerLoanRequestsFor(orderIndex);
    const totals = detail.reduce(
      (acc, d) => ({
        subTotal: round(acc.subTotal + d.subTotal),
        subTotalBeforeTaxes: round(acc.subTotalBeforeTaxes + d.subTotalBeforeTaxes),
        totalTaxes: round(acc.totalTaxes + d.totalTaxes),
        totalDiscounts: round(acc.totalDiscounts + d.totalDiscounts),
        totalSurcharges: round(acc.totalSurcharges + d.totalSurcharges),
      }),
      { subTotal: 0, subTotalBeforeTaxes: 0, totalTaxes: 0, totalDiscounts: 0, totalSurcharges: 0 }
    );

    // cada documento lleva la ruta que se le resolvió; sin ruta, los campos van nulos
    const documentRoute = this.routeFor(orderIndex);
    const deliverySubRoute = documentRoute
      ? {
          _id: documentRoute._id,
          externalKeys: documentRoute.externalKeys,
          id: documentRoute.id,
          name: documentRoute.name,
          model: documentRoute.model,
          type: documentRoute.type,
        }
      : null;

    // el viaje que entregará el documento, con sus fechas teóricas
    const selectedTrip = documentRoute?.trip;
    const deliveryTrip = selectedTrip
      ? {
          _id: selectedTrip._id,
          id: selectedTrip.id,
          externalKeys: selectedTrip.externalKeys,
          dateHourStartTeoric: selectedTrip.dateHourStartTeoric
            ? new Date(selectedTrip.dateHourStartTeoric)
            : null,
          dateHourEndTeoric: selectedTrip.dateHourEndTeoric
            ? new Date(selectedTrip.dateHourEndTeoric)
            : null,
        }
      : null;

    // el responsable del documento es el vendedor si se seleccionó; si no, el usuario logueado
    let seller = null;
    const currentUserLoginId = this.currentUser?.id ?? (this.currentUser as any)?._id;
    if (this.salesForceSelected?.masterUserId && this.salesForceSelected?._id) {
      seller = {
        _id: this.salesForceSelected.masterUserId,
        user_login: {
          _id: this.salesForceSelected._id,
          username: this.salesForceSelected.username,
          email: this.salesForceSelected.email,
        },
        business_partner_id: this.salesForceSelected.businessPartnerId,
        type: this.salesForceSelected.type,
      };
    } else if (this.currentUser?.masterUserId && currentUserLoginId) {
      seller = {
        _id: this.currentUser.masterUserId,
        user_login: {
          _id: currentUserLoginId,
          username: this.currentUser.username,
          email: this.currentUser.email,
        },
        type: this.currentUser.type,
      };
    }

    return {
      // el backend asigna código y serial
      code: null,
      mobileCode: null,
      serial: null,
      reference: this.referenceControl.value,
      // tienen campos requeridos: incompletos rechazarían todo el documento
      application: this.subdocOrNull(this.applicationSelected),
      subApplication: this.subdocOrNull(this.subApplicationSelected),
      type: this.documentClassSelected?.documentType,
      class: {
        _id: this.documentClassSelected?._id,
        externalKeys: this.documentClassSelected?.externalKeys,
        code: this.documentClassSelected?.code,
        name: this.documentClassSelected?.name,
        statusId: this.documentClassSelected?.statusId,
        canUpdatePrices: this.documentClassSelected?.canUpdatePrices ?? false,
      },
      expeditionCondition: this.subdocOrNull(
        this.expeditionConditionSelected && {
          _id: this.expeditionConditionSelected._id,
          code: this.expeditionConditionSelected.code,
          externalKeys: this.expeditionConditionSelected.externalKeys,
          name: this.expeditionConditionSelected.name,
          isItForShipping: this.expeditionConditionSelected.isItForShipping,
          statusId: this.expeditionConditionSelected.statusId,
        }
      ),
      sourceTransactions: this.buildSourceTransactions(lines),
      company: {
        _id: this.company?._id,
        externalKeys: this.company?.externalKeys,
        id: this.company?.id,
        name: this.company?.name,
      },
      distributionChain: {
        _id: chain?._id,
        externalKeys: chain?.externalKeys,
        name: chain?.name,
        // el id de la compañía sale de la oficina, no de la cadena
        company: office?.company?.id,
        salesOrganization: chain?.salesOrganization,
        channel: chain?.channel,
      },
      salesOffice: {
        _id: office?._id,
        externalKeys: office?.externalKeys,
        id: office?.id,
        name: office?.name,
      },
      routes: documentRoute?.routes ?? [],
      subRoute: null,
      deliverySubRoute,
      warehouses: this.documentWarehouses(lines),
      user: seller?.user_login?._id ? seller : null,
      shipTo: {
        _id: this.shipToSelected?._id,
        externalKeys: this.shipToSelected?.externalKeys,
        id: this.shipToSelected?.id,
        name: this.shipToSelected?.name,
        ownPriceList: this.subdocOrNull(this.shipToSelected?.priceList),
        ownDiscountList: this.subdocOrNull(this.shipToSelected?.discountList),
      },
      soldTo: {
        _id: this.soldToSelected?.parentMongoId,
        externalKeys: this.soldToSelected?.parentExternalKeys,
        id: this.soldToSelected?.parentId,
        name: this.soldToSelected?.parentName,
        ownPriceList: this.subdocOrNull(this.soldToSelected?.parentPriceList),
        ownDiscountList: this.subdocOrNull(this.soldToSelected?.parentDiscountList),
      },
      address: this.subdocOrNull(this.addressSelected),
      interlocutors: [],
      currency: {
        _id: this.selectedCurrency?._id,
        name: this.selectedCurrency?.name,
        factor: this.factorCurrencySelected,
      },
      creationDateTime: this.documentDateTime(),
      // sale del viaje del documento; sin viaje queda nula
      loadDateTime: selectedTrip?.loadDateTime ? new Date(selectedTrip.loadDateTime) : null,
      deliveryDateTimeSuggested: null,
      // sin viaje queda la fecha estimada que calculó la ventana horaria
      deliveryDateTime: selectedTrip?.dateHourStartTeoric
        ? new Date(selectedTrip.dateHourStartTeoric)
        : this.routeSplit.estimatedDeliveryDate(orderIndex),
      realDeliveryDateTime: null,
      note: this.comentariesControl.value,
      subTotal: totals.subTotal,
      subTotalBeforeTaxes: totals.subTotalBeforeTaxes,
      totalTaxes: totals.totalTaxes,
      totalDiscounts: totals.totalDiscounts,
      totalSurcharges: totals.totalSurcharges,
      total: round(totals.subTotalBeforeTaxes + totals.totalTaxes),
      isOrderTaxExempt: lines.some((it) => it.material?.isTaxExempt === true),
      detail,
      trip: null,
      deliveryTrip,
      paymentCondition: this.subdocOrNull(
        this.paymentConditionSelected && {
          _id: this.paymentConditionSelected._id,
          externalKeys: this.paymentConditionSelected.externalKeys,
          daysToPay: this.paymentConditionSelected.daysToPay,
          id: this.paymentConditionSelected.id,
          name: this.paymentConditionSelected.name,
          statusId: this.paymentConditionSelected.statusId,
          type: this.paymentConditionSelected.type,
        }
      ),
      // solicitudes de préstamo de contenedores confirmadas para este documento
      areThereContainerRequests: containerLoanRequests.length > 0,
      containerLoanRequests,
    };
  }

  // fecha elegida por el usuario con la hora del envío, para conservar el orden del día
  private documentDateTime(): Date {
    const selected = this.documentDateControl.value;
    if (!selected) return new Date();
    const now = new Date();
    const date = new Date(selected);
    date.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);
    return date;
  }

  // combina la fecha de entrega con la hora elegida (formato hh:mm AM/PM)
  private getDateWithHour(date: Date | null, hour: string): Date | null {
    if (!date) return null;
    const result = new Date(date);
    const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i.exec((hour ?? '').trim());
    if (!match) return result;
    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const meridiem = match[3]?.toUpperCase();
    if (meridiem === 'PM' && hours < 12) hours += 12;
    if (meridiem === 'AM' && hours === 12) hours = 0;
    result.setHours(hours, minutes, 0, 0);
    return result;
  }

  // sin artículos o con la cabecera incompleta no se genera
  private canGenerate(): boolean {
    if (this.submitting) return false;
    if (!this.items.length) {
      this.handlerMessage.warning({ message: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.ADD_AT_LEAST_ONE_ITEM') });
      return false;
    }
    if (this.formData.invalid) {
      this.formData.markAllAsTouched();
      this.handlerMessage.warning({ message: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.COMPLETE_REQUIRED_FIELDS') });
      return false;
    }
    const invalid = this.collectInvalidItems();
    if (invalid.length) {
      this.openInvalidItemsModal(invalid);
      return false;
    }
    return true;
  }

  // razones por las que un artículo no puede generarse
  private itemInvalidReasons(it: DocumentItem): string[] {
    if (it.isBonus) return []; // el producto gratis es válido a precio 0
    const reasons: string[] = [];
    if (!it.material?._id) reasons.push('Datos del material incompletos');
    if (it.isCommercialUom === false) reasons.push('Unidad de venta no disponible');
    if ((it.quantity ?? 0) <= 0) reasons.push('Cantidad en cero');
    if ((it.price ?? 0) <= 0) reasons.push('Sin precio');
    return reasons;
  }

  private collectInvalidItems(): { item: DocumentItem; reasons: string[] }[] {
    return this.items
      .map((item) => ({ item, reasons: this.itemInvalidReasons(item) }))
      .filter((x) => x.reasons.length > 0);
  }

  private openInvalidItemsModal(invalid: { item: DocumentItem; reasons: string[] }[]): void {
    const ref = this.dialog.open(InvalidItemsModalComponent, {
      data: {
        items: invalid.map((x) => ({
          code: x.item.code,
          name: x.item.name,
          reasons: x.reasons,
        })),
      },
      ...this.dgConfig.dialogSize('md'),
    });
    ref.afterClosed().subscribe((action) => {
      if (action === 'remove') {
        const bad = new Set(invalid.map((x) => x.item));
        this.items = this.items.filter((it) => !bad.has(it));
        this.recomputeSummary();
      }
    });
  }

  // errors[].identifier trae la etiqueta del documento y failures[] el motivo; el message de raíz es genérico
  private extractSubmitError(err: any, documentCount = 1): string | null {
    const messages: string[] = (err?.error?.errors ?? []).flatMap((entry: any) => {
      const reasons = [
        ...new Set<string>((entry?.failures ?? []).map((f: any) => f?.reason).filter(Boolean)),
      ];
      const label =
        documentCount > 1 && entry?.identifier && entry.identifier !== 'null'
          ? entry.identifier
          : null;
      return reasons.map((reason) => (label ? `${label}: ${reason}` : reason));
    });
    if (messages.length) return [...new Set<string>(messages)].join(' ');
    return err?.error?.message ?? null;
  }

  // el backend acepta el data URI tal cual sale del FileReader
  private readAttachment(file: File): Promise<DocumentAttachment> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ content: reader.result as string, fileName: file.name });
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private buildAttachments(): Promise<DocumentAttachment[]> {
    return Promise.all(this.attachmentFiles.map((file) => this.readAttachment(file)));
  }

  // adjuntos nuevos por línea: la posición se reinicia en cada documento, así que
  // se indica también a qué documento pertenece la línea
  private async buildLineAttachments(
    documents: DocumentItem[][]
  ): Promise<{ orderIndex: number; position: number; files: DocumentAttachment[] }[]> {
    const groups: { orderIndex: number; position: number; files: DocumentAttachment[] }[] = [];
    for (let orderIndex = 0; orderIndex < documents.length; orderIndex++) {
      const lines = documents[orderIndex];
      for (let i = 0; i < lines.length; i++) {
        const news = lines[i].newAttachments ?? [];
        if (!news.length) continue;
        const files = await Promise.all(news.map((file) => this.readAttachment(file)));
        groups.push({ orderIndex, position: i + 1, files });
      }
    }
    return groups;
  }

  // el reparto por almacén y por disponibilidad no debería producir más que unos pocos
  // documentos; un número mayor delata una distribución anómala
  private readonly MAX_DOCUMENTS = 20;

  // el aviso de generación dura más que un snackbar normal: el usuario suele anotar el serial
  private readonly CREATED_SNACKBAR_DURATION = 15000;

  // solicitudes de vacíos confirmadas en el modal, por índice de documento
  private confirmedPackLoanRequests: ConfirmedPackLoanRequests | null = null;

  // el reparto por ruta y ventana vive en el servicio de dominio; aquí solo queda
  // el estado de la petición y lo que se muestra
  private routeResolutionFailed = false;

  private get routeResolution(): DeliveryRouteResolution | null {
    return this.routeSplit.currentResolution;
  }

  private routeFor(orderIndex: number): DeliveryRouteCandidate | null {
    return this.routeSplit.routeFor(orderIndex);
  }

  private clearResolvedRoutes(): void {
    this.routeSplit.reset();
  }

  // false aborta la generación: el usuario canceló el modal de vacíos
  private async resolvePackLoanRequests(documents: DocumentItem[][]): Promise<boolean> {
    this.confirmedPackLoanRequests = null;

    const companyId = this.company?._id?.toString();
    const documentClassId = this.documentClassSelected?._id?.toString();
    if (!companyId || !documentClassId) return true;

    const payload = this.buildPackagingRequirementsRequest(documents);
    if (!payload.length) return true;

    const office: any = this.salesOfficeSelected;
    const response = await firstValueFrom(
      this.documentGenerateService
        .resolvePackagingRequirements(
          {
            companyId,
            documentClassId,
            businessPartnerId: this.soldToSelected?.parentMongoId?.toString(),
            distributionChainId: office?.distributionchain?._id?.toString(),
            documents: payload,
          },
          false
        )
        .pipe(catchError(() => of(null)))
    );

    const data = response?.data;
    if (!data?.packagingManagementEnabled) return true;

    const withRequests = (data.documents ?? []).filter(
      (document) => document.lines?.length || document.containers?.length
    );
    if (!withRequests.length) return true;

    const confirmed = await firstValueFrom(
      this.dialog
        .open(PackLoanRequestsModalComponent, {
          data: {
            documents: withRequests,
            reasons: data.reasons ?? [],
            limitExceededBhv: data.limitExceededBhv,
            documentLabels: this.packLoanDocumentLabels(documents),
          },
          ...this.dgConfig.dialogSize('lg'),
        })
        .afterClosed()
    );

    if (!confirmed) return false;
    this.confirmedPackLoanRequests = confirmed;
    return true;
  }

  // solo las líneas compradas aportan empaque; el bono viaja como línea propia y también cuenta
  private buildPackagingRequirementsRequest(
    documents: DocumentItem[][]
  ): PackagingRequirementDocumentRequest[] {
    const result: PackagingRequirementDocumentRequest[] = [];
    for (let index = 0; index < documents.length; index++) {
      const lines = documents[index]
        .map((it, position) => ({
          position: position + 1,
          materialId: it.material?._id?.toString() ?? it._id,
          baseQuantity: this.itemQuantities(it).base,
        }))
        .filter((line) => line.materialId && line.baseQuantity > 0);
      if (lines.length) result.push({ index, lines });
    }
    return result;
  }

  // etiqueta legible de cada documento; también identifica al documento que falla al generar
  private documentLabels(documents: DocumentItem[][]): string[] {
    return documents.map((lines, index) => {
      const names = this.documentWarehouses(lines).map((w) => w.name).filter(Boolean);
      return names.length ? `Documento ${index + 1} · ${names.join(', ')}` : `Documento ${index + 1}`;
    });
  }

  private packLoanDocumentLabels(documents: DocumentItem[][]): Record<number, string> {
    const labels: Record<number, string> = {};
    this.documentLabels(documents).forEach((label, index) => (labels[index] = label));
    return labels;
  }

  private packLoanRequestsFor(orderIndex: number, position: number): PackLoanRequestPayload[] {
    const document = this.confirmedPackLoanRequests?.byDocument.get(orderIndex);
    return document?.linesByPosition.get(position) ?? [];
  }

  private containerLoanRequestsFor(orderIndex: number): PackLoanRequestPayload[] {
    const document = this.confirmedPackLoanRequests?.byDocument.get(orderIndex);
    return document?.containers ?? [];
  }

  // false aborta la generación: el usuario canceló o no se pudo resolver
  private async confirmDeliveryRoutes(documents: DocumentItem[][]): Promise<boolean> {
    if (!this.shipToControl.value || !this.salesOfficeSelected?.company?._id) return true;

    // el backend valida siempre con la lista de artículos: de su respuesta salen las
    // rutas elegibles y las ventanas de cada artículo
    let resolution = await this.resolveRoutesFor(documents, true);
    if (!resolution) return !this.routeResolutionFailed;

    // la ventana horaria de algún artículo impide entregar el documento completo:
    // se separa por ventana y se vuelve a preguntar al backend por los nuevos documentos
    let finalDocuments = documents;
    let splitByWindow = false;
    if (this.routeSplit.blockedByDeliveryWindow(resolution)) {
      this.routeSplit.windowSplitEnabled = true;
      finalDocuments = this.splitIntoDocuments();
      splitByWindow = finalDocuments.length > documents.length;
      if (splitByWindow) {
        const resplit = await this.resolveRoutesFor(finalDocuments, true);
        // sin resolución nueva los índices que hay guardados son los del reparto
        // anterior: el servicio tiene que dejar de leerlos como si fueran los finales
        if (resplit) resolution = resplit;
        else this.routeSplit.markResolutionStale();
      } else {
        this.routeSplit.windowSplitEnabled = false;
        finalDocuments = this.splitIntoDocuments();
      }
    }

    const choice = await firstValueFrom(
      this.dialog
        .open(DeliveryRouteSplitModalComponent, {
          data: {
            documents: this.buildRouteSplitDocuments(finalDocuments),
            canGenerateWithoutRoute: !this.deliveryRouteRequired,
            splitByWindow,
            currency: 'L.',
            taxIncluded: this.taxIncluded,
            umpActive: (this.company as any)?.UMPActive !== false,
          },
          ...this.dgConfig.dialogSize('lg'),
        })
        .afterClosed()
    );

    if (!choice) return false;

    this.routeSplit.applyChoices(choice.routes ?? {}, this.splitIntoDocuments().length);
    this.syncHeaderRouteFromSelection();
    return true;
  }

  // un bloque por documento ya repartido, con sus candidatas elegibles y su ventana
  private buildRouteSplitDocuments(documents: DocumentItem[][]): DeliveryRouteSplitDocument[] {
    const labels = this.documentLabels(documents);

    return documents.map((lines, index) => {
      return {
        index,
        label: labels[index] ?? this.i18n.t('DOCUMENT_GENERATE.SPLIT_MODAL.DOCUMENT_N', {
          index: index + 1,
        }),
        warehouseLabel: this.documentWarehouses(lines)
          .map((w) => this.optionLabel(w))
          .join(', '),
        items: lines,
        summary: this.summaryOf(lines),
        daysLabel: this.windowDaysLabel(this.routeSplit.windowDaysOf(index)),
        splitReasons: this.splitReasonsOf(lines, index),
        candidates: this.routeSplit.candidatesFor(index),
        selectedRouteId: this.routeSplit.routeFor(index)?._id ?? null,
        estimatedDelivery: this.routeSplit.estimatedDeliveryDate(index),
      };
    });
  }

  // la cabecera refleja la ruta del primer documento, como cuando no hay partición
  private syncHeaderRouteFromSelection(): void {
    const first = this.routeSplit.chosenRoutes.get(0) ?? null;
    const origin = this.routeResolution?.documents?.[0];
    if (origin) this.deliveryRouteList = origin.routes.filter((route) => route.eligible);
    this.deliveryRouteControl.setValue(first?._id ?? '');
    this.onDeliveryRouteChange();
  }

  // resuelve una ruta por documento; reutiliza lo resuelto en la vista previa si nada cambió
  private async resolveRoutesFor(
    documents: DocumentItem[][],
    force = false
  ): Promise<DeliveryRouteResolution | null> {
    this.routeResolutionFailed = false;
    if (!this.shipToControl.value || !this.salesOfficeSelected?.company?._id) return null;

    const signature = this.routeSignature(documents);
    // al guardar se revalida siempre: el stock y las ventanas pueden haber cambiado
    const cached = force ? null : this.routeSplit.cachedResolutionFor(signature);
    if (cached) return cached;

    const request: DeliveryRouteDocumentRequest[] = documents.map((lines, index) => ({
      index,
      materialIds: [...new Set(lines.map((it) => it._id).filter(Boolean))],
    }));
    if (!request.some((document) => document.materialIds.length)) return null;

    const resolution = await firstValueFrom(this.requestDeliveryRoutes(undefined, request, false));
    if (!resolution) {
      this.routeResolutionFailed = true;
      return null;
    }

    this.routeSplit.storeResolution(resolution, signature);
    this.applyWindowDaysToItems();
    return resolution;
  }

  // el estado que condiciona la ruta; si cambia, la resolución guardada deja de servir
  private routeSignature(documents: DocumentItem[][]): string {
    const parts = documents.map((lines) =>
      [...new Set(lines.map((it) => it._id).filter(Boolean))].sort().join(',')
    );
    return [this.shipToControl.value, this.plainDocumentDate(), ...parts].join('|');
  }

  // los días admitidos quedan en la línea para poder mostrarlos sin recalcular
  private applyWindowDaysToItems(): void {
    const byMaterial = new Map(
      this.routeSplit.materialWindows.map((w) => [String(w._id), w])
    );
    for (const item of this.items) {
      item.deliveryWindowDays = byMaterial.get(String(item._id))?.days;
    }
  }

  // un bloque por documento para el modal de candidatas
  private buildRouteGroups(
    documents: DocumentItem[][],
    resolution: DeliveryRouteResolution
  ): DeliveryRouteCandidatesGroup[] {
    const labels = this.documentLabels(documents);
    const previousRouteId = this.deliveryRouteControl.value;

    return (resolution.documents ?? []).map((document) => {
      const eligible = document.routes.filter((route) => route.eligible);
      const selectedRouteId = this.routeSplit.resolvedRouteAt(document.index)?._id ?? null;
      const keepsRoute =
        !!previousRouteId && selectedRouteId === previousRouteId && !document.tiedRouteIds.length;

      return {
        index: document.index,
        label: labels[document.index] ?? `Documento ${document.index + 1}`,
        warehouseLabel: this.documentWarehouses(documents[document.index] ?? [])
          .map((w) => this.optionLabel(w))
          .join(', '),
        reason: this.revalidationReason(
          eligible,
          previousRouteId,
          document.tiedRouteIds,
          keepsRoute
        ),
        candidates: eligible,
        selectedRouteId,
        rejections: this.buildRejections(document),
      };
    });
  }

  // el detalle de por qué se cayó cada ruta, para que el usuario sepa qué artículo lo impide
  private buildRejections(document: DeliveryRouteDocumentResolution) {
    return document.routes
      .filter((route) => !route.eligible && route.rejectedReason)
      .map((route) => ({
        routeName: route.name,
        reason: route.rejectedReason
          ? this.i18n.resolve(DELIVERY_ROUTE_REJECTED_LABEL_KEYS[route.rejectedReason])
          : route.rejectedReason,
        materials: route.blockingMaterials ?? [],
        rejectedTrips: route.rejectedTrips ?? [],
        effectiveWindow: route.effectiveWindow ?? null,
        skippedHolidays: route.skippedHolidays ?? [],
      }));
  }

  private revalidationReason(
    eligible: DeliveryRouteCandidate[],
    previousRouteId: string,
    tiedRouteIds: string[],
    keepsRoute: boolean
  ): string {
    if (!eligible.length) {
      return this.i18n.t('DOCUMENT_GENERATE.ROUTE_REVALIDATION.NO_ELIGIBLE');
    }
    if (keepsRoute) {
      return this.i18n.t('DOCUMENT_GENERATE.ROUTE_REVALIDATION.KEEPS_ROUTE');
    }
    if (previousRouteId && !eligible.some((route) => route._id === previousRouteId)) {
      return this.i18n.t('DOCUMENT_GENERATE.ROUTE_REVALIDATION.PREVIOUS_UNAVAILABLE');
    }
    if (tiedRouteIds.length > 1) {
      return this.i18n.t('DOCUMENT_GENERATE.ROUTE_REVALIDATION.TIED');
    }
    return this.i18n.t('DOCUMENT_GENERATE.ROUTE_REVALIDATION.CLOSER_FOUND');
  }

  private createdDocumentsMessage(serials: string[]): string {
    if (!serials.length) return this.i18n.t('DOCUMENT_GENERATE.CREATED_DOCUMENTS.NONE');
    const list = serials.map((s) => `<strong>${s}</strong>`);
    if (serials.length === 1) {
      return this.i18n.t('DOCUMENT_GENERATE.CREATED_DOCUMENTS.ONE', { serial: list[0] });
    }
    return this.i18n.t('DOCUMENT_GENERATE.CREATED_DOCUMENTS.MANY', {
      count: serials.length,
      list: list.join(', '),
    });
  }

  private notifyDocumentsCreated(serials: string[]): void {
    this.snackBar.openFromComponent(DocumentCreatedSnackbarComponent, {
      data: { message: this.createdDocumentsMessage(serials), serials },
      duration: this.CREATED_SNACKBAR_DURATION,
      panelClass: 'snackbar-success',
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  // envía el documento; onSuccess solo corre si el backend confirmó la creación
  private async submitDocument(onSuccess: () => void): Promise<void> {
    if (!this.canGenerate()) return;
    this.submitting = true;

    // el reparto va primero: la ruta se resuelve con los artículos de cada documento
    const documents = this.splitIntoDocuments();
    if (this.exceedsDocumentLimit(documents)) {
      this.submitting = false;
      return;
    }

    // el Control de Materiales de la ruta solo se puede evaluar con los artículos ya cargados
    const routeConfirmed = await this.confirmDeliveryRoutes(documents);
    if (!routeConfirmed) {
      this.submitting = false;
      return;
    }

    // las solicitudes se calculan sobre los documentos ya repartidos: los contenedores son de cabecera
    const packLoanConfirmed = await this.resolvePackLoanRequests(documents);
    if (!packLoanConfirmed) {
      this.submitting = false;
      return;
    }

    let attachments: DocumentAttachment[] = [];
    let lineAttachments: { orderIndex: number; position: number; files: DocumentAttachment[] }[] = [];
    try {
      attachments = await this.buildAttachments();
      lineAttachments = await this.buildLineAttachments(documents);
    } catch {
      this.submitting = false;
      this.handlerMessage.error({ message: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.ATTACHMENTS_READ_ERROR') });
      return;
    }

    this.documentGenerateService
      .generateDocument({
        confirmed: true,
        orders: documents.map((lines, orderIndex) => this.buildOrderPayload(lines, orderIndex)),
        attachments,
        lineAttachments,
        documentLabels: this.documentLabels(documents),
      })
      .subscribe({
        next: (res) => {
          this.submitting = false;
          // si hay inválidos el backend no creó nada: se conserva el formulario
          const invalid: any[] = res?.invalid ?? [];
          if (invalid.length) {
            const detalle = invalid.map((i) => i?.message).filter(Boolean).join(' ');
            this.handlerMessage.error({
              message: detalle || this.i18n.t('DOCUMENT_GENERATE.MESSAGES.GENERATE_FAILED'),
            });
            return;
          }
          const serials: string[] = (res?.creation_response ?? [])
            .map((doc: any) => doc?.serial)
            .filter(Boolean);
          this.notifyDocumentsCreated(serials);
          onSuccess();
        },
        error: (err) => {
          this.submitting = false;
          this.handlerMessage.error({
            message: this.extractSubmitError(err, documents.length),
            alternativeMessage: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.GENERATE_ERROR'),
            statusCode: err?.status,
          });
        },
      });
  }

  // un reparto anómalo se rechaza antes de resolver rutas y de leer adjuntos
  private exceedsDocumentLimit(documents: DocumentItem[][]): boolean {
    if (documents.length <= this.MAX_DOCUMENTS) return false;
    this.handlerMessage.error({
      message: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.DOCUMENT_LIMIT_EXCEEDED', {
        count: documents.length,
        max: this.MAX_DOCUMENTS,
      }),
    });
    return true;
  }

  // Generar y revisar: el documento se envía al confirmar desde el preview
  async onGenerateReview(): Promise<void> {
    if (!this.canGenerate()) return;

    const documents = this.splitIntoDocuments();
    if (this.exceedsDocumentLimit(documents)) return;

    // la vista previa muestra la ruta de cada documento, así que resuelve antes de abrirse
    this.submitting = true;
    await this.resolveRoutesFor(documents);
    this.submitting = false;

    const ref = this.dialog.open(DocumentPreviewModalComponent, {
      data: this.buildPreviewData(),
      ...this.dgConfig.dialogSize('lg'),
    });
    ref.afterClosed().subscribe((result) => {
      if (result === 'sameClient') {
        this.submitDocument(() => this.doResetKeepClient());
      } else if (result) {
        this.submitDocument(() => this.doReset());
      }
    });
  }

  // Generar y nuevo: genera y deja el formulario en blanco
  onGenerateNew(): void {
    this.submitDocument(() => this.doReset());
  }

  // Generar y nuevo con el mismo cliente: genera y conserva la cabecera
  onGenerateNewSameClient(): void {
    this.submitDocument(() => this.doResetKeepClient());
  }

  // los documentos tal como se van a generar, para que el usuario vea la partición antes de confirmar
  private buildPreviewDocuments(
    routes?: Map<number, DeliveryRouteCandidate | null>
  ): DocumentPreviewDocument[] {
    const documents = this.splitIntoDocuments();
    const groups = this.routeResolution
      ? this.buildRouteGroups(documents, this.routeResolution)
      : [];

    return documents.map((lines, index) => {
      const altUnits: Record<string, LineAltUnits> = {};
      for (const line of lines) altUnits[line.lineId] = this.altUnitQuantities(line);
      // tras partir por ventana los índices cambian: la ruta se resuelve por origen
      const route = routes ? routes.get(index) ?? null : this.routeSplit.routeFor(index);
      const windowDays = this.routeSplit.windowDaysOf(index);

      return {
        items: lines,
        summary: this.summaryOf(lines),
        warehouseLabel: this.documentWarehouses(lines)
          .map((w) => this.optionLabel(w))
          .join(', '),
        withoutStock: lines.some((it) => !it.isBonus && this.isWithoutStock(it)),
        altUnits,
        routeGroup: groups.find((group) => group.index === index),
        splitReasons: this.splitReasonsOf(lines, index),
        windowDaysLabel: this.windowDaysLabel(windowDays),
        ...this.routeHeader(route),
      };
    });
  }

  // "Ver distribución" desde el modal de rutas: refleja lo que el usuario lleva elegido ahí,
  // sin comprometerlo todavía, y sin ofrecer cambiar la ruta para no anidar el mismo modal
  private showSplitFromCandidates(choices: Record<number, string | null>): void {
    const routes = new Map<number, DeliveryRouteCandidate | null>();
    for (const document of this.routeResolution?.documents ?? []) {
      const chosen = document.routes.find(
        (route) => route.eligible && route._id === choices[document.index]
      );
      routes.set(document.index, chosen ?? null);
    }

    this.dialog.open(DocumentSplitModalComponent, {
      data: {
        documents: this.buildPreviewDocuments(routes),
        currency: 'L.',
        taxIncluded: this.taxIncluded,
      },
      ...this.dgConfig.dialogSize('lg'),
    });
  }

  // ruta, viaje y fecha que muestra el encabezado de cada documento
  private routeHeader(route: DeliveryRouteCandidate | null): Partial<DocumentPreviewDocument> {
    return {
      routeName: route?.name ?? '',
      tripLabel: this.tripLabel(route?.trip),
      deliveryDate: route?.trip?.dateHourStartTeoric
        ? new Date(route.trip.dateHourStartTeoric)
        : null,
    };
  }

  // cambio de ruta desde el modal de partición: el modal de candidatas acotado a ese documento
  private changeDocumentRoute(document: DocumentPreviewDocument): void {
    const group = document.routeGroup;
    if (!group || group.candidates.length < 2) return;

    this.dialog
      .open(DeliveryRouteCandidatesModalComponent, {
        data: { documents: [group], canGenerateWithoutRoute: !this.deliveryRouteRequired },
        ...this.dgConfig.dialogSize('md'),
      })
      .afterClosed()
      .subscribe((choice) => {
        if (!choice) return;
        const routeId = choice.routes?.[group.index] ?? null;
        const chosen = group.candidates.find((route) => route._id === routeId);
        if (!chosen) return;
        this.routeSplit.setResolvedRoute(group.index, chosen);
        group.selectedRouteId = chosen._id;
        Object.assign(document, this.routeHeader(chosen));
      });
  }

  private buildPreviewData(): DocumentPreviewData {
    return {
      documentTypeLabel: this.documentTypeLabel(this.documentTypeControl.value),
      documentClass: this.documentClassSelected?.name ?? '',
      documentClassCode: this.documentClassSelected?.code ?? '',
      salesOffice: this.salesOfficeSelected?.name ?? '',
      shipTo: this.shipToSelected?.name ?? '',
      soldTo: this.soldToSelected?.parentName ?? '',
      address: this.addressSelected?.address ?? '',
      warehouse: this.warehouseSelected?.name ?? '',
      expeditionCondition: this.expeditionConditionSelected?.name ?? '',
      paymentCondition: this.paymentConditionSelected?.name ?? '',
      reference: this.referenceControl.value ?? '',
      documentDate: this.documentDateControl.value,
      deliveryRoute: this.deliveryRouteSelected?.name ?? '',
      deliveryDate: this.deliveryDateControl.value,
      deliveryHour: this.deliveryHourControl.value ?? '',
      currency: 'L.',
      comments: this.comentariesControl.value ?? '',
      items: this.items,
      summary: this.summary,
      documents: this.buildPreviewDocuments(),
      taxIncluded: this.taxIncluded,
      umpActive: (this.company as any)?.UMPActive !== false,
      attachments: this.attachmentFiles,
      salesOfficeId: this.salesOfficeSelected?._id?.toString(),
      showStock: (this.documentClassSelected as any)?.showCurrentStockForMaterials === true,
      splitsByWarehouse: this.splitsByWarehouse,
      separatesWithoutStock:
        (this.documentClassSelected as any)?.canSeparateOrdersWithoutStock === true,
      onChangeRoute: (document) => this.changeDocumentRoute(document),
    };
  }

  private hasContent(): boolean {
    return (
      this.items.length > 0 ||
      !!this.salesOfficeSelected ||
      !!this.shipToSelected ||
      this.attachmentFiles.length > 0
    );
  }

  // botón de refrescar: confirma si hay contenido
  resetForm(): void {
    if (this.hasContent()) {
      this.confirmationService.confirmReset().subscribe((ok) => {
        if (ok) this.doReset();
      });
    } else {
      this.doReset();
    }
  }

  // reinicia el formulario a su estado inicial
  private doReset(): void {
    this.formData.reset();
    this.documentTypeControl.setValue(DocumentTypeEnum.PEDIDO);
    this.lastDocumentType = DocumentTypeEnum.PEDIDO;
    this.documentDateControl.setValue(new Date());
    this.chargeDateControl.setValue(null);

    this.documentClassSelected = null;
    this.classSupportsBonus = false;
    this.bonusWarnedIds.clear();
    this.bonusNoStockWarned.clear();
    this.bonusOutOfStockWarned.clear();
    this.salesOfficeSelected = null;
    this.salesOfficesList = [];
    this.shipToSelected = null;
    this.shipToList = [];
    this.soldToSelected = null;
    this.addressesList = [];
    this.warehousesList = [];
    this.warehousesFilterList = [];
    this.deliveryRouteList = [];
    this.deliveryRouteSelected = null;
    this.deliveryRouteLoading = false;
    this.clearResolvedRoutes();
    this.salesForcesList = [];
    this.salesForcesFilterList = [];
    this.selectedCurrency = null;
    this.company = null;
    this.factorCurrencySelected = null;
    this.expeditionConditionDisabled = false;
    this.paymentConditionDisabled = false;
    this.overdueRestrictionActive = false;
    this.paymentConditionsFilterList = [...this.paymentConditionsList];

    this.items = [];
    this.dismissedBonuses.clear();
    this.lastValidQuantity.clear();
    this.orderHistoryList = [];
    this.suggestedProductsList = [];
    this.documentsCount = 0;
    this.productsCount = 0;
    this.documentsPage = 1;
    this.productsPage = 1;
    this.docDays = 30;
    this.docOfficeId = '';
    this.docOfficeSearch = '';
    this.docSearch = '';
    this.prodDays = 30;
    this.prodOfficeId = '';
    this.prodOfficeSearch = '';
    this.prodSearch = '';
    this.attachmentsControl.setValue([]);
    this.recomputeSummary();

    // oficina siempre deshabilitada (se completa desde el solicitante); solicitante hasta que haya clase
    this.salesOfficeControl.disable({ emitEvent: false });
    this.shipToControl.disable({ emitEvent: false });

    this.fetchDocumentClass();
  }

  // reinicia el carrito conservando la cabecera (cliente, oficina, clase, dirección, etc.)
  private doResetKeepClient(): void {
    this.items = [];
    this.dismissedBonuses.clear();
    this.lastValidQuantity.clear();
    this.bonusWarnedIds.clear();
    this.bonusNoStockWarned.clear();
    this.bonusOutOfStockWarned.clear();
    this.attachmentsControl.setValue([]);
    this.comentariesControl.setValue('');
    this.referenceControl.setValue('');
    this.documentDateControl.setValue(new Date());
    this.chargeDateControl.setValue(null);
    this.deliveryDateControl.setValue(null);
    this.deliveryHourControl.setValue('');
    this.recomputeSummary();
    // el cliente se conserva pero la fecha del documento vuelve a hoy: hay que
    // resolver la ruta otra vez para que fecha de carga y de entrega se llenen
    this.getRouteDelivery();
    this.reloadSuggested();
  }

  onRemoveItem(item: DocumentItem): void {
    const idx = this.items.indexOf(item);
    if (idx > -1) {
      this.items.splice(idx, 1);
      if (item.isBonus) {
        // se recuerda para no volver a agregarlo en el próximo reprecio
        this.dismissedBonuses.add(item.lineId);
      } else {
        // al quitar la línea comprada se quita también su producto gratis y se olvida su descarte
        this.items = this.items.filter((it) => !it.isBonus || it.bonusParentId !== item.lineId);
        this.clearDismissedBonusFor(item.lineId);
        this.forgetBonusWarnings(item.lineId);
      }
      this.recomputeSummary();
    }
  }

  // pref (opcional): unidad de venta y cantidad a precargar (productos sugeridos)
  private addMaterials(materials: any[], pref?: { unitId?: string; quantity?: number }): void {
    const pending: any[] = []; // base inutilizable: la unidad se resuelve con las alternas
    const noSellableUnit: string[] = [];
    const noStock: string[] = [];
    const addedIds: string[] = []; // recién agregados, para omitir tras el reprecio los que queden sin precio
    const distributed: DocumentItem[] = []; // líneas cuyo reparto se resuelve antes de repreciar
    for (const m of materials) {
      // sin ninguna unidad vendible: no se agrega
      if (!this.hasSellableUnit(m)) {
        noSellableUnit.push(this.articleLabel(m));
        continue;
      }
      // sin disponible en el almacén y la clase valida stock: no se agrega ni se acumula
      if (this.hasNoAvailableStock(m)) {
        noStock.push(this.articleLabel(m));
        continue;
      }
      const id = m._id?.toString();
      // unidad elegida: preferencia global (sugerido) o por material (modal de búsqueda)
      const unitPref =
        pref ??
        (m.__unitId || m.__quantity
          ? { unitId: m.__unitId, quantity: Number(m.__quantity) || undefined }
          : undefined);
      const chosenSellable =
        unitPref?.unitId &&
        (m.alternalUnitOfMeasures ?? []).some(
          (a: any) => a?.unitOfMeasure?._id?.toString() === unitPref.unitId && a?.isCommercialUom !== false
        );
      // se acumula solo en la línea del mismo material, almacén y unidad; con otra
      // unidad o almacén el artículo entra como línea aparte
      const targetUnitId = chosenSellable
        ? unitPref.unitId
        : m.baseUnitOfMeasure?._id?.toString() ?? '';
      const targetLineId = buildLineId(id, this.warehouseSelected?._id?.toString(), targetUnitId);
      const existing = this.items.find((it) => it.lineId === targetLineId);
      if (existing) {
        const added = unitPref?.quantity && unitPref.quantity > 0 ? unitPref.quantity : 1;
        existing.quantity += added;
        this.applyQuantityDecimals(existing);
        existing.quantityText = this.quantityText(existing.quantity);
        this.validateStock(existing);
        // la cantidad cambió: el reparto entre almacenes deja de ser válido
        distributed.push(existing);
        addedIds.push(existing.lineId);
        continue;
      }
      // la unidad base no sirve para vender: no es vendible, o es vendible pero sin precio.
      // En ambos casos la unidad la resuelven las alternas, salvo que el usuario ya eligiera una
      const baseUsable = this.isUmbSellable(m) && m.hasPrice !== false;
      if (baseUsable || chosenSellable) {
        const newItem = this.mapMaterialToItem(m);
        this.applyPrefToNewItem(newItem, m, unitPref);
        this.refreshLineId(newItem);
        this.applyQuantityDecimals(newItem);
        this.items.push(newItem);
        this.validateStock(newItem);
        distributed.push(newItem);
        addedIds.push(newItem.lineId);
      } else {
        pending.push(m);
      }
    }
    this.recomputeSummary();
    // el reparto va antes del reprecio: la bonificación se resuelve sobre la
    // cantidad ya repartida, y el debounce del sujeto perdía las altas múltiples
    this.distributeAdded(distributed, () =>
      this.priceItems(() => this.dropUnpricedItems(addedIds))
    );
    // primero los avisos agrupados y al cerrarlos la cadena de selección de unidad
    this.showItemWarnings(
      [this.noSellableUnitWarning(noSellableUnit), this.noStockWarning(noStock)],
      () => this.resolveUnusableBase(pending)
    );
  }

  // la clase valida stock, el material no permite vender sin stock y no hay disponible.
  // Se mira todo el conjunto de almacenes de la oficina cuando la clase permite varios,
  // y solo el almacén del formulario cuando no
  private hasNoAvailableStock(m: any): boolean {
    if (!(this.documentClassSelected as any)?.makesStockValidation) return false;
    if (m?.canMakeAnOrderWithoutStock === true) return false;
    return this.materialBaseStock(m) <= 0;
  }

  // disponible del material en los almacenes que la clase de documento considera
  private materialBaseStock(m: any): number {
    const rows: any[] = m?.materialStock ?? [];
    const warehouseId = this.warehouseSelected?._id?.toString();
    const considered = this.allowsMultipleWarehouses
      ? rows
      : rows.filter((x) => !warehouseId || x.warehouseId?.toString() === warehouseId);
    return considered.reduce((sum: number, x: any) => sum + (x.availableStock || 0), 0);
  }

  // solo los dos comportamientos que el formulario materializa: los demás hoy no producen nada
  private isSupportedBonus(bonus?: DocumentItemBonus): boolean {
    if (!bonus || (Number(bonus.quantity) || 0) <= 0) return false;
    return (
      bonus.behaviour === BonusBehaviourEnum.SEPARATE_FREE_LINE ||
      bonus.behaviour === BonusBehaviourEnum.INCLUDED_AS_DISCOUNT
    );
  }

  // confirmación al agregar artículos cuya bonificación no aplica por la clase de documento
  private confirmUnsupportedBonus(names: string[]): Observable<boolean> {
    const list = names.filter(Boolean);
    const docClass = this.optionLabel(this.documentClassSelected);
    const message =
      list.length === 1
        ? this.i18n.t('DOCUMENT_GENERATE.BONUS_UNSUPPORTED.CONFIRM_ONE', {
            item: list[0],
            documentClass: docClass,
          })
        : this.i18n.t('DOCUMENT_GENERATE.BONUS_UNSUPPORTED.CONFIRM_MANY', {
            documentClass: docClass,
            items: list.map((n) => `<strong>${n}</strong>`).join('<br>'),
          });
    const ref = this.dialog.open(FormConfigChangeModalComponent, {
      data: {
        title: this.i18n.t('DOCUMENT_GENERATE.BONUS_UNSUPPORTED.TITLE'),
        message,
        icon: 'redeem',
        confirmText: this.i18n.t('DOCUMENT_GENERATE.ACTIONS.CONTINUE'),
        cancelText: this.i18n.t('GENERAL.CANCEL'),
      },
      ...this.dgConfig.dialogSize('sm'),
    });
    return ref.afterClosed().pipe(map((ok) => !!ok));
  }

  // una sola pregunta por línea; al cancelar el artículo no se lleva
  private confirmDroppedBonuses(droppedBonusIds: string[]): void {
    const affected = this.items.filter(
      (it) => droppedBonusIds.includes(it.lineId) && !this.bonusWarnedIds.has(it.lineId)
    );
    if (!affected.length) return;
    // se marcan antes de abrir para que un reprecio en curso no dispare un segundo modal
    affected.forEach((it) => this.bonusWarnedIds.add(it.lineId));
    this.confirmUnsupportedBonus(affected.map((it) => this.articleLabel(it))).subscribe((ok) => {
      if (ok) return;
      affected.forEach((it) => this.bonusWarnedIds.delete(it.lineId));
      this.items = this.items.filter((it) => !affected.includes(it));
      this.recomputeSummary();
    });
  }

  // modal de advertencia: artículos omitidos por no tener precio en la lista del cliente
  private warnUnpricedItems(names: string[]): void {
    this.showItemWarnings([this.unpricedWarning(names)]);
  }

  clientPriceListLabel(): string {
    const priceList = this.shipToSelected?.priceList ?? this.soldToSelected?.parentPriceList;
    return priceList?._id ? this.optionLabel(priceList) : '';
  }

  private unpricedWarning(names: string[]): ItemWarning | null {
    const list = names.filter(Boolean);
    if (!list.length) return null;
    const priceList = this.clientPriceListLabel();
    const listPart = priceList ? ` <strong>${priceList}</strong>` : '';
    return {
      title: this.i18n.t('DOCUMENT_GENERATE.NO_PRICE_ITEMS.TITLE'),
      message:
        list.length === 1
          ? this.i18n.t('DOCUMENT_GENERATE.NO_PRICE_ITEMS.MESSAGE_ONE', {
              item: list[0],
              list: listPart,
            })
          : this.i18n.t('DOCUMENT_GENERATE.NO_PRICE_ITEMS.MESSAGE_MANY', {
              items: list.join(', '),
              list: listPart,
            }),
    };
  }

  private noSellableUnitWarning(names: string[]): ItemWarning | null {
    const list = names.filter(Boolean);
    if (!list.length) return null;
    return {
      title: this.i18n.t('DOCUMENT_GENERATE.NO_SELLABLE_UNITS.TITLE'),
      message:
        list.length === 1
          ? this.i18n.t('DOCUMENT_GENERATE.NO_SELLABLE_UNITS.MESSAGE_ONE', { item: list[0] })
          : this.i18n.t('DOCUMENT_GENERATE.NO_SELLABLE_UNITS.MESSAGE_MANY', {
              items: list.join(', '),
            }),
    };
  }

  private noStockWarning(names: string[]): ItemWarning | null {
    const list = names.filter(Boolean);
    if (!list.length) return null;
    const warehouse = this.optionLabel(this.warehouseSelected);
    const place = warehouse
      ? this.i18n.t('DOCUMENT_GENERATE.NO_STOCK_ITEMS.IN_WAREHOUSE', { warehouse })
      : '';
    return {
      title: this.i18n.t('DOCUMENT_GENERATE.NO_STOCK_ITEMS.TITLE'),
      message:
        list.length === 1
          ? this.i18n.t('DOCUMENT_GENERATE.NO_STOCK_ITEMS.MESSAGE_ONE', { item: list[0], place })
          : this.i18n.t('DOCUMENT_GENERATE.NO_STOCK_ITEMS.MESSAGE_MANY', {
              items: list.join(', '),
              place,
            }),
    };
  }

  // los avisos se muestran uno tras otro (nunca apilados) y al final corre done
  private showItemWarnings(warnings: (ItemWarning | null)[], done?: () => void): void {
    const [next, ...rest] = warnings.filter((w): w is ItemWarning => !!w);
    if (!next) {
      done?.();
      return;
    }
    this.dialog
      .open(FormConfigChangeModalComponent, {
        data: {
          title: next.title,
          message: next.message,
          icon: next.icon ?? 'info',
          iconClass: 'text-blue-500',
          confirmText: 'Aceptar',
        },
        ...this.dgConfig.dialogSize('sm'),
      })
      .afterClosed()
      .subscribe(() => this.showItemWarnings(rest, done));
  }

  // aplica la unidad de venta más usada y la cantidad promedio (producto sugerido)
  private applyPrefToNewItem(item: DocumentItem, m: any, pref?: { unitId?: string; quantity?: number }): void {
    if (!pref) return;
    if (pref.unitId && pref.unitId !== item.unitMeasureId) {
      const aum = (m.alternalUnitOfMeasures ?? []).find(
        (a: any) => a?.unitOfMeasure?._id?.toString() === pref.unitId && a?.isCommercialUom !== false
      );
      if (aum) {
        item.unitMeasureId = aum.unitOfMeasure._id?.toString();
        item.unitLabel = aum.unitOfMeasure.name ?? aum.unitOfMeasure.code ?? item.unitLabel;
      }
    }
    if (pref.quantity && pref.quantity > 0) {
      const { sold } = this.itemUnits(item);
      const acceptsDecimals =
        sold?.unitOfMeasure?.hasacceptedDecimals ?? item.material?.baseUnitOfMeasure?.hasacceptedDecimals;
      const qty = acceptsDecimals ? Math.floor(pref.quantity * 100) / 100 : Math.floor(pref.quantity);
      if (qty > 0) {
        item.quantity = qty;
        item.quantityText = String(qty);
      }
    }
  }

  // la UMB vive en alternalUnitOfMeasures con isUMB: true, y ahí está la bandera de venta
  // del material. baseUnitOfMeasure.isCommercialUom es la del catálogo de unidades, no la
  // del material, y no se usa. Sin esa entrada no se bloquea: se asume vendible
  private isUmbSellable(m: any): boolean {
    const umb = (m?.alternalUnitOfMeasures ?? []).find((a: any) => a?.isUMB === true);
    return !umb || umb.isCommercialUom !== false;
  }

  // el material tiene al menos una unidad de medida vendible; la UMB también está en
  // el arreglo, y la UMP solo cuenta si la compañía la habilita
  private hasSellableUnit(m: any): boolean {
    const aums: any[] = m?.alternalUnitOfMeasures ?? [];
    if (!aums.length) return true; // sin unidades configuradas no se decide aquí
    const umpActive = (this.company as any)?.UMPActive !== false;
    return aums.some(
      (a: any) => a?.isCommercialUom !== false && (umpActive || a?.isUMP !== true)
    );
  }

  // la unidad base no sirve para vender (no vendible o sin precio): se resuelve con las
  // alternas, tomando la secundaria si se puede y dejando elegir al usuario si no
  private resolveUnusableBase(queue: any[]): void {
    if (!queue.length) return;
    const [m, ...rest] = queue;

    const businessPartnerId = this.soldToSelected?.parentMongoId;
    const salesOfficeId = this.salesOfficeSelected?._id?.toString();
    const currencyId = this.selectedCurrency?._id?.toString();
    const aums: any[] = (m?.alternalUnitOfMeasures ?? []).filter((a: any) => a?.unitOfMeasure);

    // se consultan los precios de todas las unidades para decidir cuáles se pueden usar según el modo
    const payload = {
      businessPartnerId,
      salesOfficeId,
      currencyId,
      materials: aums.map((a) => ({
        materialId: m._id?.toString(),
        unitMeasureId: a.unitOfMeasure._id?.toString(),
        quantity: 1,
      })),
    };
    this.documentGenerateService.fetchPrice(payload).subscribe({
      next: (res) => this.decideUnusableBase(m, rest, aums, res?.materials ?? []),
      error: () => this.openUnitSelection(m, rest), // sin precios: al modal (que reconsulta)
    });
  }

  // decide qué hacer con un artículo de UMB inutilizable según las unidades usables por el modo
  private decideUnusableBase(m: any, rest: any[], aums: any[], priced: PricedMaterial[]): void {
    const umpActive = (this.company as any)?.UMPActive !== false;
    const priceOf = (umId: string) => {
      const p = priced.find((x) => x.unitMeasureId?.toString() === umId);
      return p ? Number(p.price) || 0 : 0;
    };
    const umbEntry = aums.find((a) => a.isUMB);
    const umbHasPrice = umbEntry ? priceOf(umbEntry.unitOfMeasure._id?.toString()) > 0 : false;
    const umbSellable = umbEntry ? umbEntry.isCommercialUom !== false : false;

    // misma regla de seleccionabilidad que el modal de cambio de unidad
    const usable = (a: any) => {
      if (a.isUMB) return false; // la UMB llegó aquí porque no sirve para vender
      if (a.isCommercialUom === false) return false;
      if (!umpActive && a.isUMP === true) return false;
      if (priceOf(a.unitOfMeasure._id?.toString()) > 0) return true; // precio propio
      if (this.recalcMode === 1) return umbHasPrice; // deriva desde la UMB
      // convierte a la UMB: hace falta que sea vendible y que tenga precio del que partir
      if (this.recalcMode === 2) return umbSellable && umbHasPrice;
      return false; // modo 0
    };

    const usableAlts = aums.filter(usable);
    if (!usableAlts.length) {
      this.dialog
        .open(FormConfigChangeModalComponent, {
          data: {
            title: this.i18n.t('DOCUMENT_GENERATE.UNAVAILABLE_ITEM.TITLE'),
            message: this.i18n.t('DOCUMENT_GENERATE.UNAVAILABLE_ITEM.MESSAGE', {
              product: this.articleLabel(m),
              cause: this.unusableBaseCause(m),
            }),
            icon: 'info',
            iconClass: 'text-blue-500',
            confirmText: this.i18n.t('GENERAL.ACCEPT'),
          },
          ...this.dgConfig.dialogSize('sm'),
        })
        .afterClosed()
        .subscribe(() => this.resolveUnusableBase(rest));
      return;
    }

    // si la UMS es usable → se selecciona sin preguntar
    const ums = usableAlts.find((a) => a.isSecondaryAlternativeUom === true);
    if (ums) {
      const umsId = ums.unitOfMeasure._id?.toString();
      const umsName = ums.unitOfMeasure.name ?? ums.unitOfMeasure.code ?? '';
      // modo 1: si la UMS no tiene precio, se deriva desde la UMB
      const derived =
        this.recalcMode === 1 && priceOf(umsId) <= 0 && umbHasPrice
          ? priceOf(umbEntry.unitOfMeasure._id?.toString()) *
            Number(ums.conversionFactor ?? ums.equivalentQuantity ?? 1)
          : undefined;
      if (!CONFIRM_SECONDARY_UNIT) {
        this.addItemWithUnit(m, umsId, umsName, derived);
        this.resolveUnusableBase(rest);
        return;
      }
      this.confirmSecondaryUnit(m, ums.unitOfMeasure).subscribe((confirmed) => {
        if (confirmed) this.addItemWithUnit(m, umsId, umsName, derived);
        this.resolveUnusableBase(rest);
      });
      return;
    }

    // sin UMS usable → el usuario elige una unidad (el modal aplica la misma regla)
    this.openUnitSelection(m, rest);
  }

  // por qué la unidad base no sirve para vender, para explicarlo en los avisos
  private unusableBaseCause(m: any): string {
    return this.i18n.t(
      !this.isUmbSellable(m)
        ? 'DOCUMENT_GENERATE.UNUSABLE_BASE.CAUSE_NOT_SELLABLE'
        : 'DOCUMENT_GENERATE.UNUSABLE_BASE.CAUSE_NO_PRICE'
    );
  }

  // confirmación previa al cambio a la unidad secundaria. En espera de la preferencia
  // de usuario que la habilite (hoy CONFIRM_SECONDARY_UNIT la deja apagada)
  private confirmSecondaryUnit(m: any, unit: any): Observable<boolean> {
    return this.confirmationService.confirm({
      title: this.i18n.t('DOCUMENT_GENERATE.UNUSABLE_BASE.CONFIRM_TITLE'),
      description: this.i18n.t('DOCUMENT_GENERATE.UNUSABLE_BASE.CONFIRM_MESSAGE', {
        product: this.articleLabel(m),
        cause: this.unusableBaseCause(m),
        unit: this.optionLabel(unit),
      }),
      type: ConfirmationModalType.WARNING,
      confirmButtonText: this.i18n.t('DOCUMENT_GENERATE.ACTIONS.CONTINUE'),
      cancelButtonText: this.i18n.t('GENERAL.CANCEL'),
      descriptionIcon: 'swap_horiz',
    });
  }

  // abre el modal de selección de unidad para un artículo cuya UMB no sirve para vender
  private openUnitSelection(m: any, rest: any[]): void {
    const ref = this.dialog.open(ChangeUnitModalComponent, {
      data: {
        item: {
          _id: m._id?.toString(),
          code: this.applyRemoveLeftZeros(m.code),
          name: m.name,
          material: m,
          unitMeasureId: m.baseUnitOfMeasure?._id?.toString() ?? '',
        },
        warehouseId: this.warehouseSelected?._id?.toString(),
        businessPartnerId: this.soldToSelected?.parentMongoId,
        salesOfficeId: this.salesOfficeSelected?._id?.toString(),
        currencyId: this.selectedCurrency?._id?.toString(),
        currency: 'L.',
        showStock: (this.documentClassSelected as any)?.showCurrentStockForMaterials === true,
        umpActive: (this.company as any)?.UMPActive !== false,
        recalcMode: this.recalcMode,
        notice: this.i18n.t('DOCUMENT_GENERATE.UNUSABLE_BASE.SELECT_UNIT_NOTICE', {
          product: this.articleLabel(m),
          cause: this.unusableBaseCause(m),
        }),
      },
      ...this.dgConfig.dialogSize('md'),
    });
    ref.afterClosed().subscribe((unit) => {
      if (unit?.unitMeasureId) this.addItemWithUnit(m, unit.unitMeasureId, unit.name, unit.price);
      this.resolveUnusableBase(rest);
    });
  }

  // agrega el material con la unidad de medida elegida por el usuario
  private addItemWithUnit(m: any, unitMeasureId: string, unitLabel: string, price?: number): void {
    const lineId = buildLineId(
      m._id?.toString(),
      this.warehouseSelected?._id?.toString(),
      unitMeasureId
    );
    if (this.items.some((it) => it.lineId === lineId)) {
      this.handlerMessage.info({
        message: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.ITEM_ALREADY_IN_LIST', {
          name: m?.name || this.i18n.t('DOCUMENT_GENERATE.MESSAGES.DEFAULT_PRODUCT_NAME'),
        }),
      });
      return;
    }
    const item = this.mapMaterialToItem(m);
    item.unitMeasureId = unitMeasureId;
    item.unitLabel = unitLabel;
    item.isCommercialUom = true;
    this.refreshLineId(item);
    item.quantity = 1;
    item.quantityText = '1';
    if (price != null) item.price = price;
    this.items.push(item);
    this.validateStock(item);
    this.recomputeSummary();
    this.priceItems(() => this.dropUnpricedItems([item.lineId]));
  }

  onItemQuantityChange(item?: DocumentItem): void {
    if (item) {
      this.applyQuantityDecimals(item);
      this.validateStock(item);
      if (this.allowsMultipleWarehouses) this.distributionSubject.next(item);
      // total provisional mientras llega el reprecio, que trae el discountTotal definitivo
      item.discount = (item.discountUnit || 0) * (item.quantity || 0);
      item.isv = this.computeIsv(item.price, item.quantity || 0, item.taxPct || 0, item.discountUnit || 0);
      // al cambiar la cantidad de la línea comprada, el bono se vuelve a evaluar (deja de estar descartado)
      if (!item.isBonus) this.clearDismissedBonusFor(item.lineId);
    }
    this.recomputeSummary();
    this.priceSubject.next();
  }

  // olvida los avisos de bonificación de una línea que deja de existir. El lineId es
  // determinista (material + almacén + unidad): sin esto, volver a agregar el mismo
  // artículo lo daba por avisado y el aviso ya no salía hasta reiniciar el formulario
  private forgetBonusWarnings(lineId: string): void {
    this.bonusWarnedIds.delete(lineId);
    this.bonusNoStockWarned.delete(lineId);
    this.bonusOutOfStockWarned.delete(lineId);
  }

  // olvida los bonos descartados de una línea (para que el reprecio los vuelva a agregar)
  private clearDismissedBonusFor(parentId: string): void {
    for (const key of [...this.dismissedBonuses]) {
      if (key.startsWith(`bonus:${parentId}:`)) this.dismissedBonuses.delete(key);
    }
  }

  // precio editado manualmente (canUpdatePrices): recalcula descuento e ISV de la línea sin reprecio
  onItemPriceChange(item: DocumentItem): void {
    item.discount = (item.discountUnit || 0) * (item.quantity || 0);
    item.isv = this.computeIsv(item.price, item.quantity || 0, item.taxPct || 0, item.discountUnit || 0);
    this.recomputeSummary();
  }

  // reparte una cantidad entre los almacenes disponibles según la estrategia de la clase.
  // Devuelve las asignaciones en la unidad de la línea; el remanente sin cubrir se marca aparte
  private planAllocations(
    item: DocumentItem,
    quantity: number,
    stocks: WarehouseStockRow[]
  ): WarehouseAllocation[] {
    const factor = this.unitFactor(item) || 1;
    const ordered = this.orderedWarehouses()
      .map((w) => {
        const id = w._id?.toString();
        const base = stocks.find((s) => s.warehouseId === id)?.availableStock ?? 0;
        return {
          warehouseId: id,
          warehouseCode: w.externalKeys?.code ?? (w as any)?.code ?? '',
          warehouseName: w.name ?? '',
          // acotado a los decimales asignables: el residuo sobrante no se puede repartir
          available: this.allocationQuantity(item, factor > 0 ? base / factor : base),
        };
      })
      .filter((w) => !!w.warehouseId);

    const strategy = (this.documentClassSelected as any)?.warehousePriorityStrategy;
    const allocations: WarehouseAllocation[] = [];

    // COVER_FULL_NEED: si un almacén cubre la totalidad por sí solo, se toma ese
    if (strategy === WarehousePriorityStrategyEnum.COVER_FULL_NEED) {
      const full = ordered.find((w) => w.available >= quantity);
      if (full) {
        return [
          {
            warehouseId: full.warehouseId,
            warehouseCode: full.warehouseCode,
            warehouseName: full.warehouseName,
            quantity,
          },
        ];
      }
    }

    // COVER_PARTIAL_NEED, y el respaldo de COVER_FULL_NEED cuando ninguno cubre:
    // se toma de cada almacén lo disponible y el remanente pasa al siguiente
    let pending = quantity;
    for (const w of ordered) {
      if (pending <= 0) break;
      const take = Math.min(pending, Math.max(0, w.available));
      if (take <= 0) continue;
      // se descuenta lo que realmente se asigna: restar el disponible sin truncar
      // perdía la parte decimal en cada almacén y el pedido salía por menos
      const assigned = this.allocationQuantity(item, take);
      if (assigned <= 0) continue;
      allocations.push({
        warehouseId: w.warehouseId,
        warehouseCode: w.warehouseCode,
        warehouseName: w.warehouseName,
        quantity: assigned,
      });
      pending = this.exact(pending - assigned);
    }

    // lo que ningún almacén cubre se vende si el material lo permite, o si la clase
    // no valida existencia: en ese caso la clase manda sobre el flag del material
    if (pending > 0 && this.allowsSaleWithoutStock(item)) {
      const main = ordered[0];
      const remainder = {
        warehouseId: main?.warehouseId ?? this.warehouseSelected?._id?.toString(),
        warehouseCode: main?.warehouseCode,
        warehouseName: main?.warehouseName,
        quantity: this.allocationQuantity(item, pending),
      };
      // sin nada que repartir no hay excedente que señalar: es una venta normal
      // desde el almacén principal. La marca es para la parte que un reparto no cubrió
      allocations.push(
        allocations.length ? { ...remainder, withoutStock: true } : remainder
      );
    }

    return this.balanceAllocations(item, allocations, quantity);
  }

  // el truncado de cada asignación va restando milésimas: la última absorbe el
  // residuo para que la suma sea exactamente la cantidad pedida. Solo se corrige el
  // residuo del redondeo; si falta cantidad por falta de existencia, se deja faltando
  private balanceAllocations(
    item: DocumentItem,
    allocations: WarehouseAllocation[],
    quantity: number
  ): WarehouseAllocation[] {
    if (!allocations.length) return allocations;
    const target = this.allocationQuantity(item, quantity);
    const assigned = this.exact(allocations.reduce((sum, a) => sum + (a.quantity || 0), 0));
    const diff = this.exact(target - assigned);
    const roundingSlack = allocations.length * 0.001;
    if (diff !== 0 && Math.abs(diff) <= roundingSlack) {
      const last = allocations[allocations.length - 1];
      last.quantity = this.exact(last.quantity + diff);
    }
    return allocations;
  }

  // corrige el arrastre binario de las operaciones intermedias
  private exact(value: number): number {
    return Math.round(value * 1e6) / 1e6;
  }

  // el valor exacto se conserva; el texto del input se muestra con los decimales
  // de stock, para no arrastrar la cola binaria de una división
  private quantityText(value: number): string {
    const text = (Number(value) || 0).toFixed(this.dgConfig.stockDisplayDecimals);
    return text.includes('.') ? text.replace(/\.?0+$/, '') : text;
  }

  // decimales efectivos de la línea: la unidad decide SI admite decimales y cuántos
  // como techo; la compañía decide cuántos se representan. Manda el menor de los dos
  uomDecimals(item: DocumentItem): { accepts: boolean; decimals: number } {
    const uom = this.lineUom(item);
    const accepts = !!(uom?.hasacceptedDecimals ?? item.hasAcceptedDecimals ?? false);
    if (!accepts) return { accepts: false, decimals: 0 };
    const uomDecimals = Number(uom?.decimalsAllowed ?? item.decimalsAllowed ?? 0) || 0;
    const companyDecimals = this.dgConfig.stockDisplayDecimals;
    return { accepts: true, decimals: Math.min(uomDecimals, companyDecimals) };
  }

  private lineUom(item: DocumentItem): any {
    const { sold } = this.itemUnits(item);
    return sold?.unitOfMeasure ?? item.material?.baseUnitOfMeasure;
  }

  // decimales del reparto entre almacenes: además de aceptar decimales, la unidad
  // debe permitirlos en el asistente de distribución; si no, se reparte en enteros
  private distributionDecimals(item: DocumentItem): { accepts: boolean; decimals: number } {
    const uom = this.lineUom(item);
    const allowsInWizard =
      uom?.allowDecimalDistributionInInventoryWizard ?? item.allowDecimalDistribution ?? false;
    if (allowsInWizard !== true) return { accepts: false, decimals: 0 };
    return this.uomDecimals(item);
  }

  // trunca a los decimales que admite la unidad; sin decimales permitidos va a entero
  private truncateToUom(value: number, accepts: boolean, decimals: number): number {
    if (!accepts || decimals <= 0) return Math.floor(value);
    const factor = Math.pow(10, decimals);
    return Math.floor(value * factor) / factor;
  }

  // el flag ya viene resuelto por compañía desde el endpoint de materiales
  private allowsSaleWithoutStock(item: DocumentItem): boolean {
    return item.material?.canMakeAnOrderWithoutStock === true;
  }

  // cantidad de una asignación: se acota a los decimales que admite la unidad de la línea,
  // los mismos que gobiernan su cantidad. No se usa el flag del asistente de inventario:
  // llega desincronizado en la unidad embebida del material y truncaba repartos válidos
  private allocationQuantity(item: DocumentItem, value: number): number {
    const { accepts, decimals } = this.distributionDecimals(item);
    return this.truncateToUom(this.exact(value), accepts, decimals);
  }

  private roundQuantity(item: DocumentItem, value: number): number {
    const { accepts, decimals } = this.uomDecimals(item);
    // se corrige el arrastre binario antes de truncar: sin esto, 200 dividido y
    // multiplicado por su factor vuelve como 199.9999 y terminaría en 199.999
    const corrected = Math.round(value * 1e6) / 1e6;
    return this.truncateToUom(corrected, accepts, decimals);
  }

  // disponibilidad por almacén de la oficina; se cachea en la línea y en sus hermanas
  private loadWarehouseStocks(item: DocumentItem): Observable<WarehouseStockRow[]> {
    if (item.warehouseStocks?.length) return of(item.warehouseStocks);
    const salesOfficeId = this.salesOfficeSelected?._id?.toString();
    const materialId = item.material?._id?.toString() ?? item._id;
    if (!salesOfficeId || !materialId) return of([]);
    return this.documentGenerateService
      .fetchStockBySalesOfficeBatch(salesOfficeId, [materialId])
      .pipe(
        map((res) => {
          // solo los almacenes que el formulario lista: el servicio puede devolver
          // alguno de otra compañía, y entonces los totales no cuadrarían entre pantallas
          const allowed = this.officeWarehouseIds();
          const rows: WarehouseStockRow[] = (res?.data ?? [])
            .map((r: any) => ({
              warehouseId: r.warehouseId?.toString(),
              warehouseCode: r.warehouseCode ?? '',
              warehouseName: r.warehouseName ?? '',
              availableStock: Number(r.availableStock ?? 0),
              unitOfMeasureId: r.unitOfMeasureId?.toString(),
            }))
            .filter((r: WarehouseStockRow) => allowed.includes(r.warehouseId));
          // la consulta fresca reemplaza el stock capturado con el artículo, para que
          // el cambio de unidad y la distribución muestren la misma disponibilidad
          const materialStock = rows.map((r) => ({
            warehouseId: r.warehouseId,
            unitOfMeasureId: r.unitOfMeasureId,
            availableStock: r.availableStock,
          }));
          this.siblingLines(item).forEach((it) => {
            it.warehouseStocks = rows;
            if (it.material) it.material.materialStock = materialStock;
          });
          return rows;
        }),
        catchError(() => of([]))
      );
  }

  // resuelve de qué almacenes sale la cantidad de la línea, según la configuración de la clase
  // reparte en secuencia las líneas recién agregadas y llama a done al terminar. No usa
  // el sujeto con debounce: ese colapsaba varias altas en una y repreciaba antes de repartir
  private distributeAdded(items: DocumentItem[], done: () => void): void {
    const targets = items.filter((item) => !item.isBonus && this.items.includes(item));
    if (!this.allowsMultipleWarehouses || !targets.length) {
      done();
      return;
    }
    let remaining = targets.length;
    const finish = () => {
      remaining -= 1;
      if (remaining <= 0) done();
    };
    for (const item of targets) this.resolveWarehouseDistribution(item, finish);
  }

  private resolveWarehouseDistribution(item: DocumentItem, done?: () => void): void {
    if (item.isBonus || !this.items.includes(item)) {
      done?.(); // la línea pudo eliminarse mientras se esperaba
      return;
    }
    if (!this.allowsMultipleWarehouses) {
      this.confirmPartialSale(item, this.availableForItem(item), undefined, done);
      return;
    }
    this.loadWarehouseStocks(item).subscribe({
      next: (stocks) => {
        if (!this.items.includes(item)) {
          done?.();
          return;
        }
        const requested = this.requestedQuantity(item);
        const factor = this.unitFactor(item) || 1;
        const totalAvailable = stocks.reduce((s, x) => s + (x.availableStock || 0), 0) / factor;

        // sin respaldo suficiente y sin venta sin inventario: se confirma vender lo disponible
        if (requested > totalAvailable && item.material?.canMakeAnOrderWithoutStock !== true) {
          this.confirmPartialSale(
            item,
            totalAvailable,
            () => this.distribute(item, Math.min(requested, totalAvailable), stocks),
            done
          );
          return;
        }
        this.distribute(item, requested, stocks);
        done?.();
      },
      error: () => done?.(),
    });
  }

  // cantidad total pedida del material en esta unidad, sumando las líneas ya repartidas
  private requestedQuantity(item: DocumentItem): number {
    return this.siblingLines(item).reduce((sum, it) => sum + (it.quantity || 0), 0);
  }

  private distribute(item: DocumentItem, quantity: number, stocks: WarehouseStockRow[]): void {
    if (quantity <= 0) return;
    // separando por almacén la línea sigue siendo una sola: el reparto se guarda en ella
    // y se expande recién al generar los documentos
    if (this.splitsByWarehouse) {
      this.setAllocations(item, this.planAllocations(item, quantity, stocks));
      this.afterDistribution(item);
      return;
    }
    // sin separación el reparto se materializa en una línea por almacén
    if (quantity <= this.availableForItem(item)) return; // cabe en el almacén actual
    // sin existencia en ningún almacén no hay reparto que decidir: la venta sale
    // completa del almacén principal, como cualquier otra línea
    const totalAvailable = stocks.reduce((sum, x) => sum + (x.availableStock || 0), 0);
    if (totalAvailable <= 0) return;
    this.openWarehouseDistribution(item, quantity, stocks);
  }

  // guarda el reparto dentro de la línea y refleja en ella el total y el almacén principal
  private setAllocations(item: DocumentItem, allocations: WarehouseAllocation[]): void {
    if (!allocations.length) return;
    item.allocations = allocations;
    const total = allocations.reduce((sum, a) => sum + (a.quantity || 0), 0);
    item.quantity = total;
    item.quantityText = this.quantityText(total);
    item.withoutStock = allocations.every((a) => a.withoutStock === true);
    const [first] = allocations;
    this.assignWarehouseToItem(item, {
      _id: first.warehouseId,
      name: first.warehouseName,
      code: first.warehouseCode,
    });
  }

  // reparto manual: el usuario decide cuánto sale de cada almacén
  private openWarehouseDistribution(
    item: DocumentItem,
    quantity: number,
    stocks: WarehouseStockRow[],
    // abierto a mano desde la línea: cerrar sin repartir no debe tocar la cantidad
    manual = false
  ): void {
    const factor = this.unitFactor(item) || 1;
    // el disponible va sin truncar, igual que en el modal de cambio de unidad,
    // para que el usuario vea la misma cifra en los dos lugares
    const warehouses = this.orderedWarehouses().map((w) => {
      const id = w._id?.toString();
      const base = stocks.find((s) => s.warehouseId === id)?.availableStock ?? 0;
      return {
        warehouseId: id,
        warehouseCode: w.externalKeys?.code ?? (w as any)?.code ?? '',
        warehouseName: w.name ?? '',
        available: factor > 0 ? base / factor : base,
      };
    });
    // el reparto vigente vive en la línea cuando se separa por almacén, y en las
    // líneas hermanas cuando no
    const existing: WarehouseAllocation[] = this.splitsByWarehouse
      ? item.allocations ?? []
      : this.siblingLines(item).map((it) => ({
          warehouseId: it.warehouseId ?? '',
          quantity: it.quantity || 0,
        }));
    // el modal abre con una propuesta ya repartida para que el usuario la revise
    // y ajuste, en vez de con toda la cantidad sobre el almacén principal
    const current =
      existing.length > 1 ? existing : this.planAllocations(item, quantity, stocks);

    const ref = this.dialog.open(WarehouseDistributionModalComponent, {
      data: {
        productLabel: this.articleLabel(item),
        unitLabel: item.unitLabel,
        requested: quantity,
        warehouses,
        allocations: current,
        // el material permite venderse por encima de su existencia: el faltante se
        // asigna al almacén principal y se marca como sin respaldo
        allowsWithoutStock: this.allowsSaleWithoutStock(item),
        mainWarehouseId: this.warehouseSelected?._id?.toString(),
        acceptsDecimals: this.distributionDecimals(item).accepts,
        decimalsAllowed: this.distributionDecimals(item).decimals,
      },
      ...this.dgConfig.dialogSize('md'),
      disableClose: true,
    });
    ref.afterClosed().subscribe((allocations: WarehouseAllocation[]) => {
      if (!allocations?.length) {
        // abierto a mano no se deshace nada: el usuario solo estaba consultando
        if (manual) return;
        // sin reparto la línea vuelve a la cantidad que tenía antes del cambio; sin registro previo se conserva
        const previous = this.lastValidQuantity.get(item.lineId);
        if (previous === undefined) return;
        item.quantity = previous;
        item.quantityText = String(previous);
        this.recomputeSummary();
        return;
      }
      if (this.splitsByWarehouse) {
        this.setAllocations(item, allocations);
      } else {
        this.applyAllocations(item, allocations);
      }
      this.afterDistribution(item);
    });
  }

  // acción por línea: reabre el reparto con las asignaciones actuales
  onOpenWarehouseDistribution(item: DocumentItem): void {
    if (!this.allowsMultipleWarehouses || item.isBonus) return;
    this.loadWarehouseStocks(item).subscribe((stocks) => {
      this.openWarehouseDistribution(item, this.requestedQuantity(item), stocks, true);
    });
  }

  private afterDistribution(item: DocumentItem): void {
    this.siblingLines(item).forEach((it) => this.lastValidQuantity.set(it.lineId, it.quantity || 0));
    this.recomputeSummary();
    this.priceSubject.next();
  }

  // se pide confirmación en lugar de recortar en silencio; al rechazar se vuelve a la cantidad anterior
  // onSettled corre por cualquier salida, también al cancelar: quien encadena el
  // reprecio detrás del reparto no puede quedarse esperando
  private confirmPartialSale(
    item: DocumentItem,
    available: number,
    onConfirm?: () => void,
    onSettled?: () => void
  ): void {
    // sin nada que confirmar el reparto debe seguir su curso: cortar aquí dejaba la
    // línea sin asignaciones y el documento salía entero sobre un solo almacén
    if (!(this.documentClassSelected as any)?.makesStockValidation) {
      onConfirm?.();
      onSettled?.();
      return;
    }
    if (item.material?.canMakeAnOrderWithoutStock === true) {
      onConfirm?.();
      onSettled?.();
      return;
    }
    const requested = item.quantity ?? 0;
    if (requested <= available) {
      this.lastValidQuantity.set(item.lineId, requested);
      onConfirm?.();
      onSettled?.();
      return;
    }
    const sellable = this.roundQuantity(item, Math.max(0, available));
    const unit = item.unitLabel ? ` ${item.unitLabel}` : '';
    this.confirmationService
      .confirm({
        title: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.INSUFFICIENT_STOCK_TITLE'),
        description: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.INSUFFICIENT_STOCK_DESCRIPTION', {
          requested: `${requested}${unit}`,
          item: this.articleLabel(item),
          available: `${sellable}${unit}`,
          sellable,
        }),
        type: ConfirmationModalType.WARNING,
        confirmButtonText: this.i18n.t('DOCUMENT_GENERATE.ACTIONS.SELL_QUANTITY', { quantity: sellable }),
        cancelButtonText: this.i18n.t('GENERAL.CANCEL'),
        descriptionIcon: 'inventory_2',
      })
      .subscribe((ok) => {
        if (!ok) {
          const previous = this.lastValidQuantity.get(item.lineId);
          if (previous !== undefined) {
            item.quantity = previous;
            item.quantityText = String(previous);
            this.recomputeSummary();
            this.priceSubject.next();
          }
          onSettled?.();
          return;
        }
        item.quantity = sellable;
        item.quantityText = String(sellable);
        this.lastValidQuantity.set(item.lineId, sellable);
        if (onConfirm) onConfirm();
        this.recomputeSummary();
        this.priceSubject.next();
        onSettled?.();
      });
  }

  // líneas del mismo material y unidad, que son las que se reparten entre almacenes
  private siblingLines(item: DocumentItem): DocumentItem[] {
    return this.items.filter(
      (it) => !it.isBonus && it._id === item._id && it.unitMeasureId === item.unitMeasureId
    );
  }

  // materializa el reparto: la primera asignación se queda en la línea de origen y el
  // resto se vuelca en líneas hermanas, que se crean, actualizan o descartan según el plan
  private applyAllocations(source: DocumentItem, allocations: WarehouseAllocation[]): void {
    if (!allocations.length) return;
    const siblings = this.siblingLines(source);
    const anchorIndex = this.items.indexOf(source);

    const [first, ...rest] = allocations;
    source.allocations = undefined; // en este modo el reparto vive en las líneas, no en una
    this.assignWarehouseToItem(source, {
      _id: first.warehouseId,
      name: first.warehouseName,
      code: first.warehouseCode,
    });
    source.quantity = first.quantity;
    source.quantityText = this.quantityText(first.quantity);
    source.withoutStock = first.withoutStock === true;
    this.refreshLineId(source);

    // las hermanas que ya no están en el plan se retiran junto con sus bonificaciones
    const keyOfAllocation = (warehouseId?: string, withoutStock?: boolean) =>
      `${warehouseId ?? ''}:${withoutStock === true}`;
    const planned = new Set(rest.map((a) => keyOfAllocation(a.warehouseId, a.withoutStock)));
    const obsolete = siblings.filter(
      (it) => it !== source && !planned.has(keyOfAllocation(it.warehouseId, it.withoutStock))
    );
    if (obsolete.length) {
      const obsoleteIds = new Set(obsolete.map((it) => it.lineId));
      this.items = this.items.filter(
        (it) => !obsoleteIds.has(it.lineId) && !(it.isBonus && obsoleteIds.has(it.bonusParentId ?? ''))
      );
      obsoleteIds.forEach((id) => this.forgetBonusWarnings(id));
    }

    let offset = 1;
    for (const allocation of rest) {
      const existing = this.siblingLines(source).find(
        (it) =>
          it !== source &&
          it.warehouseId === allocation.warehouseId &&
          (it.withoutStock === true) === (allocation.withoutStock === true)
      );
      if (existing) {
        existing.quantity = allocation.quantity;
        existing.quantityText = this.quantityText(allocation.quantity);
        existing.withoutStock = allocation.withoutStock === true;
        continue;
      }
      const clone: DocumentItem = {
        ...source,
        allocations: undefined,
        quantity: allocation.quantity,
        quantityText: this.quantityText(allocation.quantity),
        withoutStock: allocation.withoutStock === true,
        lineId: buildLineId(
          source._id,
          allocation.warehouseId,
          source.unitMeasureId,
          allocation.withoutStock === true
        ),
        // la bonificación se resuelve por línea: la hermana parte sin nada heredado
        bonusInfo: undefined,
        bonusIncludedQty: 0,
        bonusNotified: false,
        // las notas y evidencias pertenecen a la línea original
        note: undefined,
        evidences: [],
        newAttachments: [],
      };
      this.assignWarehouseToItem(clone, {
        _id: allocation.warehouseId,
        name: allocation.warehouseName,
        code: allocation.warehouseCode,
      });
      this.items.splice(anchorIndex + offset, 0, clone);
      offset++;
    }
  }

  // la clase permite abastecer una línea desde varios almacenes de la oficina
  private get allowsMultipleWarehouses(): boolean {
    return (this.documentClassSelected as any)?.allowMultipleWarehousesPerProduct === true;
  }

  get allowsMultipleWarehousesInView(): boolean {
    return this.allowsMultipleWarehouses;
  }

  get splitsByWarehouseInView(): boolean {
    return this.splitsByWarehouse;
  }

  private get splitsByWarehouse(): boolean {
    return this.allowsMultipleWarehouses &&
      (this.documentClassSelected as any)?.splitOrderByWarehouse === true;
  }

  // disponible del material en un almacén, en unidad base; se prefiere la disponibilidad
  // consultada por almacén y se cae al arreglo del material cuando aún no se consultó
  private baseStockInWarehouse(item: DocumentItem, warehouseId?: string): number {
    const id = warehouseId ?? item.warehouseId;
    const rows = item.warehouseStocks?.length
      ? item.warehouseStocks
      : (item.material?.materialStock ?? []).map((x: any) => ({
          warehouseId: x.warehouseId?.toString(),
          availableStock: x.availableStock || 0,
        }));
    return rows
      .filter((x: any) => !id || x.warehouseId === id)
      .reduce((sum: number, x: any) => sum + (x.availableStock || 0), 0);
  }

  // disponible sumando todos los almacenes considerados por la clase de documento
  private baseStockForItem(item: DocumentItem): number {
    if (!this.allowsMultipleWarehouses) return this.baseStockInWarehouse(item);
    const rows = item.warehouseStocks?.length
      ? item.warehouseStocks
      : (item.material?.materialStock ?? []);
    return rows.reduce((sum: number, x: any) => sum + (x.availableStock || 0), 0);
  }

  // lo que ya consumen las otras líneas del mismo material en ese almacén, en unidad base
  private baseConsumedByOtherLines(item: DocumentItem): number {
    return this.items
      .filter((it) => it !== item && it._id === item._id)
      .reduce((sum, it) => sum + this.baseQuantityInWarehouse(it, item.warehouseId), 0);
  }

  // cuánto toma una línea de un almacén concreto; con reparto guardado se mira su asignación
  private baseQuantityInWarehouse(it: DocumentItem, warehouseId?: string): number {
    const factor = this.unitFactor(it);
    if (it.allocations?.length) {
      const assigned = it.allocations
        .filter((a) => a.warehouseId === warehouseId && a.withoutStock !== true)
        .reduce((sum, a) => sum + (a.quantity || 0), 0);
      return assigned * factor;
    }
    // la parte vendida sin respaldo no consume existencia real
    if (it.withoutStock === true) return 0;
    return it.warehouseId === warehouseId ? (it.quantity || 0) * factor : 0;
  }

  // makesStockValidation: no permite exceder el stock disponible (en la unidad del ítem)
  // el consumo se mide agregado por material y almacén: dos líneas del mismo par compiten por el disponible
  private validateStock(item: DocumentItem): void {
    if (item.isBonus) return; // el producto gratis no valida stock
    if (!(this.documentClassSelected as any)?.makesStockValidation) return;
    if (!item.material) return; // sin datos de material (ej. copia de "usar como base") no se valida
    // el exceso lo resuelve la distribución o la venta parcial, que ya filtran por canMakeAnOrderWithoutStock
    if ((item.quantity ?? 0) > this.availableForItem(item)) {
      this.distributionSubject.next(item);
      return;
    }
    this.lastValidQuantity.set(item.lineId, item.quantity ?? 0);
  }

  // consulta la secuencia de precios y vuelca precio/ISV en cada artículo; done corre solo si el reprecio tuvo éxito
  // lineId ausente = consulta auxiliar de la UMB para derivar precios en modo 1
  private priceItems(done?: () => void): void {
    if (!this.items.length) return;
    const businessPartnerId = this.soldToSelected?.parentMongoId;
    const salesOfficeId = this.salesOfficeSelected?._id?.toString();
    const currencyId = this.selectedCurrency?._id?.toString();
    if (!businessPartnerId || !salesOfficeId || !currencyId) return;

    // la bonificación y las escalas se resuelven sobre la cantidad total del material,
    // no sobre la de cada línea: el reparto entre almacenes no debe cambiarlas
    const reqMap = new Map<string, { materialId: string; unitMeasureId: string; quantity: number }>();
    for (const it of this.items) {
      if (it.isBonus) continue;
      const key = `${it._id}|${it.unitMeasureId}`;
      const existing = reqMap.get(key);
      if (existing) {
        existing.quantity += it.quantity || 0;
      } else {
        reqMap.set(key, {
          materialId: it._id,
          unitMeasureId: it.unitMeasureId,
          quantity: it.quantity || 1,
        });
      }
      const baseId = it.material?.baseUnitOfMeasure?._id?.toString();
      if (baseId && baseId !== it.unitMeasureId && !reqMap.has(`${it._id}|${baseId}`)) {
        reqMap.set(`${it._id}|${baseId}`, { materialId: it._id, unitMeasureId: baseId, quantity: 1 });
      }
    }
    const payload = { businessPartnerId, salesOfficeId, currencyId, materials: [...reqMap.values()] };

    this.documentGenerateService.fetchPrice(payload).subscribe({
      next: (res) => {
        const priced: PricedMaterial[] = res?.materials ?? [];
        const priceOf = (matId: string, umId: string) =>
          priced.find(
            (m) => m.materialId?.toString() === matId && m.unitMeasureId?.toString() === umId
          );
        const droppedBonusIds: string[] = [];
        const offMainWarehouse: DocumentItem[] = [];
        const blockedByStock: DocumentItem[] = [];
        const withoutStockBonus: DocumentItem[] = [];
        for (const it of this.items) {
          if (it.isBonus) continue; // la línea de bono queda a precio 0
          const p = priceOf(it._id, it.unitMeasureId);
          // descuento total de la línea resuelto por el endpoint para la cantidad enviada
          let discountTotal = p ? Number(p.discountTotal) || 0 : 0;
          // precio manual (canUpdatePrices) o heredado de cotización: no se reprecia, solo se recalcula su ISV
          if (!it.manualPrice && !it.keepSourcePrice) {
            let price = p ? Number(p.price) || 0 : 0;
            it.priceConditionClasses = p?.conditionClasses ?? [];
            // descuento por unidad solo de la unidad vendida, en el mismo espacio que el precio
            const rawDiscount = price > 0 && p ? Number(p.discount) || 0 : 0;
            it.discountUnit = rawDiscount;
            it.discountType = rawDiscount > 0 ? p?.discountType : undefined;
            if (rawDiscount <= 0) discountTotal = 0;
            // modo 1: si la unidad de la línea no tiene precio, se deriva desde la UMB (precioUMB × factor)
            if (this.recalcMode === 1 && price <= 0) {
              const baseId = it.material?.baseUnitOfMeasure?._id?.toString();
              const basePriced = baseId ? priceOf(it._id, baseId) : null;
              const basePrice = basePriced ? Number(basePriced.price) || 0 : 0;
              if (basePrice > 0) price = basePrice * this.unitFactor(it);
              // no se deriva descuento desde la UMB
              it.discountUnit = 0;
              it.discountType = undefined;
              discountTotal = 0;
            }
            it.price = price;
          } else {
            // precio no repreciado: el descuento de la línea sigue el unitario ya resuelto
            discountTotal = (it.discountUnit || 0) * (it.quantity || 1);
          }
          // el bono sale del almacén principal: solo esa línea lo lleva.
          // Sin clase de línea GIFT no se materializa ninguno
          if (!this.classSupportsBonus) {
            if (this.isSupportedBonus(p?.bonus)) droppedBonusIds.push(it.lineId);
            it.bonusInfo = undefined;
            it.bonusIncludedQty = 0;
          } else if (!this.carriesBonus(it)) {
            // el regalo sale del almacén principal: esta línea no se abastece de ahí
            if (this.isSupportedBonus(p?.bonus)) offMainWarehouse.push(it);
            it.bonusInfo = undefined;
            it.bonusIncludedQty = 0;
          } else {
            // el almacén principal debe poder sostener la regalía, sea línea aparte
            // o incluida en la misma línea
            const check = this.isSupportedBonus(p?.bonus)
              ? this.checkBonusStock(it, p.bonus)
              : 'ok';
            if (check === 'bloqueada') {
              blockedByStock.push(it);
              it.bonusInfo = undefined;
              it.bonusIncludedQty = 0;
            } else {
              if (check === 'sin-respaldo') withoutStockBonus.push(it);
              else this.bonusOutOfStockWarned.delete(it.lineId);
              this.bonusNoStockWarned.delete(it.lineId);
              it.bonusInfo = p?.bonus ?? undefined;
              // INCLUDED_AS_DISCOUNT: las unidades gratis van en la misma línea (chip "+N"), no se cobran
              it.bonusIncludedQty =
                it.bonusInfo && it.bonusInfo.behaviour === BonusBehaviourEnum.INCLUDED_AS_DISCOUNT
                  ? Number(it.bonusInfo.quantity) || 0
                  : 0;
            }
          }
          const qty = it.quantity || 1;
          // el endpoint resolvió el descuento para la cantidad total del material:
          // cuando está repartido, cada línea lleva la parte que le toca
          it.discount =
            this.siblingLines(it).length > 1 ? (it.discountUnit || 0) * qty : discountTotal;
          it.isv = this.computeIsv(it.price, qty, it.taxPct || 0, it.discountUnit || 0);
        }
        // si ninguna línea del material se abastece del almacén principal, la regalía
        // que resolvió el endpoint no se puede entregar y hay que decirlo
        const unreachable = offMainWarehouse.filter((it) => {
          const group = this.siblingLines(it);
          return group[0] === it && !group.some((x) => this.carriesBonus(x));
        });
        if (unreachable.length) this.warnBonusUnavailable(unreachable, 'no-participa');
        if (blockedByStock.length) this.warnBonusUnavailable(blockedByStock, 'sin-existencia');
        if (withoutStockBonus.length) this.notifyBonusWithoutStock(withoutStockBonus);
        // materializa/actualiza las líneas de producto gratis según el bono
        this.reconcileBonusLines();
        this.notifyNewBonuses();
        this.recomputeSummary();
        done?.();
        // el bono puede aparecer al insertar o recién al subir la cantidad: se pregunta en ambos casos
        this.confirmDroppedBonuses(droppedBonusIds);
      },
      // un fallo parcial dejaría precios a medias: se descarta todo y quedan los anteriores
      error: () => {},
    });
  }

  // la bonificación sale del almacén principal: solo la línea que se abastece de ahí
  // la lleva. Si el principal no participa, no hay bonificación que entregar
  private carriesBonus(item: DocumentItem): boolean {
    const mainId = this.warehouseSelected?._id?.toString();
    if (!mainId) return false;
    if (item.allocations?.length) {
      return item.allocations.some((a) => a.warehouseId === mainId && a.withoutStock !== true);
    }
    return item.warehouseId === mainId;
  }

  // disponible del almacén principal para el material, ya descontado lo que las
  // líneas del documento consumen de ahí, en la unidad de la línea
  private mainWarehouseAvailable(item: DocumentItem): number {
    const mainId = this.warehouseSelected?._id?.toString();
    const stock = this.baseStockInWarehouse(item, mainId);
    const consumed = this.items
      .filter((it) => it._id === item._id && !it.isBonus)
      .reduce((sum, it) => sum + this.baseQuantityInWarehouse(it, mainId), 0);
    const factor = this.unitFactor(item) || 1;
    return Math.max(0, (stock - consumed) / factor);
  }

  // materializa las líneas de producto gratis (solo SEPARATE_FREE_LINE), enlazadas a su línea origen
  private reconcileBonusLines(): void {
    // clave estable por (línea comprada, material regalado, unidad)
    const keyOf = (parentId: string, matId: string, umId: string) => `bonus:${parentId}:${matId}:${umId}`;
    const desired = new Map<string, { parent: DocumentItem; bonus: any }>();
    for (const it of this.items) {
      if (it.isBonus) continue;
      const b = it.bonusInfo;
      if (b && b.behaviour === BonusBehaviourEnum.SEPARATE_FREE_LINE && (Number(b.quantity) || 0) > 0) {
        const key = keyOf(it.lineId, b.materialId, b.unitMeasureId);
        if (this.dismissedBonuses.has(key)) continue; // el usuario lo eliminó
        // la disponibilidad ya se resolvió al fijar bonusInfo: aquí solo se materializa
        desired.set(key, { parent: it, bonus: b });
      }
    }
    // quita las líneas de bono que ya no aplican
    this.items = this.items.filter((it) => !it.isBonus || desired.has(it.lineId));
    // agrega o actualiza las vigentes
    for (const [key, { parent, bonus }] of desired) {
      const existing = this.items.find((it) => it.isBonus && it.lineId === key);
      const qty = Number(bonus.quantity) || 0;
      if (existing) {
        existing.quantity = qty;
        existing.quantityText = String(qty);
        continue;
      }
      this.addBonusLine(key, parent, bonus);
    }
  }

  // el regalo sale del almacén principal. Si la compra ya agotó su existencia, decide
  // `activateBonusWithoutStock`: con la bandera activa se entrega igual y se avisa que
  // va sin respaldo; sin ella no se entrega
  private checkBonusStock(parent: DocumentItem, bonus: any): BonusStockCheck {
    const sameMaterial =
      bonus.materialId === parent._id || bonus.materialId === parent.material?._id?.toString();
    // de otro material no se conoce la disponibilidad: no se bloquea
    if (!sameMaterial) return 'ok';
    const needed = Number(bonus.quantity) || 0;
    if (this.mainWarehouseAvailable(parent) >= needed) return 'ok';
    return (this.documentClassSelected as any)?.activateBonusWithoutStock === true
      ? 'sin-respaldo'
      : 'bloqueada';
  }

  // la regalía se entrega pero sale por encima de la existencia del almacén principal
  private notifyBonusWithoutStock(lines: DocumentItem[]): void {
    const pending = lines.filter((it) => !this.bonusOutOfStockWarned.has(it.lineId));
    if (!pending.length) return;
    pending.forEach((it) => this.bonusOutOfStockWarned.add(it.lineId));
    const names = pending.map((it) => `<strong>${this.articleLabel(it)}</strong>`).join('<br>');
    const single = pending.length === 1;
    this.showItemWarnings([
      {
        title: this.i18n.t(
          single
            ? 'DOCUMENT_GENERATE.BONUS_OUT_OF_STOCK.TITLE_ONE'
            : 'DOCUMENT_GENERATE.BONUS_OUT_OF_STOCK.TITLE_MANY'
        ),
        message: this.i18n.t(
          single
            ? 'DOCUMENT_GENERATE.BONUS_OUT_OF_STOCK.MESSAGE_ONE'
            : 'DOCUMENT_GENERATE.BONUS_OUT_OF_STOCK.MESSAGE_MANY',
          { warehouse: this.optionLabel(this.warehouseSelected), items: names }
        ),
        icon: 'redeem',
      },
    ]);
  }

  // avisa una sola vez por línea que la regalía no se entrega porque el almacén
  // principal no puede sostenerla
  private warnBonusUnavailable(lines: DocumentItem[], reason: 'sin-existencia' | 'no-participa'): void {
    const pending = lines.filter((it) => !this.bonusNoStockWarned.has(it.lineId));
    if (!pending.length) return;
    pending.forEach((it) => this.bonusNoStockWarned.add(it.lineId));
    const names = pending.map((it) => `<strong>${this.articleLabel(it)}</strong>`).join('<br>');
    const warehouse = this.optionLabel(this.warehouseSelected);
    const docClass = this.optionLabel(this.documentClassSelected);
    const single = pending.length === 1;
    const cause =
      reason === 'sin-existencia'
        ? this.i18n.t('DOCUMENT_GENERATE.BONUS_NOT_APPLIED.CAUSE_NO_STOCK', {
            warehouse,
            documentClass: docClass,
          })
        : this.i18n.t(
            single
              ? 'DOCUMENT_GENERATE.BONUS_NOT_APPLIED.CAUSE_OTHER_WAREHOUSE_ONE'
              : 'DOCUMENT_GENERATE.BONUS_NOT_APPLIED.CAUSE_OTHER_WAREHOUSE_MANY',
            { warehouse }
          );
    this.showItemWarnings([
      {
        title: this.i18n.t(
          single
            ? 'DOCUMENT_GENERATE.BONUS_NOT_APPLIED.TITLE_ONE'
            : 'DOCUMENT_GENERATE.BONUS_NOT_APPLIED.TITLE_MANY'
        ),
        message: this.i18n.t(
          single
            ? 'DOCUMENT_GENERATE.BONUS_NOT_APPLIED.MESSAGE_ONE'
            : 'DOCUMENT_GENERATE.BONUS_NOT_APPLIED.MESSAGE_MANY',
          { cause, items: names }
        ),
        icon: 'redeem',
      },
    ]);
  }

  // crea la línea de producto gratis (precio 0), enlazada a su línea origen
  private addBonusLine(key: string, parent: DocumentItem, bonus: any): void {
    const qty = Number(bonus.quantity) || 0;
    // el regalo suele ser el mismo material comprado: se reutiliza su ficha
    const same =
      !!parent.material &&
      (bonus.materialId === parent._id || bonus.materialId === parent.material?._id?.toString());
    const material = same ? parent.material : null;
    const um = same
      ? (material?.alternalUnitOfMeasures ?? []).find(
          (a: any) => a?.unitOfMeasure?._id?.toString() === bonus.unitMeasureId
        )
      : null;
    const line: DocumentItem = {
      // la clave del bono identifica la línea; _id sigue siendo el material regalado
      lineId: key,
      _id: bonus.materialId,
      // el regalo sale del mismo almacén que su línea de origen
      warehouseId: parent.warehouseId,
      warehouseCode: parent.warehouseCode,
      warehouseName: parent.warehouseName,
      code: same ? this.applyRemoveLeftZeros(material?.code ?? '') : '',
      name: same
        ? material?.name ?? ''
        : this.i18n.t('DOCUMENT_GENERATE.BONUS_REVIEW.PRODUCT_NAME'),
      category: material?.materialGroups?.[0]?.name ?? '',
      unitLabel: um?.unitOfMeasure?.name ?? parent.unitLabel ?? '',
      unitMeasureId: bonus.unitMeasureId,
      taxPct: 0,
      quantity: qty,
      quantityText: String(qty),
      price: 0,
      discount: 0,
      isv: 0,
      material,
      isCommercialUom: true,
      isBonus: true,
      bonusParentId: parent.lineId,
    };
    // se inserta justo después de su línea origen
    const idx = this.items.findIndex((it) => it.lineId === parent.lineId);
    this.items.splice(idx >= 0 ? idx + 1 : this.items.length, 0, line);
  }

  // texto formal de la regla de regalía resuelta por el endpoint (para tooltip y modal)
  bonusRuleText(b: DocumentItemBonus | undefined): string {
    if (!b || !b.buyQuantity) return '';
    const parts = [
      this.i18n.t('DOCUMENT_GENERATE.BONUS_REVIEW.RULE', {
        buy: b.buyQuantity,
        bonus: b.bonusQuantity,
      }),
    ];
    if (b.minimumQuantity) {
      parts.push(
        this.i18n.t('DOCUMENT_GENERATE.BONUS_APPLIED.CELL_MINIMUM', { min: b.minimumQuantity })
      );
    }
    if (b.appliedScale) {
      parts.push(
        this.i18n.t('DOCUMENT_GENERATE.BONUS_APPLIED.CELL_SCALE', {
          from: b.appliedScale.quantityFrom,
          to: b.appliedScale.quantityTo,
        })
      );
    }
    return parts.join(' · ');
  }

  // avisa una vez por línea cuando aparece una regalía; la regalía ya quedó aplicada
  private notifyNewBonuses(): void {
    const nuevos: DocumentItem[] = [];
    for (const it of this.items) {
      if (it.isBonus) continue;
      const tiene = !!it.bonusInfo && (Number(it.bonusInfo.quantity) || 0) > 0;
      if (!tiene) {
        it.bonusNotified = false; // si desaparece, podrá volver a avisar
        continue;
      }
      if (!it.bonusNotified) {
        it.bonusNotified = true;
        nuevos.push(it);
      }
    }
    if (!nuevos.length) return;
    const lineas = nuevos
      .map((it) =>
        this.i18n.t('DOCUMENT_GENERATE.BONUS_REVIEW.LINE', {
          item: this.articleLabel(it),
          rule: this.bonusRuleText(it.bonusInfo),
          quantity: it.bonusInfo?.quantity,
        })
      )
      .join('<br>');
    this.confirmationService
      .confirm({
        title: this.i18n.t(
          nuevos.length > 1
            ? 'DOCUMENT_GENERATE.BONUS_APPLIED.TITLE_MANY'
            : 'DOCUMENT_GENERATE.BONUS_APPLIED.TITLE_ONE'
        ),
        description: this.i18n.t('DOCUMENT_GENERATE.BONUS_APPLIED.MESSAGE', { lines: lineas }),
        type: ConfirmationModalType.INFO,
        confirmButtonText: this.i18n.t('DOCUMENT_GENERATE.BONUS_APPLIED.UNDERSTOOD'),
        cancelButtonText: '',
        descriptionIcon: 'redeem',
      })
      .subscribe();
  }

  // factor de conversión de la unidad actual de la línea (unidades base por 1)
  private unitFactor(it: DocumentItem): number {
    const aums: any[] = it.material?.alternalUnitOfMeasures ?? [];
    const found = aums.find((a) => a?.unitOfMeasure?._id?.toString() === it.unitMeasureId);
    return Number(found?.conversionFactor ?? found?.equivalentQuantity ?? 1);
  }

  // stock disponible para la línea en su unidad (el stock se guarda en unidad base),
  // ya descontado lo que consumen las otras líneas del mismo material y almacén
  private availableForItem(it: DocumentItem): number {
    const baseAvailable = this.baseStockInWarehouse(it) - this.baseConsumedByOtherLines(it);
    const factor = this.unitFactor(it);
    const available = factor > 0 ? baseAvailable / factor : baseAvailable;
    return Math.max(0, available);
  }

  // unidades del material de la línea: base (isUMB), paralela (isUMP),
  // secundaria (isSecondaryAlternativeUom) y la de venta seleccionada
  private itemUnits(it: DocumentItem): { umb: any; ump: any; ums: any; sold: any } {
    const aums: any[] = it.material?.alternalUnitOfMeasures ?? [];
    const umb = aums.find((a) => a?.isUMB === true);
    const ump = aums.find((a) => a?.isUMP === true);
    const ums = aums.find((a) => a?.isSecondaryAlternativeUom === true);
    const sold = aums.find((a) => a?.unitOfMeasure?._id?.toString() === it.unitMeasureId);
    return { umb, ump, ums, sold };
  }

  // cantidades en UMB, UMP y secundaria derivadas de una cantidad en la unidad de venta
  // (la de la línea si no se indica otra, como la cantidad regalada de un bono)
  private itemQuantities(
    it: DocumentItem,
    quantity = it.quantity || 0
  ): { base: number; pararell: number; alternal: number } {
    const { umb, ump, ums } = this.itemUnits(it);
    const base = quantity * this.unitFactor(it);
    const umpFactor = Number(ump?.conversionFactor ?? 0);
    const umsFactor = Number(ums?.conversionFactor ?? 0);

    return {
      base: this.roundByUnit(base, umb),
      pararell: umpFactor > 0 ? this.roundByUnit(base / umpFactor, ump) : 0,
      alternal: umsFactor > 0 ? this.roundByUnit(base / umsFactor, ums) : 0,
    };
  }

  // cantidad entregada de la línea en unidad secundaria y paralela; sin redondear, es informativa
  private altUnitQuantities(it: DocumentItem): LineAltUnits {
    const delivered = (it.quantity || 0) + (it.bonusIncludedQty || 0);
    return altUnitsOf(it.material, it.unitMeasureId, delivered, (this.company as any)?.UMPActive !== false);
  }

  // sin decimales se redondea hacia arriba para no despachar de menos
  private roundByUnit(quantity: number, unit: any): number {
    if (!quantity) return 0;
    return unit?.unitOfMeasure?.hasacceptedDecimals
      ? parseFloat(quantity.toFixed(2))
      : Math.ceil(quantity);
  }

  // trunca la cantidad tecleada si la unidad de venta no acepta decimales
  // la cantidad se acota a los decimales que admite la unidad de la línea
  private applyQuantityDecimals(it: DocumentItem): void {
    const { accepts, decimals } = this.uomDecimals(it);
    const current = it.quantity || 0;
    const limited = this.truncateToUom(current, accepts, decimals);
    if (limited !== current) {
      it.quantity = limited;
      it.quantityText = this.quantityText(limited);
    }
  }

  private recomputeSummary(): void {
    this.summary = this.summaryOf(this.items);
  }

  // resumen de un conjunto de líneas; con partición cada documento tiene el suyo
  private summaryOf(lines: DocumentItem[]): DocumentSummary {
    // si el precio incluye impuesto, el subtotal es la base neta (precio − ISV)
    const subtotal = lines.reduce(
      (s, it) => s + it.quantity * it.price - (this.taxIncluded ? it.isv : 0),
      0
    );
    const discount = lines.reduce((s, it) => s + it.discount, 0);
    const taxes = lines.reduce((s, it) => s + it.isv, 0);
    return {
      itemsCount: lines.length,
      subtotal: this.dgConfig.roundCalc(subtotal),
      discount: this.dgConfig.roundCalc(discount),
      taxes: this.dgConfig.roundCalc(taxes),
      total: this.dgConfig.roundCalc(subtotal - discount + taxes),
    };
  }

  // Adjuntos: la lista se muestra fuera de la card
  get attachmentFiles(): File[] {
    return (this.attachmentsControl.value as File[]) ?? [];
  }

  removeAttachment(index: number): void {
    const files = [...this.attachmentFiles];
    files.splice(index, 1);
    this.attachmentsControl.setValue(files);
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  }

  private mapMaterialToItem(m: any): DocumentItem {
    const materialId = m._id?.toString();
    const unitMeasureId = m.baseUnitOfMeasure?._id?.toString() ?? '';
    const warehouse = this.warehouseSelected;
    return {
      lineId: buildLineId(materialId, warehouse?._id?.toString(), unitMeasureId),
      _id: materialId,
      warehouseId: warehouse?._id?.toString(),
      warehouseCode: warehouse?.externalKeys?.code ?? (warehouse as any)?.code ?? '',
      warehouseName: warehouse?.name ?? '',
      code: this.applyRemoveLeftZeros(m.code),
      name: m.name,
      category: m.materialGroups?.[0]?.name ?? '',
      unitLabel:
        m.baseUnitOfMeasure?.name ??
        m.baseUnitOfMeasure?.code ??
        this.i18n.t('DOCUMENT_GENERATE.COLUMNS.UNIT_FALLBACK'),
      unitMeasureId,
      taxPct: m.taxPercentageEffective ?? m.tax?.percentage ?? 0,
      taxName: m.tax?.name ?? '',
      quantity: 1,
      quantityText: '1',
      price: m.price ?? 0,
      discount: 0,
      isv: 0,
      material: this.scopeMaterialStock(m),
      isCommercialUom: true,
      // respaldo de los flags de la unidad base; la unidad de la línea manda si los trae
      hasAcceptedDecimals: m.baseUnitOfMeasure?.hasacceptedDecimals ?? false,
      allowDecimalDistribution:
        m.baseUnitOfMeasure?.allowDecimalDistributionInInventoryWizard ?? false,
      decimalsAllowed: Number(m.baseUnitOfMeasure?.decimalsAllowed ?? 0) || 0,
    };
  }

  // el lineId depende de la unidad y del almacén: se recalcula cuando alguno cambia
  private refreshLineId(item: DocumentItem): void {
    const previous = item.lineId;
    item.lineId = buildLineId(item._id, item.warehouseId, item.unitMeasureId, item.withoutStock);
    if (previous === item.lineId) return;
    // la última cantidad aceptada se guarda por lineId: sin moverla, rechazar una venta
    // parcial devolvería la línea a cero en vez de a la cantidad que ya tenía
    const lastQuantity = this.lastValidQuantity.get(previous);
    if (lastQuantity !== undefined) {
      this.lastValidQuantity.set(item.lineId, lastQuantity);
      this.lastValidQuantity.delete(previous);
    }
    // las líneas de regalo apuntan a su línea comprada por lineId
    this.items
      .filter((it) => it.isBonus && it.bonusParentId === previous)
      .forEach((it) => (it.bonusParentId = item.lineId));
  }

  private assignWarehouseToItem(item: DocumentItem, warehouse: any): void {
    item.warehouseId = warehouse?._id?.toString();
    item.warehouseCode = warehouse?.externalKeys?.code ?? warehouse?.code ?? '';
    item.warehouseName = warehouse?.name ?? '';
    this.refreshLineId(item);
  }

  // Sugerencias

  openMyDailyTransactions(): void {
    const userLoginId = this.currentUser?.id ?? (this.currentUser as any)?._id;
    if (!userLoginId) {
      this.handlerMessage.warning({ message: this.i18n.t('DOCUMENT_GENERATE.MESSAGES.USER_NOT_IDENTIFIED') });
      return;
    }
    const now = new Date();
    const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).toISOString();
    this.dialog.open(MyDailyTransactionsModalComponent, {
      data: {
        userLoginId,
        dayStart,
        salesOfficeId: this.salesOfficeSelected?._id?.toString(),
        showStock: (this.documentClassSelected as any)?.showCurrentStockForMaterials === true,
        umpActive: (this.company as any)?.UMPActive !== false,
      },
      ...this.dgConfig.dialogSize('lg'),
    });
  }

  openBusinessPartnerOpenItems(): void {
    if (!this.shipToSelected) return;
    this.dialog.open(OpenItemsModalComponent, {
      data: {
        businessPartnerMongoId: this.shipToSelected._id?.toString(),
        customerName: this.shipToSelected.name,
        customerCode: this.shipToSelected.externalKeys?.code ?? this.shipToSelected.parentExternalKeys?.code ?? '',
      },
      ...this.dgConfig.dialogSize('lg'),
    });
  }

  onViewOrderHistory(order: any): void {
    const ref = this.dialog.open(OrderDetailModalComponent, {
      data: {
        order,
        salesOfficeId: this.salesOfficeSelected?._id?.toString(),
        showStock: (this.documentClassSelected as any)?.showCurrentStockForMaterials === true,
        umpActive: (this.company as any)?.UMPActive !== false,
      },
      // lleva tabla de artículos: con el tier medio quedaba angosto en pantalla amplia
      ...this.dgConfig.dialogSize('lg'),
    });
    // "Usar como base": copia únicamente los productos seleccionados
    ref.afterClosed().subscribe((selectedProducts: any[]) => {
      if (!selectedProducts?.length) return;
      // los modales de precio y el encadenamiento aplican solo a cotizaciones (DT001);
      // un pedido usado como base mantiene el comportamiento anterior (copiar y repreciar)
      const isQuotation = order?.type?.code === 'DT001';
      const withBonus = this.productsWithBonus(selectedProducts);
      if (!isQuotation) {
        // sin diálogo de cotización, la advertencia de bonificación va sola
        if (!withBonus.length) {
          this.addSuggestedOrderProducts(order, selectedProducts, false, false);
          return;
        }
        this.confirmBonusRecalculation(withBonus).subscribe((ok) => {
          if (ok) this.addSuggestedOrderProducts(order, selectedProducts, false, false);
        });
        return;
      }
      // ya usada tiene prioridad sobre el flag: si la cotización tiene nextTransactions se recalcula
      const used = Array.isArray(order?.nextTransactions) && order.nextTransactions.length > 0;
      const keepPrice = this.keepsSourcePrices && !used;
      this.confirmUseAsBase(order, keepPrice, used, withBonus).subscribe((ok) => {
        if (ok) this.addSuggestedOrderProducts(order, selectedProducts, keepPrice, true);
      });
    });
  }

  // la cantidad vendida del origen incluye el regalo entregado en la misma línea: al copiar
  // se toma solo lo comprado, porque la bonificación se vuelve a resolver con las vigentes
  private purchasedQuantityOf(product: any): number {
    const sold = Number(product?.quantity) || 0;
    const bonus = Number(product?.bonusSoldQuantity) || 0;
    return Math.max(0, sold - bonus);
  }

  // líneas del origen que traían bonificación: regalo en la misma línea o líneas de regalo enlazadas
  private productsWithBonus(products: any[]): any[] {
    return (products ?? []).filter(
      (p) => (Number(p?.bonusSoldQuantity) || 0) > 0 || (p?.bonusChildPositions?.length ?? 0) > 0
    );
  }

  // la bonificación del origen no se copia: se vuelve a evaluar con las promociones vigentes
  private bonusRecalculationMessage(withBonus: any[]): string {
    const lineas = withBonus.map((p) => `<strong>${this.articleLabel(p)}</strong>`).join('<br>');
    const cierre = this.i18n.t(
      this.classSupportsBonus
        ? 'DOCUMENT_GENERATE.BONUS_REVIEW.RECALC_WITH_BONUS'
        : 'DOCUMENT_GENERATE.BONUS_REVIEW.RECALC_NO_BONUS'
    );
    return this.i18n.t('DOCUMENT_GENERATE.BONUS_REVIEW.RECALC_MESSAGE', {
      items: lineas,
      closing: cierre,
    });
  }

  // advertencia de bonificación cuando el origen no es una cotización (no hay diálogo de precios)
  private confirmBonusRecalculation(withBonus: any[]): Observable<boolean> {
    const ref = this.dialog.open(FormConfigChangeModalComponent, {
      data: {
        title: this.i18n.t('DOCUMENT_GENERATE.BONUS_REVIEW.TITLE'),
        message: this.bonusRecalculationMessage(withBonus),
        icon: 'redeem',
        confirmText: this.i18n.t('DOCUMENT_GENERATE.ACTIONS.CONTINUE'),
        cancelText: this.i18n.t('GENERAL.CANCEL'),
      },
      ...this.dgConfig.dialogSize('sm'),
    });
    return ref.afterClosed().pipe(map((ok) => !!ok));
  }

  // modal de advertencia según la matriz (respetar precios / recalcular / ya usada)
  private confirmUseAsBase(order: any, keepPrice: boolean, used: boolean, withBonus: any[] = []): Observable<boolean> {
    let message: string;
    let icon: string;
    if (used) {
      const usedSerial = order?.nextTransactions?.[0]?.serial;
      message = usedSerial
        ? this.i18n.t('DOCUMENT_GENERATE.QUOTATION_AS_BASE.USED_WITH_SERIAL', { serial: usedSerial })
        : this.i18n.t('DOCUMENT_GENERATE.QUOTATION_AS_BASE.USED_NO_SERIAL');
      icon = 'price_change';
    } else if (keepPrice) {
      message = this.i18n.t('DOCUMENT_GENERATE.QUOTATION_AS_BASE.KEEP_PRICES');
      icon = 'sell';
    } else {
      message = this.i18n.t('DOCUMENT_GENERATE.QUOTATION_AS_BASE.RECALC_PRICES');
      icon = 'price_change';
    }
    // el aviso de bonificación se integra aquí para no encadenar dos diálogos
    if (withBonus.length) message = `${message}<br><br>${this.bonusRecalculationMessage(withBonus)}`;
    const ref = this.dialog.open(FormConfigChangeModalComponent, {
      data: { title: this.i18n.t('DOCUMENT_GENERATE.QUOTATION_AS_BASE.TITLE'), message, icon, confirmText: this.i18n.t('DOCUMENT_GENERATE.ACTIONS.CONTINUE'), cancelText: this.i18n.t('GENERAL.CANCEL') },
      ...this.dgConfig.dialogSize('sm'),
    });
    return ref.afterClosed().pipe(map((ok) => !!ok));
  }

  // copia los productos del documento sugerido como artículos del formulario,
  // preservando el orden por posición; en cotizaciones enlaza el origen y/o respeta el precio.
  private addSuggestedOrderProducts(order: any, products: any[], keepPrice: boolean, chain: boolean): void {
    // no se copian las líneas de bonificación del origen (las que tienen línea padre): el reprecio las regenera
    const ordered = [...products]
      .filter((p) => p?.bonustParentPosition == null)
      .sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0));
    const addedIds: string[] = []; // recién agregados, para omitir tras el reprecio los que queden sin precio
    for (const p of ordered) {
      const id = p.materialId?.toString() ?? p.code;
      const unitId = p.unitMeasureId?.toString() ?? '';
      const lineId = buildLineId(id, this.warehouseSelected?._id?.toString(), unitId);
      const purchased = this.purchasedQuantityOf(p) || 1;
      const existing = this.items.find((it) => it.lineId === lineId);
      if (existing) {
        existing.quantity += purchased;
        existing.quantityText = String(existing.quantity);
        this.validateStock(existing);
        continue;
      }
      // el sugerido trae el material vigente, con el stock de todos los almacenes
      const material = p.material ? this.scopeMaterialStock(p.material) : null;
      const item: DocumentItem = {
        lineId,
        _id: id,
        warehouseId: this.warehouseSelected?._id?.toString(),
        warehouseCode: this.warehouseSelected?.externalKeys?.code ?? '',
        warehouseName: this.warehouseSelected?.name ?? '',
        code: this.applyRemoveLeftZeros(material?.code ?? p.code),
        name: p.name,
        category: material?.materialGroups?.[0]?.name ?? '',
        unitLabel: p.unitLabel,
        unitMeasureId: unitId,
        taxPct: material?.tax?.percentage ?? p.taxPct ?? 0,
        taxName: material?.tax?.name ?? p.taxName ?? '',
        quantity: purchased,
        quantityText: String(purchased),
        // precio/ISV se recalculan con la lista del cliente (salvo que se herede)
        price: 0,
        discount: 0,
        isv: 0,
        material,
        isCommercialUom: true,
      };
      // copia la nota del origen si la clase de documento lo permite
      if ((this.documentClassSelected as any)?.copyNotesToNextDocuments === true && p.note) {
        item.note = p.note;
      }
      // solo cotizaciones: referencia al origen (encadenamiento) y precio heredado
      if (chain) {
        item.sourceRef = {
          serial: order?.serial,
          type: order?.type,
          class: order?.class,
          position: p.position,
        };
        if (keepPrice) {
          item.price = p.price ?? 0;
          item.keepSourcePrice = true;
        }
      }
      this.items.push(item);
      this.validateStock(item);
      addedIds.push(item.lineId);
    }
    // las líneas bonificadas en el origen ya se advirtieron en el modal previo a copiar
    this.productsWithBonus(ordered).forEach((p) =>
      this.bonusWarnedIds.add(
        buildLineId(
          p.materialId?.toString() ?? p.code,
          this.warehouseSelected?._id?.toString(),
          p.unitMeasureId?.toString() ?? ''
        )
      )
    );
    this.recomputeSummary();
    // reprecia con la secuencia por cliente (las líneas con keepSourcePrice se saltan) y luego omite los sin precio
    this.priceItems(() => this.dropUnpricedItems(addedIds));
  }

  // quita los artículos recién agregados que quedaron sin precio y avisa cuáles;
  // las líneas con precio heredado o editado a mano no se reprecian, así que no se tocan
  private dropUnpricedItems(lineIds: string[]): void {
    const unpriced = this.items.filter(
      (it) => lineIds.includes(it.lineId) && !it.keepSourcePrice && !it.manualPrice && !((it.price ?? 0) > 0)
    );
    if (!unpriced.length) return;
    const names = unpriced.map((it) => this.articleLabel(it));
    this.items = this.items.filter((it) => !unpriced.includes(it));
    unpriced.forEach((it) => this.forgetBonusWarnings(it.lineId));
    this.recomputeSummary();
    this.warnUnpricedItems(names);
  }

  onAddSuggestedProduct(product: any): void {
    const material = product?.material;
    if (!material?._id) return;
    // la sugerencia no trae isCommercialUom en sus unidades: se busca el material completo para validar/agregar
    const body = this.buildMaterialsQueryBody(product.sku ?? product.code ?? material.code ?? '', 1, 50);
    if (!body) return;
    const targetId = material._id?.toString();
    // se precarga la unidad de venta más usada y su cantidad promedio
    const pref = { unitId: product.soldUnitId?.toString(), quantity: product.avgQty };
    this.documentGenerateService.findDocumentGeneratorMaterials(body).subscribe({
      next: (res) => {
        const list: any[] = res?.data?.data ?? [];
        const full = list.find((m) => m._id?.toString() === targetId);
        // sin material vigente con precio para el cliente: no se agrega y se avisa
        if (!full) {
          this.warnUnpricedItems([this.articleLabel(material ?? product)]);
          return;
        }
        this.addMaterials([full], pref);
      },
      error: () => this.addMaterials([material], pref),
    });
  }

  onSuggestionsTabChange(event: { index: number }): void {
    this.suggestedTab = event.index;
  }

  // reinicia paginación de ambas tabs y recarga (al elegir/cambiar solicitante)
  private reloadSuggested(): void {
    this.documentsPage = 1;
    this.productsPage = 1;
    this.loadSuggested();
  }

  // la columna lateral solo existe desde lg; por debajo se abre el mismo bloque como panel
  toggleSuggestions(): void {
    this.suggestionsExpanded = !this.suggestionsExpanded;
    if (this.suggestionsExpanded && this.shipToSelected) {
      this.reloadSuggested();
    }
  }

  openSuggestionsPanel(): void {
    if (this.suggestionsRef || !this.suggestionsDialogTpl) return;
    this.suggestionsRef = this.dialog.open(this.suggestionsDialogTpl, {
      ...this.dgConfig.dialogSize('lg'),
      panelClass: 'dg-suggestions-panel',
    });
    this.suggestionsRef.afterClosed().subscribe(() => (this.suggestionsRef = null));
  }

  closeSuggestionsPanel(): void {
    this.suggestionsRef?.close();
    this.suggestionsRef = null;
  }

  // filtros por tab: cada uno reinicia solo su paginación
  onDocumentsFilterChange(): void {
    this.documentsPage = 1;
    this.loadSuggested();
  }

  onProductsFilterChange(): void {
    this.productsPage = 1;
    this.loadSuggested();
  }

  // búsqueda por texto (se dispara con Enter o al limpiar)
  onDocSearch(): void {
    this.documentsPage = 1;
    this.loadSuggested();
  }

  onProdSearch(): void {
    this.productsPage = 1;
    this.loadSuggested();
  }

  // filtro de oficina (autocomplete) por tab
  filteredSuggestOffices(search: string): SalesOfficeAttributes[] {
    const term = (search || '').toLowerCase();
    return this.salesOfficesList.filter((o) => (o.name || '').toLowerCase().includes(term));
  }

  onDocOfficeInput(): void {
    if (!this.docOfficeSearch) {
      this.docOfficeId = '';
      this.onDocumentsFilterChange();
    }
  }

  onDocOfficeSelected(name: string): void {
    const office = this.salesOfficesList.find((o) => o.name === name);
    this.docOfficeId = office?._id?.toString() ?? '';
    this.docOfficeSearch = office?.name ?? '';
    this.onDocumentsFilterChange();
  }

  onProdOfficeInput(): void {
    if (!this.prodOfficeSearch) {
      this.prodOfficeId = '';
      this.onProductsFilterChange();
    }
  }

  onProdOfficeSelected(name: string): void {
    const office = this.salesOfficesList.find((o) => o.name === name);
    this.prodOfficeId = office?._id?.toString() ?? '';
    this.prodOfficeSearch = office?.name ?? '';
    this.onProductsFilterChange();
  }

  // pedidos y productos sugeridos del solicitante (un solo endpoint, filtros por sección)
  loadSuggested(): void {
    const businessPartnerId = this.shipToSelected?._id?.toString();
    if (!businessPartnerId) {
      this.orderHistoryList = [];
      this.suggestedProductsList = [];
      this.documentsCount = 0;
      this.productsCount = 0;
      return;
    }
    const body: any = {
      businessPartnerId,
      documentsDays: this.docDays,
      documentsPage: this.documentsPage,
      productsDays: this.prodDays,
      productsPage: this.productsPage,
    };
    if (this.docOfficeId) body.documentsSalesOfficeId = this.docOfficeId;
    if (this.prodOfficeId) body.productsSalesOfficeId = this.prodOfficeId;
    if (this.docSearch.trim()) body.documentsSearch = this.docSearch.trim();
    if (this.prodSearch.trim()) body.productsSearch = this.prodSearch.trim();

    this.suggestedLoading = true;
    this.documentGenerateService.findSuggested(body).subscribe({
      next: (res) => {
        const data = res?.data ?? {};
        // quita ceros a la izquierda en los códigos si la clase de documento lo pide (consistente con el resto del formulario)
        this.orderHistoryList = (data.documents?.data ?? []).map((doc: any) => ({
          ...doc,
          products: (doc.products ?? []).map((p: any) => ({ ...p, code: this.applyRemoveLeftZeros(p.code) })),
        }));
        this.documentsCount = data.documents?.pageInfo?.count ?? 0;
        this.suggestedProductsList = (data.products?.data ?? []).map((p: any) =>
          this.mapSuggestedProduct(p)
        );
        this.productsCount = data.products?.pageInfo?.count ?? 0;
        this.suggestedLoading = false;
        // los Productos Sugeridos muestran el precio de la lista actual, no el del último pedido
        this.repriceSuggestedProducts();
      },
      error: () => {
        this.orderHistoryList = [];
        this.suggestedProductsList = [];
        this.documentsCount = 0;
        this.productsCount = 0;
        this.suggestedLoading = false;
      },
    });
  }

  // prepara el producto sugerido: promedio de compra en la unidad de venta más usada
  private mapSuggestedProduct(p: any): any {
    const avg = Number(p.avgQuantity) || 0;
    const sold = (p.material?.alternalUnitOfMeasures ?? []).find(
      (a: any) => a?.unitOfMeasure?._id?.toString() === p.soldUnitId?.toString()
    );
    const acceptsDecimals =
      sold?.unitOfMeasure?.hasacceptedDecimals ?? p.material?.baseUnitOfMeasure?.hasacceptedDecimals;
    return {
      ...p,
      material: p.material ? this.scopeMaterialStock(p.material) : p.material,
      sku: this.applyRemoveLeftZeros(p.sku),
      // sin decimales se trunca; con decimales se deja el promedio real
      avgQty: acceptsDecimals ? Math.floor(avg * 100) / 100 : Math.floor(avg),
      avgUnitName: p.soldUnitName,
    };
  }

  // quita ceros a la izquierda del código si la clase de documento tiene removeLeftZerosOfMaterial
  private applyRemoveLeftZeros(code: string): string {
    if (!code) return code;
    return (this.documentClassSelected as any)?.removeLeftZerosOfMaterial ? code.replace(/^0+/, '') : code;
  }

  // reprecia los Productos Sugeridos con la lista de precios del cliente/oficina actual
  private repriceSuggestedProducts(): void {
    const businessPartnerId = this.soldToSelected?.parentMongoId;
    const salesOfficeId = this.salesOfficeSelected?._id?.toString();
    const currencyId = this.selectedCurrency?._id?.toString();
    // la moneda no participa del precio XP01; no se exige (puede llegar async después)
    if (!businessPartnerId || !salesOfficeId || !this.suggestedProductsList.length) return;

    // se cotiza la unidad de venta más usada (y su UMB para derivar en modo 1)
    const reqMap = new Map<string, { materialId: string; unitMeasureId: string; quantity: number }>();
    for (const p of this.suggestedProductsList) {
      const mid = p.material?._id?.toString();
      const umb = p.material?.baseUnitOfMeasure?._id?.toString();
      const sold = p.soldUnitId?.toString() ?? umb;
      if (!mid || !sold) continue;
      reqMap.set(`${mid}|${sold}`, { materialId: mid, unitMeasureId: sold, quantity: 1 });
      if (umb && umb !== sold) reqMap.set(`${mid}|${umb}`, { materialId: mid, unitMeasureId: umb, quantity: 1 });
    }
    if (!reqMap.size) return;

    this.documentGenerateService
      .fetchPrice({ businessPartnerId, salesOfficeId, currencyId, materials: [...reqMap.values()] }, true)
      .subscribe({
        next: (res) => {
          const priced: PricedMaterial[] = res?.materials ?? [];
          const priceOf = (mid: string, um: string) =>
            priced.find((x) => x.materialId?.toString() === mid && x.unitMeasureId?.toString() === um);
          for (const p of this.suggestedProductsList) {
            const mid = p.material?._id?.toString();
            const umb = p.material?.baseUnitOfMeasure?._id?.toString();
            const sold = p.soldUnitId?.toString() ?? umb;
            let price = Number(priceOf(mid, sold)?.price) || 0;
            // modo 1: si la unidad de venta no tiene precio propio, se deriva desde la UMB
            if (this.recalcMode === 1 && price <= 0 && umb && umb !== sold) {
              const basePrice = Number(priceOf(mid, umb)?.price) || 0;
              const factor = Number(
                (p.material?.alternalUnitOfMeasures ?? []).find(
                  (a: any) => a?.unitOfMeasure?._id?.toString() === sold
                )?.conversionFactor ?? 1
              );
              if (basePrice > 0) price = basePrice * factor;
            }
            p.price = price;
          }
        },
        error: () => {},
      });
  }

  get documentsTotalPages(): number {
    return Math.max(1, Math.ceil(this.documentsCount / this.SUGGESTED_PAGE_SIZE));
  }

  get productsTotalPages(): number {
    return Math.max(1, Math.ceil(this.productsCount / this.SUGGESTED_PAGE_SIZE));
  }

  changeDocumentsPage(delta: number): void {
    const next = this.documentsPage + delta;
    if (next < 1 || next > this.documentsTotalPages) return;
    this.documentsPage = next;
    this.loadSuggested();
  }

  changeProductsPage(delta: number): void {
    const next = this.productsPage + delta;
    if (next < 1 || next > this.productsTotalPages) return;
    this.productsPage = next;
    this.loadSuggested();
  }

}
